import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  LinearProgress,
  Select,
  Step,
  StepLabel,
  StepButton,
  Stepper,
  Tab,
  Tabs,
  Tooltip,
  TextField,
  Typography,
  Skeleton,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import useSocketIO from '../../hooks/useSocketIO';
import useHttp from '../../hooks/useHttp';
import { useAuth } from '../../context/authContext';
import useBranch from '../../hooks/useBranch';
import useEntertainment from '../../hooks/useEntertainment';
import useResource from '../../hooks/useResource';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { buildRolloutIndex } from '../../data/regiondoRolloutOrder';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const steps = [
  { key: 'compare', title: 'Vergleich & Ist-Stand' },
  { key: 'match', title: 'Matching (Produkte/Preisliste/Dauer)' },
  { key: 'checks', title: 'Konsistenzchecks (Dry-Run)' },
  { key: 'import', title: 'Import (Live)' },
];

function moneyEUR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function formatUpgradeLine(u, options = null) {
  if (!u) return '—';
  const opts = options && typeof options === 'object' ? options : null;
  const resolveUpgradeById =
    opts && typeof opts.resolveUpgradeById === 'function' ? opts.resolveUpgradeById : null;
  const parts = [];
  const name = String(u.name || '').trim();
  if (name) parts.push(name);

  const internal = String(u.internalDescription || '').trim();
  if (internal) parts.push(internal);

  const type = String(u.type || '').trim();
  if (type) parts.push(type);

  const basePriceRaw = Number(u.price);
  const basePrice = Number.isFinite(basePriceRaw) ? basePriceRaw : null;

  let priceLabel = basePrice != null ? moneyEUR(basePrice) : null;
  if (
    resolveUpgradeById &&
    basePrice != null &&
    Array.isArray(u.includedUpgrades) &&
    u.includedUpgrades.length > 0
  ) {
    const included = [];
    for (const idRaw of u.includedUpgrades) {
      const id = String(idRaw || '').trim();
      if (!id) continue;
      const up = resolveUpgradeById(id);
      if (!up) continue;
      const priceRaw = Number(up.price);
      included.push({
        name: String(up.name || '').trim(),
        price: Number.isFinite(priceRaw) ? priceRaw : 0,
      });
    }
    if (included.length > 0) {
      const includedTotal = included.reduce((s, it) => s + Number(it.price || 0), 0);
      const remaining = Math.max(0, basePrice - includedTotal);
      const incNames = included.map(it => it.name).filter(Boolean);
      const incLabel = incNames.length > 0 ? incNames.join(', ') : `${included.length} inkludiert`;
      const remainingLabel = moneyEUR(remaining);
      const includedLabel = moneyEUR(includedTotal);
      if (remainingLabel && includedLabel) {
        priceLabel = `${remainingLabel} + ${includedLabel} (inkl. ${incLabel})`;
      }
    }
  }

  if (priceLabel) parts.push(priceLabel);

  const id = String(u.upgradeId || u._id || '').trim();
  if (id) parts.push(`#${id.slice(-6)}`);

  return parts.join(' • ');
}

function deriveStepPhaseFromSession(session) {
  const status = String(session?.status || '').trim();
  const hasDryRun = Boolean(session?.resultData?.dryRun);
  const hasMatching = Boolean(session?.matching?.lockedAt);

  if (status === 'importing' || status === 'finished') return 'import';
  if (status === 'dryRunning') return 'checks';
  if (hasDryRun || status === 'readyForImport') return 'checks';
  if (hasMatching || status === 'readyForDryRun' || status === 'needsMatching' || status === 'compareFinished')
    return 'match';
  if (status === 'comparing' || status === 'created') return 'compare';
  return 'compare';
}

function isSessionWorking(session) {
  const status = String(session?.status || '').trim();
  return status === 'created' || status === 'comparing' || status === 'dryRunning' || status === 'importing';
}

function extractPreparedSuccessRows(session) {
  const prepared = session?.resultData?.preparedInternalBookings || null;
  const items = prepared && Array.isArray(prepared.items) ? prepared.items : [];
  return items
    .map(it => {
      const bk = String(it?.bookingKey || '').trim();
      if (!bk) return null;
      const on = it?.orderNumber != null ? String(it.orderNumber).trim() : '';
      return { bookingKey: bk, orderNumber: on || null };
    })
    .filter(Boolean);
}

function isPlainObject(x) {
  return Boolean(x) && typeof x === 'object' && !Array.isArray(x);
}

function deepMergePlainObjects(base, patch) {
  if (!isPlainObject(base)) return patch;
  if (!isPlainObject(patch)) return patch;
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (isPlainObject(v) && isPlainObject(base[k])) {
      out[k] = deepMergePlainObjects(base[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function StatCard({
  title,
  value,
  subtitle,
  color = 'default',
  isDone = false,
  action = null,
  showDoneIcon = false,
  donePulseKey = null,
  onClick = null,
}) {
  const theme = useTheme();
  const hoverOutline =
    theme.palette.mode === 'light' ? theme.palette.divider : alpha(theme.palette.common.white, 0.25);
  return (
    <Card
      variant='outlined'
      onClick={onClick || undefined}
      sx={{
        height: '100%',
        borderColor: isDone ? 'success.main' : 'divider',
        backgroundImage: isDone
          ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.22)}, ${alpha(
              theme.palette.success.main,
              0.05
            )})`
          : 'none',
        cursor: onClick ? 'pointer' : 'default',
        userSelect: onClick ? 'none' : 'auto',
        transition: 'border-color 120ms ease, transform 120ms ease',
        '&:hover': onClick
          ? {
              borderColor: hoverOutline,
              transform: 'translateY(-1px)',
            }
          : undefined,
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant='overline' color='text.secondary'>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant='h4' sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
              {value}
            </Typography>
            <Chip size='small' color={color} label={subtitle || ''} sx={{ opacity: subtitle ? 1 : 0 }} />
          </Box>
        </Box>
        {action ? (
          action
        ) : isDone && showDoneIcon ? (
          <CheckCircleIcon
            key={donePulseKey != null ? String(donePulseKey) : undefined}
            sx={{
              color: 'success.main',
              animation: 'fzCheckPop 520ms cubic-bezier(0.2, 0.8, 0.2, 1) both',
              '@keyframes fzCheckPop': {
                '0%': { transform: 'scale(0.2)', opacity: 0 },
                '55%': { transform: 'scale(1.12)', opacity: 1 },
                '100%': { transform: 'scale(1.0)', opacity: 1 },
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }}
          />
        ) : isDone ? (
          <CheckCircleIcon color='success' />
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function RegiondoMigration() {
  const theme = useTheme();
  const rmSurface = useMemo(() => {
    const isLight = theme.palette.mode === 'light';
    const d = theme.palette.divider;
    const w = theme.palette.common.white;
    return {
      b10: `1px solid ${isLight ? d : alpha(w, 0.1)}`,
      b08: `1px solid ${isLight ? d : alpha(w, 0.08)}`,
      b06: `1px solid ${isLight ? d : alpha(w, 0.06)}`,
      b22: `1px solid ${isLight ? d : alpha(w, 0.22)}`,
      neutral08: isLight ? d : alpha(w, 0.08),
      neutral06: isLight ? d : alpha(w, 0.06),
      s02: isLight ? theme.palette.grey[50] : alpha(w, 0.02),
      s03: isLight ? theme.palette.grey[100] : alpha(w, 0.03),
      s04: isLight ? theme.palette.grey[100] : alpha(w, 0.04),
      s08: isLight ? theme.palette.grey[200] : alpha(w, 0.08),
      selectBg: isLight ? theme.palette.grey[100] : alpha(w, 0.04),
      selectIcon: isLight ? theme.palette.action.active : alpha(w, 0.7),
      dragGrip: isLight ? theme.palette.grey[400] : alpha(w, 0.28),
      rowHover: isLight ? theme.palette.action.hover : alpha(w, 0.04),
      rowSelected: isLight ? alpha(theme.palette.primary.main, 0.15) : alpha(w, 0.28),
      borderTop10: `1px solid ${isLight ? d : alpha(w, 0.1)}`,
      borderBottom08: `1px solid ${isLight ? d : alpha(w, 0.08)}`,
      borderBottom10: `1px solid ${isLight ? d : alpha(w, 0.1)}`,
      borderRight10: `1px solid ${isLight ? d : alpha(w, 0.1)}`,
      finalizeHint: isLight ? theme.palette.grey[100] : alpha(w, 0.04),
      stickyStepperBg: isLight
        ? alpha(theme.palette.background.paper, 0.92)
        : alpha(theme.palette.common.black, 0.85),
    };
  }, [theme]);

  const selectMenuProps = useMemo(
    () => ({
      PaperProps: {
        sx: {
          bgcolor: 'background.paper',
          color: 'text.primary',
          border:
            theme.palette.mode === 'light'
              ? `1px solid ${theme.palette.divider}`
              : rmSurface.b10,
          backgroundImage: 'none',
        },
      },
    }),
    [theme, rmSurface]
  );

  const auth = useAuth();
  const { sendRequest } = useHttp();
  const { emit, on, off, isConnected } = useSocketIO();
  const { getBranches } = useBranch();
  const { getAllEntertainmentsByBranch } = useEntertainment();
  const { getResourcesByProduct } = useResource();

  const copyToClipboard = useCallback(async textRaw => {
    const text = String(textRaw || '');
    if (!text.trim()) {
      toast.info('Nichts zum Kopieren.');
      return false;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success('In die Zwischenablage kopiert.');
        return true;
      }
    } catch (_) {}
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', 'true');
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      el.style.top = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      if (ok) toast.success('In die Zwischenablage kopiert.');
      else toast.warning('Kopieren fehlgeschlagen.');
      return ok;
    } catch (_) {
      toast.warning('Kopieren fehlgeschlagen.');
      return false;
    }
  }, []);

  const readLS = useCallback((key, fallback = '') => {
    try {
      const v = window?.localStorage?.getItem?.(key);
      return v != null ? String(v) : fallback;
    } catch (_) {
      return fallback;
    }
  }, []);

  const writeLS = useCallback((key, value) => {
    try {
      if (value == null || String(value).trim() === '') {
        window?.localStorage?.removeItem?.(key);
      } else {
        window?.localStorage?.setItem?.(key, String(value));
      }
    } catch (_) {}
  }, []);

  const [activeStep, setActiveStep] = useState(0);
  const [branches, setBranches] = useState([]);
  const [entertainments, setEntertainments] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(() => readLS('regiondoMigration.branchId', ''));
  const [selectedEntertainmentId, setSelectedEntertainmentId] = useState(() =>
    readLS('regiondoMigration.entertainmentId', '')
  );

  const [dateFrom, setDateFrom] = useState(dayjs().format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(dayjs().add(4, 'year').format('YYYY-MM-DD'));
  const [includeCanceled, setIncludeCanceled] = useState(false);
  const [includeMissingInDb, setIncludeMissingInDb] = useState(true);
  const [convertFutureOnlinePaymentsToOffline, setConvertFutureOnlinePaymentsToOffline] = useState(
    () => readLS('regiondoMigration.convertFutureOnlinePaymentsToOffline', 'true') === 'true'
  );
  const [discountCodesApplyToUpgradesByDefault, setDiscountCodesApplyToUpgradesByDefault] = useState(
    () => readLS('regiondoMigration.discountCodesApplyToUpgradesByDefault', 'false') === 'true'
  );
  const [mergeFrankfurtGolfComboBookings, setMergeFrankfurtGolfComboBookings] = useState(
    () => readLS('regiondoMigration.mergeFrankfurtGolfComboBookings', 'false') === 'true'
  );
  const [sendBookingReviewMail, setSendBookingReviewMail] = useState(() => readLS('regiondoMigration.sendBookingReviewMail', 'false') === 'true');
  const [repairMode, setRepairMode] = useState(() => readLS('regiondoMigration.repairMode', 'false') === 'true');

  const [sessionId, setSessionId] = useState(null);
  const [matchingContext, setMatchingContext] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [matchingDraft, setMatchingDraft] = useState({
    productByRegiondoProductName: {},
    resourceByRegiondoProductName: {},
    packageUpgradeByRegiondoProductName: {},
    packageUpgradeNoneByRegiondoProductName: {},
    pricingByRegiondoKey: {},
    durationByRegiondoKey: {},
    categoryByRegiondoKey: {},
    upgradeByRegiondoSoldItemKey: {},
    ignoredRegiondoSoldItemKeys: {},
  });
  const [status, setStatus] = useState({
    phase: 'idle',
    progress: 0,
    title: 'Bereit',
    details: '',
  });
  const [progressInfo, setProgressInfo] = useState(null);
  const [stats, setStats] = useState({
    regiondoCount: 0,
    dbCount: 0,
    missingInDb: 0,
    extraInDb: 0,
    addressCoveragePct: 0,
  });
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isDryRunning, setIsDryRunning] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isAiSuggesting, setIsAiSuggesting] = useState(false);
  const [isLegacySyncing, setIsLegacySyncing] = useState(false);
  const legacySyncPollRef = useRef(null);
  const [dryRunReport, setDryRunReport] = useState(null);
  const [preparedSuccessRows, setPreparedSuccessRows] = useState([]);
  const [excludeBookingKeys, setExcludeBookingKeys] = useState([]);
  const [importReport, setImportReport] = useState(null);
  const [importDetailsOpen, setImportDetailsOpen] = useState(false);
  const [importDetailsTab, setImportDetailsTab] = useState(0);
  const [importDonePulse, setImportDonePulse] = useState(0);
  const [dryRunDetailsOpen, setDryRunDetailsOpen] = useState(false);
  const [dryRunDetailsKey, setDryRunDetailsKey] = useState('');
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTarget, setCompareTarget] = useState(null);
  const [compareBookingId, setCompareBookingId] = useState('');
  const [comparePreview, setComparePreview] = useState({ loading: false, error: null, data: null });
  const [compareContext, setCompareContext] = useState(null);
  const [compareQueueItems, setCompareQueueItems] = useState([]);
  const [compareQueueIndex, setCompareQueueIndex] = useState(-1);
  const compareQueueRef = useRef([]);
  const compareQueueIndexRef = useRef(-1);
  const [compareJsonEditOpen, setCompareJsonEditOpen] = useState(false);
  const [compareJsonText, setCompareJsonText] = useState('');
  const [compareJsonApplied, setCompareJsonApplied] = useState(null);
  const [isSavingCompareJson, setIsSavingCompareJson] = useState(false);
  const compareJsonOwnerBookingKeyRef = useRef('');
  const [compareJsonPaneHeight, setCompareJsonPaneHeight] = useState(320); // split: bottom pane height (px)
  const compareSplitContainerRef = useRef(null);
  const compareSplitDragRef = useRef({ active: false });
  const [isSavingManualOk, setIsSavingManualOk] = useState(false);
  const [forceImportDialogOpen, setForceImportDialogOpen] = useState(false);
  const [regiondoEmbedFrames, setRegiondoEmbedFrames] = useState([]); // [{ bookingKey, url }]
  const regiondoEmbedFramesRef = useRef([]);
  const [regiondoUsername, setRegiondoUsername] = useState('');
  const [regiondoPassword, setRegiondoPassword] = useState('');
  const [regiondoAuthInfo, setRegiondoAuthInfo] = useState({
    hasCookie: false,
    isValid: false,
    checkedAt: null,
    reason: '',
    usernameHint: '',
  });
  const [isRegiondoLoggingIn, setIsRegiondoLoggingIn] = useState(false);
  const lastJoinedSessionIdRef = useRef(null);
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);
  const [unmatchedTab, setUnmatchedTab] = useState(0);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [unmatchedData, setUnmatchedData] = useState(null);
  const lastAutoContextLoadRef = useRef({ sessionId: null });
  const lastLoadedMatchingDraftSessionIdRef = useRef(null);
  const matchingDraftDirtyRef = useRef(false);
  const startCompareTimeoutRef = useRef(null);
  const sessionPollRef = useRef(null);
  const productResourcesCacheRef = useRef(new Map());
  const [productResourcesCacheVersion, setProductResourcesCacheVersion] = useState(0);
  const [savedSessions, setSavedSessions] = useState([]);
  const [savedSessionId, setSavedSessionId] = useState('');
  const [isLoadingSavedSessions, setIsLoadingSavedSessions] = useState(false);
  const [isApplyingSavedMatching, setIsApplyingSavedMatching] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [isFinalizingEntertainment, setIsFinalizingEntertainment] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [finalizeDialogMode, setFinalizeDialogMode] = useState('complete');
  const savedSessionIdRef = useRef('');
  const authHeadersRef = useRef(null);
  const sendRequestRef = useRef(null);
  const checksPopStateRef = useRef(new Map());
  const comparePreviewCacheRef = useRef(new Map());
  const comparePreviewPrefetchQueueRef = useRef([]);
  const comparePreviewPrefetchQueuedRef = useRef(new Set()); // cacheKey
  const comparePreviewPrefetchInFlightRef = useRef(new Set()); // cacheKey
  const regiondoEmbedPrefetchQueueRef = useRef([]); // bookingKey
  const regiondoEmbedPrefetchQueuedRef = useRef(new Set()); // bookingKey
  const regiondoEmbedPrefetchInFlightRef = useRef(new Set()); // bookingKey
  const regiondoEmbedLoadedRef = useRef(new Set()); // bookingKey
  const lastRegiondoNeedsAuthSigRef = useRef('');
  const lastLatestRunKeyRef = useRef('');
  const lastImportPulseSigRef = useRef('');
  const lastRallyeAutoMappingSigRef = useRef('');

  const stepKey = steps[activeStep]?.key || 'compare';

  const excludeBookingKeysSet = useMemo(() => {
    const set = new Set();
    if (!Array.isArray(excludeBookingKeys)) return set;
    for (const k of excludeBookingKeys) {
      const key = String(k || '').trim();
      if (key) set.add(key);
    }
    return set;
  }, [excludeBookingKeys]);

  const setBookingExcluded = useCallback((bookingKey, excluded) => {
    const bk = String(bookingKey || '').trim();
    if (!bk) return;
    const shouldExclude = Boolean(excluded);
    setExcludeBookingKeys(prev => {
      const cur = Array.isArray(prev) ? prev : [];
      const set = new Set(cur.map(x => String(x || '').trim()).filter(Boolean));
      if (shouldExclude) set.add(bk);
      else set.delete(bk);
      return Array.from(set).sort();
    });
  }, []);

  useEffect(() => {
    const sid = String(sessionId || '').trim();
    if (!sid) {
      setExcludeBookingKeys([]);
      return;
    }
    const lsKey = `regiondoMigration.excludeBookingKeys.${sid}`;
    try {
      const raw = readLS(lsKey, '[]');
      const parsed = raw ? JSON.parse(raw) : [];
      const cleaned = Array.isArray(parsed)
        ? parsed
            .map(x => String(x || '').trim())
            .filter(Boolean)
        : [];
      setExcludeBookingKeys(Array.from(new Set(cleaned)).sort());
    } catch (_) {
      setExcludeBookingKeys([]);
    }
  }, [sessionId, readLS]);

  useEffect(() => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    const lsKey = `regiondoMigration.excludeBookingKeys.${sid}`;
    const cleaned = Array.from(excludeBookingKeysSet);
    writeLS(lsKey, cleaned.length > 0 ? JSON.stringify(cleaned) : '');
  }, [excludeBookingKeysSet, sessionId, writeLS]);

  const closeCompare = useCallback(() => {
    setCompareOpen(false);
    setCompareTarget(null);
    setCompareBookingId('');
    setComparePreview({ loading: false, error: null, data: null });
    setCompareContext(null);
    setCompareQueueItems([]);
    setCompareQueueIndex(-1);
    compareQueueRef.current = [];
    compareQueueIndexRef.current = -1;
    setCompareJsonEditOpen(false);
    setCompareJsonText('');
    setCompareJsonApplied(null);
    setIsSavingCompareJson(false);
    setRegiondoEmbedFrames([]);
    regiondoEmbedFramesRef.current = [];
    comparePreviewPrefetchQueueRef.current = [];
    comparePreviewPrefetchQueuedRef.current.clear();
    comparePreviewPrefetchInFlightRef.current.clear();
    regiondoEmbedPrefetchQueueRef.current = [];
    regiondoEmbedPrefetchQueuedRef.current.clear();
    regiondoEmbedPrefetchInFlightRef.current.clear();
    regiondoEmbedLoadedRef.current.clear();
  }, []);

  useEffect(() => {
    if (!compareOpen) return undefined;
    const sid = String(sessionId || '').trim();
    const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
    if (!sid || !bk) {
      setComparePreview({ loading: false, error: 'Session/bookingKey fehlt.', data: null });
      return undefined;
    }

    let canceled = false;
    const run = async () => {
      const cacheKey = `v2:${sid}:${bk}`;
      const cached = comparePreviewCacheRef.current.get(cacheKey) || null;
      if (cached) {
        setComparePreview({ loading: false, error: null, data: cached });
        return;
      }

      setComparePreview({ loading: true, error: null, data: null });
      try {
        const headers = authHeadersRef.current || (auth?.getAuthHeaders?.() || null);
        const req = sendRequestRef.current || sendRequest;
        const res = await req({
          url: `/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(
            sid
          )}/regiondo/booking/${encodeURIComponent(bk)}/preview`,
          method: 'GET',
          headers,
        });
        if (canceled) return;
        const next = res?.preview || null;
        if (next) comparePreviewCacheRef.current.set(cacheKey, next);
        setComparePreview({ loading: false, error: null, data: next });
      } catch (e) {
        if (canceled) return;
        setComparePreview({ loading: false, error: 'Konnte Preview nicht laden.', data: null });
      }
    };
    run();

    return () => {
      canceled = true;
    };
  }, [compareOpen, compareBookingId, sessionId, compareTarget?.bookingKey, compareTarget?.booking_key]);

  useEffect(() => {
    if (!compareOpen) return;
    const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
    const ownerBk = String(compareJsonOwnerBookingKeyRef.current || '').trim();

    // When switching to another booking in the queue, the JSON editor state must
    // follow the new booking (otherwise the left preview could show the previous booking's override).
    if (bk && ownerBk && bk !== ownerBk) {
      setCompareJsonApplied(null);
      if (compareJsonEditOpen) setCompareJsonText('');
      compareJsonOwnerBookingKeyRef.current = bk;
      return;
    }

    // If editor is closed, keep state clean.
    if (!compareJsonEditOpen) {
      setCompareJsonText('');
      setCompareJsonApplied(null);
      compareJsonOwnerBookingKeyRef.current = bk;
    }
  }, [compareOpen, compareTarget?.bookingKey, compareTarget?.booking_key, compareJsonEditOpen]);

  useEffect(() => {
    if (!compareOpen) return;
    if (!compareJsonEditOpen) return;
    if (isSavingCompareJson) return;
    const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
    if (!bk) return;
    const ownerBk = String(compareJsonOwnerBookingKeyRef.current || '').trim();
    if (ownerBk && ownerBk !== bk) return;

    const previewBk = String(comparePreview?.data?.bookingKey || '').trim();
    if (previewBk && previewBk !== bk) return;
    const internal = comparePreview?.data?.internalBooking && typeof comparePreview.data.internalBooking === 'object'
      ? comparePreview.data.internalBooking
      : null;
    if (!internal) return;

    // If the editor was opened before the new preview arrived, initialize the textarea now.
    if (!compareJsonText) {
      try {
        setCompareJsonText(JSON.stringify(internal, null, 2));
      } catch (_) {
        setCompareJsonText(String(internal || ''));
      }
    }
  }, [compareOpen, compareJsonEditOpen, isSavingCompareJson, compareTarget?.bookingKey, compareTarget?.booking_key, comparePreview?.data, compareJsonText]);

  const moveCompareSplitToClientY = useCallback(clientY => {
    const container = compareSplitContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect ? container.getBoundingClientRect() : null;
    if (!rect) return;
    const totalH = Number(rect.height);
    if (!Number.isFinite(totalH) || totalH <= 0) return;

    const minTop = 220;
    const minBottom = 180;
    const y = Number(clientY) - Number(rect.top);
    if (!Number.isFinite(y)) return;

    const topH = Math.max(minTop, Math.min(totalH - minBottom, y));
    const nextBottom = Math.max(minBottom, Math.min(totalH - minTop, Math.round(totalH - topH)));
    setCompareJsonPaneHeight(prev => (prev === nextBottom ? prev : nextBottom));
  }, []);

  const onCompareSplitPointerDown = useCallback(
    e => {
      if (!compareOpen) return;
      if (!e) return;
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();

      compareSplitDragRef.current.active = true;

      const handle = e.currentTarget;
      const pid = e.pointerId;
      if (handle && pid != null && typeof handle.setPointerCapture === 'function') {
        try {
          handle.setPointerCapture(pid);
        } catch (_) {}
      }

      moveCompareSplitToClientY(e.clientY);

      const prevCursor = document?.body?.style?.cursor || '';
      const prevUserSelect = document?.body?.style?.userSelect || '';
      try {
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';
      } catch (_) {}

      const onMove = ev => {
        if (!compareSplitDragRef.current.active) return;
        if (typeof ev.preventDefault === 'function') ev.preventDefault();
        moveCompareSplitToClientY(ev.clientY);
      };

      const onUp = ev => {
        compareSplitDragRef.current.active = false;
        try {
          document.body.style.cursor = prevCursor;
          document.body.style.userSelect = prevUserSelect;
        } catch (_) {}
        window.removeEventListener('pointermove', onMove, true);
        window.removeEventListener('pointerup', onUp, true);
        window.removeEventListener('pointercancel', onUp, true);

        const h = e.currentTarget;
        const upPid = ev?.pointerId ?? pid;
        if (h && upPid != null && typeof h.releasePointerCapture === 'function') {
          try {
            h.releasePointerCapture(upPid);
          } catch (_) {}
        }
      };

      window.addEventListener('pointermove', onMove, true);
      window.addEventListener('pointerup', onUp, true);
      window.addEventListener('pointercancel', onUp, true);
    },
    [compareOpen, moveCompareSplitToClientY]
  );

  useEffect(() => {
    savedSessionIdRef.current = savedSessionId;
  }, [savedSessionId]);

  useEffect(() => {
    try {
      authHeadersRef.current = auth?.getAuthHeaders?.() || null;
    } catch (_) {
      authHeadersRef.current = null;
    }
  }, [auth]);

  useEffect(() => {
    sendRequestRef.current = sendRequest;
  }, [sendRequest]);

  const compareRef = useRef(null);
  const matchRef = useRef(null);
  const checksRef = useRef(null);

  const canStart = useMemo(() => {
    return Boolean(selectedBranchId && selectedEntertainmentId && dateFrom && dateTo);
  }, [selectedBranchId, selectedEntertainmentId, dateFrom, dateTo]);

  const appendLog = useCallback(msg => {
    const text = String(msg || '').trim();
    if (!text) return;
    setLogs(prev => {
      const next = [...prev, { at: new Date().toISOString(), text }];
      return next.length > 200 ? next.slice(next.length - 200) : next;
    });
  }, []);

  const resetSessionUi = useCallback(() => {
    setSessionId(null);
    setMatchingContext(null);
    setAiSuggestions(null);
    setMatchingDraft({
      productByRegiondoProductName: {},
      resourceByRegiondoProductName: {},
      packageUpgradeByRegiondoProductName: {},
      packageUpgradeNoneByRegiondoProductName: {},
      pricingByRegiondoKey: {},
      durationByRegiondoKey: {},
      categoryByRegiondoKey: {},
      upgradeByRegiondoSoldItemKey: {},
      ignoredRegiondoSoldItemKeys: {},
    });
    lastLoadedMatchingDraftSessionIdRef.current = null;
    matchingDraftDirtyRef.current = false;
    setStatus({ phase: 'idle', progress: 0, title: 'Bereit', details: '' });
    setProgressInfo(null);
    setStats({ regiondoCount: 0, dbCount: 0, missingInDb: 0, extraInDb: 0, addressCoveragePct: 0 });
    setDryRunReport(null);
    setPreparedSuccessRows([]);
    setImportReport(null);
    setLogs([]);
    setActiveStep(0);
    userStepOverrideRef.current = false;
    lastAutoContextLoadRef.current = { sessionId: null };
    lastJoinedSessionIdRef.current = null;
  }, []);

  useEffect(() => {
    writeLS('regiondoMigration.branchId', selectedBranchId);
  }, [selectedBranchId, writeLS]);

  useEffect(() => {
    writeLS('regiondoMigration.entertainmentId', selectedEntertainmentId);
  }, [selectedEntertainmentId, writeLS]);

  useEffect(() => {
    writeLS('regiondoMigration.sendBookingReviewMail', sendBookingReviewMail ? 'true' : 'false');
  }, [sendBookingReviewMail, writeLS]);

  useEffect(() => {
    writeLS('regiondoMigration.repairMode', repairMode ? 'true' : 'false');
  }, [repairMode, writeLS]);

  useEffect(() => {
    writeLS('regiondoMigration.convertFutureOnlinePaymentsToOffline', convertFutureOnlinePaymentsToOffline ? 'true' : 'false');
  }, [convertFutureOnlinePaymentsToOffline, writeLS]);

  useEffect(() => {
    writeLS(
      'regiondoMigration.discountCodesApplyToUpgradesByDefault',
      discountCodesApplyToUpgradesByDefault ? 'true' : 'false'
    );
  }, [discountCodesApplyToUpgradesByDefault, writeLS]);

  useEffect(() => {
    writeLS(
      'regiondoMigration.mergeFrankfurtGolfComboBookings',
      mergeFrankfurtGolfComboBookings ? 'true' : 'false'
    );
  }, [mergeFrankfurtGolfComboBookings, writeLS]);

  const stopStartingSpinner = useCallback(() => {
    if (startCompareTimeoutRef.current) {
      clearTimeout(startCompareTimeoutRef.current);
      startCompareTimeoutRef.current = null;
    }
    setIsStarting(false);
  }, []);

  const userStepOverrideRef = useRef(false);
  useEffect(() => {
    userStepOverrideRef.current = false;
  }, [sessionId]);

  const scrollToStep = useCallback(idx => {
    const ref = idx === 0 ? compareRef : idx === 1 ? matchRef : checksRef;
    const el = ref?.current;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    userStepOverrideRef.current = true;
    setActiveStep(idx);
  }, []);

  useEffect(() => {
    const phase = String(status?.phase || '').trim();
    const map = { compare: 0, match: 1, checks: 2, import: 3 };
    if (userStepOverrideRef.current) return;
    if (map[phase] !== undefined) {
      setActiveStep(map[phase]);
    }
  }, [status?.phase]);

  const goPrev = useCallback(() => {
    userStepOverrideRef.current = true;
    setActiveStep(s => Math.max(0, s - 1));
  }, []);

  const canEnterStep = useCallback(
    idx => {
      const i = Number(idx);
      if (!Number.isFinite(i)) return false;
      if (i <= 0) return true;
      if (!sessionId) return false;
      if (i >= 2 && !matchingContext?.matching?.lockedAt) return false;
      if (i >= 3 && !dryRunReport) return false;
      return true;
    },
    [dryRunReport, matchingContext?.matching?.lockedAt, sessionId]
  );

  const canGoNext = useMemo(() => canEnterStep(activeStep + 1), [activeStep, canEnterStep]);

  const goNext = useCallback(() => {
    const next = Math.min(steps.length - 1, Math.max(0, Number(activeStep || 0) + 1));
    if (!canEnterStep(next)) {
      const msg =
        next === 1
          ? 'Bitte zuerst eine Session starten (Step 1: Vergleich).'
          : next === 2
            ? 'Bitte zuerst Matching speichern.'
            : next === 3
              ? 'Bitte zuerst den Dry-Run laufen lassen.'
              : 'Kann nicht zum nächsten Schritt wechseln.';
      toast.info(msg);
      return;
    }
    userStepOverrideRef.current = true;
    setActiveStep(next);
  }, [activeStep, canEnterStep]);

  const fetchLegacySyncStatus = useCallback(
    async sid => {
      if (!sid) return null;
      try {
        const res = await sendRequest({
          url: `/api/v1/internalBookingMigration/regiondo/session/${sid}/legacy/sync/status`,
          method: 'GET',
          headers: auth.getAuthHeaders(),
        });
        return res || null;
      } catch (_) {
        return null;
      }
    },
    [sendRequest, auth]
  );

  const startLegacySyncPolling = useCallback(
    sid => {
      if (!sid) return;
      if (legacySyncPollRef.current) {
        clearInterval(legacySyncPollRef.current);
        legacySyncPollRef.current = null;
      }
      setIsLegacySyncing(true);
      legacySyncPollRef.current = setInterval(async () => {
        const st = await fetchLegacySyncStatus(sid);
        const jobStatus = String(st?.legacySync?.status || st?.job?.status || '')
          .trim()
          .toLowerCase();
        const running = jobStatus === 'queued' || jobStatus === 'running' || jobStatus === 'created';
        if (running) {
          setIsLegacySyncing(true);
          return;
        }
        if (legacySyncPollRef.current) {
          clearInterval(legacySyncPollRef.current);
          legacySyncPollRef.current = null;
        }
        setIsLegacySyncing(false);
        appendLog(`Legacy Sync: status=${jobStatus || 'unknown'}. Compare sollte gleich neu laufen.`);
      }, 2000);
    },
    [fetchLegacySyncStatus, appendLog]
  );

  useEffect(() => {
    return () => {
      if (legacySyncPollRef.current) {
        clearInterval(legacySyncPollRef.current);
        legacySyncPollRef.current = null;
      }
    };
  }, []);

  const loadUnmatched = useCallback(
    async sid => {
      if (!sid) return;
      setUnmatchedLoading(true);
      try {
        const res = await sendRequest({
          url: `/api/v1/internalBookingMigration/regiondo/session/${sid}/compare/unmatched?limit=200`,
          method: 'GET',
          headers: auth.getAuthHeaders(),
        });
        setUnmatchedData(res || null);
      } catch (e) {
        toast.error('Ungematchte Einträge konnten nicht geladen werden');
      } finally {
        setUnmatchedLoading(false);
      }
    },
    [sendRequest, auth]
  );

  const openUnmatchedModal = useCallback(
    (tab = 0) => {
      if (!sessionId) return;
      setUnmatchedOpen(true);
      setUnmatchedTab(tab === 1 ? 1 : 0);
      loadUnmatched(sessionId);
    },
    [sessionId, loadUnmatched]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const b = await getBranches();
        const all = Array.isArray(b) ? b : [];
        const { byBranchId } = buildRolloutIndex();
        const filtered = showCompleted
          ? all
          : all.filter(x => !x?.regiondoMigration?.completedAt);
        const sorted = [...filtered].sort((a, b2) => {
          const ai = byBranchId.has(String(a?._id)) ? byBranchId.get(String(a._id)) : 9999;
          const bi = byBranchId.has(String(b2?._id)) ? byBranchId.get(String(b2._id)) : 9999;
          if (ai !== bi) return ai - bi;
          const an = String(a?.locationName || a?.longName || '').localeCompare(
            String(b2?.locationName || b2?.longName || ''),
            undefined,
            { sensitivity: 'base' }
          );
          if (an !== 0) return an;
          return String(a?._id || '').localeCompare(String(b2?._id || ''));
        });
        setBranches(sorted);
        if (!selectedBranchId || !sorted.some(x => String(x?._id) === String(selectedBranchId))) {
          if (sorted.length > 0) {
            setSelectedBranchId(String(sorted[0]._id));
          }
        }
      } catch (e) {
        setError('Branches konnten nicht geladen werden.');
      }
    };
    load();
  }, [showCompleted]);

  useEffect(() => {
    const load = async () => {
      if (!selectedBranchId) {
        setEntertainments([]);
        setSelectedEntertainmentId('');
        return;
      }
      try {
        const ents = await getAllEntertainmentsByBranch(selectedBranchId);
        const all = Array.isArray(ents) ? ents : [];
        const filtered = showCompleted ? all : all.filter(x => !x?.regiondoMigration?.completedAt);
        const sorted = [...filtered].sort((a, b) => {
          const an = String(a?.entertainmentName || a?.longName || '').localeCompare(
            String(b?.entertainmentName || b?.longName || ''),
            undefined,
            { sensitivity: 'base' }
          );
          if (an !== 0) return an;
          return String(a?._id || '').localeCompare(String(b?._id || ''));
        });
        setEntertainments(sorted);
        if (!selectedEntertainmentId || !sorted.some(x => String(x?._id) === String(selectedEntertainmentId))) {
          if (sorted.length > 0) {
            setSelectedEntertainmentId(String(sorted[0]._id));
          } else {
            setSelectedEntertainmentId('');
          }
        }
      } catch (e) {
        setError('Entertainments konnten nicht geladen werden.');
      }
    };
    load();
  }, [selectedBranchId, showCompleted]);

  useEffect(() => {
    const f = async () => {
      if (!selectedBranchId || !selectedEntertainmentId) {
        setSavedSessions([]);
        setSavedSessionId('');
        return;
      }
      setIsLoadingSavedSessions(true);
      try {
        const res = await sendRequest({
          url: `/api/v1/internalBookingMigration/regiondo/sessions?branchId=${encodeURIComponent(
            selectedBranchId
          )}&entertainmentId=${encodeURIComponent(
            selectedEntertainmentId
          )}&onlyWithMatching=true&limit=25`,
          method: 'GET',
          headers: authHeadersRef.current || auth.getAuthHeaders(),
        });
        const list = Array.isArray(res?.sessions) ? res.sessions : [];
        setSavedSessions(list);
        const current = String(savedSessionIdRef.current || '');
        const hasCurrent = Boolean(current && list.some(s => String(s?.sessionId) === current));
        const nextId = hasCurrent ? current : list?.[0]?.sessionId ? String(list[0].sessionId) : '';
        if (String(nextId) !== current) {
          setSavedSessionId(nextId);
        }
      } catch (_) {
        setSavedSessions([]);
        if (savedSessionIdRef.current) setSavedSessionId('');
      } finally {
        setIsLoadingSavedSessions(false);
      }
    };
    f();
  }, [selectedBranchId, selectedEntertainmentId, sendRequest]);

  const loadMatchingContext = useCallback(
    async sid => {
      if (!sid) return;
      try {
        const res = await sendRequest({
          url: `/api/v1/internalBookingMigration/regiondo/session/${sid}/matching/context`,
          method: 'GET',
          headers: auth.getAuthHeaders(),
        });
        if (res?.context) {
          const sidStr = String(sid);
          setMatchingContext(res.context);
          setAiSuggestions(res.context?.matching?.aiSuggestions || null);
          const m = res.context?.matching || {};

          const lastSid = lastLoadedMatchingDraftSessionIdRef.current;
          const isNewSession = !lastSid || String(lastSid) !== sidStr;
          if (isNewSession) {
            matchingDraftDirtyRef.current = false;
          }
          lastLoadedMatchingDraftSessionIdRef.current = sidStr;

          if (!matchingDraftDirtyRef.current) {
            setMatchingDraft({
              productByRegiondoProductName: m.productByRegiondoProductName || {},
              resourceByRegiondoProductName: m.resourceByRegiondoProductName || {},
              packageUpgradeByRegiondoProductName: m.packageUpgradeByRegiondoProductName || {},
              packageUpgradeNoneByRegiondoProductName: m.packageUpgradeNoneByRegiondoProductName || {},
              pricingByRegiondoKey: m.pricingByRegiondoKey || {},
              durationByRegiondoKey: m.durationByRegiondoKey || {},
              categoryByRegiondoKey: m.categoryByRegiondoKey || {},
              upgradeByRegiondoSoldItemKey: m.upgradeByRegiondoSoldItemKey || {},
              ignoredRegiondoSoldItemKeys: m.ignoredRegiondoSoldItemKeys || {},
            });
          }
        }
      } catch (e) {
        toast.error('Matching-Context konnte nicht geladen werden');
      }
    },
    [sendRequest, auth]
  );

  const ensureProductResourcesLoaded = useCallback(
    async productId => {
      const pid = String(productId || '').trim();
      if (!pid) return { status: 'done', resources: [], error: null };

      const cache = productResourcesCacheRef.current;
      const cur = cache.get(pid) || null;
      if (cur && cur.status === 'done') return cur;
      if (cur && cur.status === 'loading') return cur;

      cache.set(pid, { status: 'loading', resources: [], error: null });
      setProductResourcesCacheVersion(v => v + 1);

      try {
        const res = await getResourcesByProduct(pid);
        const list = Array.isArray(res) ? res : Array.isArray(res?.resources) ? res.resources : [];
        const normalized = (Array.isArray(list) ? list : [])
          .map(r => ({ ...r, _id: r?._id ?? r?.id }))
          .filter(r => Boolean(r?._id));
        const entry = { status: 'done', resources: normalized, error: null };
        cache.set(pid, entry);
        setProductResourcesCacheVersion(v => v + 1);
        return entry;
      } catch (e) {
        const entry = { status: 'error', resources: [], error: e?.message || 'Fehler beim Laden der Ressourcen' };
        cache.set(pid, entry);
        setProductResourcesCacheVersion(v => v + 1);
        return entry;
      }
    },
    [getResourcesByProduct]
  );

  const refreshMatchingContextOnly = useCallback(
    async sid => {
      if (!sid) return;
      try {
        const res = await sendRequest({
          url: `/api/v1/internalBookingMigration/regiondo/session/${sid}/matching/context`,
          method: 'GET',
          headers: auth.getAuthHeaders(),
        });
        if (res?.context) {
          setMatchingContext(res.context);
          setAiSuggestions(res.context?.matching?.aiSuggestions || null);
        }
      } catch (_) {
      }
    },
    [sendRequest, auth]
  );

  const loadSession = useCallback(
    async sid => {
      if (!sid) return null;
      try {
        const res = await sendRequest({
          url: `/api/v1/internalBookingMigration/regiondo/session/${sid}`,
          method: 'GET',
          headers: auth.getAuthHeaders(),
        });
        return res?.session || null;
      } catch (e) {
        return null;
      }
    },
    [sendRequest, auth]
  );

  const applySessionSnapshotToUi = useCallback(
    s => {
      if (!s) return;
      try {
        const phase = deriveStepPhaseFromSession(s);
        const progressPct = Number(s?.progressPercentage);
        const clamped = Number.isFinite(progressPct) ? Math.max(0, Math.min(100, progressPct)) : 0;
        const working = isSessionWorking(s);
        setStatus(prev => ({ ...prev, phase, progress: clamped }));
        if (s.stats) setStats(prev => ({ ...prev, ...s.stats }));
        if (!working && s?.resultData?.dryRun) setDryRunReport(s.resultData.dryRun);
        if (!working && s?.resultData?.import) setImportReport(s.resultData.import);
        if (typeof s?.sendBookingReviewMail === 'boolean') {
          setSendBookingReviewMail(Boolean(s.sendBookingReviewMail));
        }
        if (typeof s?.repairMode === 'boolean') {
          setRepairMode(Boolean(s.repairMode));
        }
        if (typeof s?.convertFutureOnlinePaymentsToOffline === 'boolean') {
          setConvertFutureOnlinePaymentsToOffline(Boolean(s.convertFutureOnlinePaymentsToOffline));
        }
        if (typeof s?.discountCodesApplyToUpgradesByDefault === 'boolean') {
          setDiscountCodesApplyToUpgradesByDefault(Boolean(s.discountCodesApplyToUpgradesByDefault));
        }
        if (typeof s?.mergeFrankfurtGolfComboBookings === 'boolean') {
          setMergeFrankfurtGolfComboBookings(Boolean(s.mergeFrankfurtGolfComboBookings));
        }
        if (!working) {
          try {
            setPreparedSuccessRows(extractPreparedSuccessRows(s));
          } catch (_) {
            setPreparedSuccessRows([]);
          }
        }
        const pi =
          s?.resultData?.importProgress
            ? { kind: 'import', ...s.resultData.importProgress }
            : s?.resultData?.dryRunProgress
              ? { kind: 'dryRun', ...s.resultData.dryRunProgress }
              : null;
        if (pi) setProgressInfo(pi);
        if (s?.status === 'failed' && s?.error?.message) {
          setError(String(s.error.message));
        }
      } catch (_) {}
    },
    []
  );

  const persistSessionSettings = useCallback(
    async (sid, patch) => {
      const id = String(sid || '').trim();
      if (!id || !patch || typeof patch !== 'object') return;
      try {
        await sendRequest({
          url: `/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(id)}/settings`,
          method: 'PUT',
          headers: auth.getAuthHeaders(),
          body: patch,
        });
      } catch (e) {
        toast.error('Einstellungen konnten nicht gespeichert werden');
        throw e;
      }
    },
    [sendRequest, auth]
  );

  useEffect(() => {
    return () => {
      if (sessionPollRef.current) {
        clearInterval(sessionPollRef.current);
        sessionPollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;

    const phase = String(status?.phase || '').trim();
    const prog = Number(status?.progress || 0);
    const shouldPoll =
      Boolean(isStarting || isDryRunning || isImporting) ||
      (phase === 'compare' && (!Number.isFinite(prog) || prog < 25)) ||
      (phase === 'checks' && (!Number.isFinite(prog) || prog < 75)) ||
      (phase === 'import' && (!Number.isFinite(prog) || prog < 100));

    if (!shouldPoll) {
      if (sessionPollRef.current) {
        clearInterval(sessionPollRef.current);
        sessionPollRef.current = null;
      }
      return;
    }

    if (sessionPollRef.current) {
      clearInterval(sessionPollRef.current);
      sessionPollRef.current = null;
    }

    let canceled = false;
    const tick = async () => {
      if (canceled) return;
      const s = await loadSession(sid);
      if (canceled) return;
      if (s) applySessionSnapshotToUi(s);
    };
    tick();

    sessionPollRef.current = setInterval(tick, 2000);

    return () => {
      canceled = true;
      if (sessionPollRef.current) {
        clearInterval(sessionPollRef.current);
        sessionPollRef.current = null;
      }
    };
  }, [
    sessionId,
    status?.phase,
    status?.progress,
    isStarting,
    isDryRunning,
    isImporting,
    loadSession,
    applySessionSnapshotToUi,
  ]);

  const loadLatestRun = useCallback(async () => {
    if (!selectedBranchId || !selectedEntertainmentId) return;
    try {
      const res = await sendRequest({
        url: `/api/v1/internalBookingMigration/regiondo/sessions?branchId=${encodeURIComponent(
          selectedBranchId
        )}&entertainmentId=${encodeURIComponent(
          selectedEntertainmentId
        )}&onlyWithMatching=false&limit=1`,
        method: 'GET',
        headers: auth.getAuthHeaders(),
      });
      const s = Array.isArray(res?.sessions) ? res.sessions[0] : null;
      if (!s?.sessionId) return;

      setSessionId(String(s.sessionId));
      const phase = deriveStepPhaseFromSession(s);
      const progressPct = Number(s?.progressPercentage);
      setStatus({
        phase,
        progress: Number.isFinite(progressPct) ? Math.max(0, Math.min(100, progressPct)) : 0,
        title:
          phase === 'import'
            ? 'Import'
            : phase === 'checks'
              ? 'Checks (Dry-Run)'
              : phase === 'match'
                ? 'Matching'
                : 'Vergleich',
        details:
          phase === 'import'
            ? 'Lauf ist im Import/abgeschlossen.'
            : phase === 'checks'
              ? 'Dry-Run läuft oder Import ist startbereit.'
              : phase === 'match'
                ? 'Vergleich ist fertig; Matching kann geprüft/gespeichert werden.'
                : 'Vergleich läuft oder ist noch nicht gestartet.',
      });

      const map = { compare: 0, match: 1, checks: 2, import: 3 };
      if (!userStepOverrideRef.current && map[phase] !== undefined) setActiveStep(map[phase]);
      if (s.stats) setStats(s.stats);

      const full = await loadSession(String(s.sessionId));
      if (!full) return;

      const fullWorking = isSessionWorking(full);

      const report = full?.resultData?.dryRun || null;
      if (!fullWorking && report) setDryRunReport(report);

      const imp = full?.resultData?.import || null;
      if (!fullWorking && imp) setImportReport(imp);

      const pi =
        full?.resultData?.importProgress
          ? { kind: 'import', ...full.resultData.importProgress }
          : full?.resultData?.dryRunProgress
            ? { kind: 'dryRun', ...full.resultData.dryRunProgress }
            : null;
      if (pi) setProgressInfo(pi);

      try {
        const authInfo = await sendRequest({
          url: `/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(
            String(s.sessionId)
          )}/regiondo/auth`,
          method: 'GET',
          headers: auth.getAuthHeaders(),
        });
        const ra = authInfo?.regiondoAuth || {};
        const hasCookie = Boolean(ra?.hasCookie);
        const usernameHint = ra?.usernameHint ? String(ra.usernameHint) : '';
        setRegiondoAuthInfo({
          hasCookie,
          isValid: ra?.isValid === true,
          checkedAt: ra?.checkedAt || null,
          reason: ra?.reason ? String(ra.reason) : '',
          usernameHint,
        });
        if (hasCookie) {
          if (usernameHint) setRegiondoUsername(usernameHint);
          setRegiondoPassword('');
        }
      } catch (_) {
        // fallback: keep UI state, login can still be performed manually
      }

      await loadMatchingContext(String(s.sessionId));
    } catch (_) {}
  }, [
    selectedBranchId,
    selectedEntertainmentId,
    sendRequest,
    auth,
    loadSession,
    loadMatchingContext,
  ]);

  const openCompare = useCallback(
    (target, context = null, options = null) => {
      const t = target && typeof target === 'object' ? target : null;
      if (!t) return;
      const preserveQueue = Boolean(options && options.preserveQueue);
      setCompareTarget(t);
      setCompareBookingId('');
      setCompareContext(context && typeof context === 'object' ? context : null);
      if (!preserveQueue) {
        setCompareQueueItems([]);
        setCompareQueueIndex(-1);
        compareQueueRef.current = [];
        compareQueueIndexRef.current = -1;
      }
      setCompareOpen(true);

      const directId = t.internalBookingId || t.internalBookingID || t.bookingId || '';
      if (directId && String(directId).trim()) {
        setCompareBookingId(String(directId).trim());
      }
    },
    []
  );

  const openCompareQueue = useCallback(
    items => {
      const list = Array.isArray(items) ? items.filter(Boolean) : [];
      if (list.length === 0) return;
      setCompareQueueItems(list);
      setCompareQueueIndex(0);
      compareQueueRef.current = list;
      compareQueueIndexRef.current = 0;
      const first = list[0] || null;
      if (first?.target) openCompare(first.target, first.context || null, { preserveQueue: true });
    },
    [openCompare]
  );

  const openCompareQueueAt = useCallback(
    (items, startIndex) => {
      const list = Array.isArray(items) ? items.filter(Boolean) : [];
      if (list.length === 0) return;
      const idxRaw = Number(startIndex);
      const idx = Number.isFinite(idxRaw) ? Math.max(0, Math.min(list.length - 1, Math.trunc(idxRaw))) : 0;
      setCompareQueueItems(list);
      setCompareQueueIndex(idx);
      compareQueueRef.current = list;
      compareQueueIndexRef.current = idx;
      const cur = list[idx] || null;
      if (cur?.target) openCompare(cur.target, cur.context || null, { preserveQueue: true });
    },
    [openCompare]
  );

  const moveCompareQueue = useCallback(
    dir => {
      const list = compareQueueRef.current || [];
      if (!Array.isArray(list) || list.length === 0) return;
      const cur = Number(compareQueueIndexRef.current);
      const next = Math.max(0, Math.min(list.length - 1, (Number.isFinite(cur) ? cur : 0) + Number(dir || 0)));
      if (next === cur) return;
      setCompareQueueIndex(next);
      compareQueueIndexRef.current = next;
      const item = list[next] || null;
      if (item?.target) openCompare(item.target, item.context || null, { preserveQueue: true });
    },
    [openCompare]
  );

  const openRandomCompareQueueItem = useCallback(() => {
    const list = compareQueueRef.current || [];
    if (!Array.isArray(list) || list.length === 0) return;
    const max = list.length;
    const cur = Number(compareQueueIndexRef.current);
    let next = Math.floor(Math.random() * max);
    if (max > 1 && Number.isFinite(cur) && next === cur) next = (next + 1) % max;
    setCompareQueueIndex(next);
    compareQueueIndexRef.current = next;
    const item = list[next] || null;
    if (item?.target) openCompare(item.target, item.context || null, { preserveQueue: true });
  }, [openCompare]);

  const loginRegiondoReseller = useCallback(async () => {
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    const u = String(regiondoUsername || '').trim();
    const p = String(regiondoPassword || '').trim();
    if (!u || !p) {
      toast.error('Regiondo Login: Bitte E-Mail und Passwort eingeben.');
      return;
    }
    setIsRegiondoLoggingIn(true);
    try {
      const res = await sendRequest({
        url: `/api/v1/internalBookingMigration/regiondo/session/${sid}/regiondo/auth/login`,
        method: 'POST',
        headers: auth.getAuthHeaders(),
        body: { regiondoUsername: u, regiondoPassword: p },
      });
      const hasCookie = Boolean(res?.regiondoAuth?.hasCookie);
      const usernameHint = res?.regiondoAuth?.usernameHint ? String(res.regiondoAuth.usernameHint) : u;
      setRegiondoAuthInfo({
        hasCookie,
        isValid: Boolean(res?.regiondoAuth?.isValid),
        checkedAt: res?.regiondoAuth?.checkedAt || null,
        reason: res?.regiondoAuth?.reason ? String(res.regiondoAuth.reason) : '',
        usernameHint,
      });
      if (hasCookie) {
        setRegiondoUsername(usernameHint);
        setRegiondoPassword('');
        lastRegiondoNeedsAuthSigRef.current = '';
        setDryRunReport(prev => {
          const rep = prev && typeof prev === 'object' ? prev : null;
          const samples = rep?.samples && typeof rep.samples === 'object' ? rep.samples : null;
          const counters = rep?.counters && typeof rep.counters === 'object' ? rep.counters : null;
          const list = Array.isArray(samples?.regiondoBookingScrapeErrors) ? samples.regiondoBookingScrapeErrors : [];
          if (list.length === 0) return prev;
          const nextList = list.filter(r => !r?.needsAdditionalAuth);
          if (nextList.length === list.length) return prev;
          const nextSamples = { ...samples, regiondoBookingScrapeErrors: nextList };
          const nextCounters = counters
            ? {
                ...counters,
                regiondoBookingScrapeErrors: Math.max(0, Number(counters.regiondoBookingScrapeErrors || 0) - (list.length - nextList.length)),
              }
            : counters;
          return { ...rep, samples: nextSamples, counters: nextCounters };
        });
        toast.success('Regiondo Login gespeichert');
      } else {
        toast.error('Regiondo Login fehlgeschlagen');
      }
    } catch (e) {
      toast.error(e?.message || 'Regiondo Login fehlgeschlagen');
    } finally {
      setIsRegiondoLoggingIn(false);
    }
  }, [auth, regiondoPassword, regiondoUsername, sendRequest, sessionId]);

  const buildRegiondoEmbedUrl = useCallback(
    bookingKey => {
      const sid = String(sessionId || '').trim();
      const bk = String(bookingKey || '').trim();
      if (!sid || !bk) return '';
      const base = import.meta.env.VITE_BE_URL || '';
      const token = auth?.token ? String(auth.token) : '';
      return `${base}/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(
        sid
      )}/regiondo/booking/${encodeURIComponent(bk)}/embed?authToken=${encodeURIComponent(token)}`;
    },
    [sessionId, auth?.token]
  );

  const buildRegiondoExternalBookingUrl = useCallback(bookingKey => {
    const bk = String(bookingKey || '').trim();
    if (!bk) return '';
    return `https://www.regiondo.de/resellers/account/bookingsview/key/${encodeURIComponent(bk)}`;
  }, []);

  useEffect(() => {
    regiondoEmbedFramesRef.current = Array.isArray(regiondoEmbedFrames) ? regiondoEmbedFrames : [];
  }, [regiondoEmbedFrames]);

  const pumpComparePreviewPrefetch = useCallback(() => {
    const maxConcurrent = 4;
    const queue = comparePreviewPrefetchQueueRef.current || [];
    comparePreviewPrefetchQueueRef.current = queue;

    while (comparePreviewPrefetchInFlightRef.current.size < maxConcurrent && queue.length > 0) {
      const job = queue.shift();
      if (!job) continue;
      const sid = String(job.sessionId || '').trim();
      const bk = String(job.bookingKey || '').trim();
      if (!sid || !bk) continue;
      const cacheKey = `v2:${sid}:${bk}`;
      if (comparePreviewCacheRef.current.has(cacheKey)) continue;
      if (comparePreviewPrefetchInFlightRef.current.has(cacheKey)) continue;

      comparePreviewPrefetchQueuedRef.current.delete(cacheKey);
      comparePreviewPrefetchInFlightRef.current.add(cacheKey);

      (async () => {
        try {
          const headers = authHeadersRef.current || (auth?.getAuthHeaders?.() || null);
          const req = sendRequestRef.current || sendRequest;
          const res = await req({
            url: `/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(
              sid
            )}/regiondo/booking/${encodeURIComponent(bk)}/preview`,
            method: 'GET',
            headers,
          });
          const next = res?.preview || null;
          if (next) comparePreviewCacheRef.current.set(cacheKey, next);
        } catch (_) {
          // ignore; a later foreground navigation can retry
        } finally {
          comparePreviewPrefetchInFlightRef.current.delete(cacheKey);
          pumpComparePreviewPrefetch();
        }
      })();
    }
  }, [auth, sendRequest]);

  useEffect(() => {
    if (!compareOpen) return;
    const sid = String(sessionId || '').trim();
    if (!sid) return;
    const list = compareQueueRef.current || [];
    if (!Array.isArray(list) || list.length === 0) return;
    const idx = Number.isFinite(Number(compareQueueIndexRef.current))
      ? Number(compareQueueIndexRef.current)
      : Number.isFinite(Number(compareQueueIndex))
        ? Number(compareQueueIndex)
        : -1;
    if (idx < 0) return;

    const start = Math.max(0, idx + 1);
    const end = Math.min(list.length, start + 22);

    for (let i = start; i < end; i += 1) {
      const item = list[i];
      const target = item?.target && typeof item.target === 'object' ? item.target : null;
      const bk = String(target?.bookingKey || target?.booking_key || '').trim();
      if (!bk) continue;
      const cacheKey = `v2:${sid}:${bk}`;
      if (comparePreviewCacheRef.current.has(cacheKey)) continue;
      if (comparePreviewPrefetchQueuedRef.current.has(cacheKey)) continue;
      if (comparePreviewPrefetchInFlightRef.current.has(cacheKey)) continue;
      comparePreviewPrefetchQueuedRef.current.add(cacheKey);
      comparePreviewPrefetchQueueRef.current.push({ sessionId: sid, bookingKey: bk });
    }

    pumpComparePreviewPrefetch();
  }, [compareOpen, compareQueueIndex, compareQueueItems, sessionId, pumpComparePreviewPrefetch]);

  const pumpRegiondoEmbedPrefetch = useCallback(
    desiredSet => {
      const maxConcurrent = 3;
      const desired = desiredSet instanceof Set ? desiredSet : null;
      const queue = regiondoEmbedPrefetchQueueRef.current || [];
      regiondoEmbedPrefetchQueueRef.current = queue;

      while (regiondoEmbedPrefetchInFlightRef.current.size < maxConcurrent && queue.length > 0) {
        const bk = String(queue.shift() || '').trim();
        if (!bk) continue;
        if (desired && !desired.has(bk)) continue;

        regiondoEmbedPrefetchQueuedRef.current.delete(bk);
        if (regiondoEmbedLoadedRef.current.has(bk)) continue;
        if (regiondoEmbedPrefetchInFlightRef.current.has(bk)) continue;

        const alreadyMounted = (regiondoEmbedFramesRef.current || []).some(x => String(x?.bookingKey || '').trim() === bk);
        if (alreadyMounted) continue;

        const url = buildRegiondoEmbedUrl(bk);
        if (!url) continue;

        regiondoEmbedPrefetchInFlightRef.current.add(bk);
        setRegiondoEmbedFrames(prev => {
          const cur = Array.isArray(prev) ? prev : [];
          if (cur.some(x => String(x?.bookingKey || '').trim() === bk)) return cur;
          return [...cur, { bookingKey: bk, url }];
        });
      }
    },
    [buildRegiondoEmbedUrl]
  );

  const markRegiondoEmbedLoaded = useCallback(
    bookingKey => {
      const bk = String(bookingKey || '').trim();
      if (!bk) return;
      regiondoEmbedLoadedRef.current.add(bk);
      regiondoEmbedPrefetchInFlightRef.current.delete(bk);
      pumpRegiondoEmbedPrefetch();
    },
    [pumpRegiondoEmbedPrefetch]
  );

  useEffect(() => {
    if (!compareOpen) return;
    const sid = String(sessionId || '').trim();
    const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
    const isLoggedIn = Boolean(regiondoAuthInfo?.hasCookie && regiondoAuthInfo?.isValid);
    if (!sid || !bk || !isLoggedIn) {
      setRegiondoEmbedFrames([]);
      regiondoEmbedFramesRef.current = [];
      regiondoEmbedPrefetchQueueRef.current = [];
      regiondoEmbedPrefetchQueuedRef.current.clear();
      regiondoEmbedPrefetchInFlightRef.current.clear();
      regiondoEmbedLoadedRef.current.clear();
      return;
    }

    const list = compareQueueRef.current || [];
    const idx = Number.isFinite(Number(compareQueueIndexRef.current))
      ? Number(compareQueueIndexRef.current)
      : Number.isFinite(Number(compareQueueIndex))
        ? Number(compareQueueIndex)
        : -1;

    const desiredKeys = [bk];
    if (Array.isArray(list) && list.length > 0 && idx >= 0) {
      const start = Math.max(0, idx + 1);
      const end = Math.min(list.length, start + 22);
      for (let i = start; i < end; i += 1) {
        const item = list[i];
        const target = item?.target && typeof item.target === 'object' ? item.target : null;
        const nextBk = String(target?.bookingKey || target?.booking_key || '').trim();
        if (nextBk) desiredKeys.push(nextBk);
      }
    }

    const desiredSet = new Set(desiredKeys);

    setRegiondoEmbedFrames(prev => {
      const cur = Array.isArray(prev) ? prev : [];
      const next = cur.filter(x => desiredSet.has(String(x?.bookingKey || '').trim()));
      const hasCurrent = next.some(x => String(x?.bookingKey || '').trim() === bk);
      if (hasCurrent) return next;
      const url = buildRegiondoEmbedUrl(bk);
      if (!url) return next;
      regiondoEmbedPrefetchInFlightRef.current.add(bk);
      return [...next, { bookingKey: bk, url }];
    });

    for (const k of Array.from(regiondoEmbedPrefetchQueuedRef.current)) {
      if (!desiredSet.has(k)) regiondoEmbedPrefetchQueuedRef.current.delete(k);
    }
    for (const k of Array.from(regiondoEmbedPrefetchInFlightRef.current)) {
      if (!desiredSet.has(k)) regiondoEmbedPrefetchInFlightRef.current.delete(k);
    }
    for (const k of Array.from(regiondoEmbedLoadedRef.current)) {
      if (!desiredSet.has(k)) regiondoEmbedLoadedRef.current.delete(k);
    }
    regiondoEmbedPrefetchQueueRef.current = (regiondoEmbedPrefetchQueueRef.current || []).filter(k =>
      desiredSet.has(String(k || '').trim())
    );

    for (const k of desiredKeys) {
      const key = String(k || '').trim();
      if (!key || key === bk) continue;
      if (regiondoEmbedLoadedRef.current.has(key)) continue;
      if (regiondoEmbedPrefetchQueuedRef.current.has(key)) continue;
      if (regiondoEmbedPrefetchInFlightRef.current.has(key)) continue;
      const alreadyMounted = (regiondoEmbedFramesRef.current || []).some(x => String(x?.bookingKey || '').trim() === key);
      if (alreadyMounted) continue;
      regiondoEmbedPrefetchQueuedRef.current.add(key);
      regiondoEmbedPrefetchQueueRef.current.push(key);
    }

    pumpRegiondoEmbedPrefetch(desiredSet);
  }, [
    compareOpen,
    compareQueueIndex,
    compareQueueItems,
    sessionId,
    compareTarget?.bookingKey,
    compareTarget?.booking_key,
    regiondoAuthInfo?.hasCookie,
    regiondoAuthInfo?.isValid,
    buildRegiondoEmbedUrl,
    pumpRegiondoEmbedPrefetch,
  ]);

  useEffect(() => {
    if (!compareOpen) return undefined;
    const onKeyDown = e => {
      if (!compareOpen) return;
      if (e.defaultPrevented) return;
      if (e.repeat) return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      const el = document.activeElement;
      if (el) {
        const tag = String(el.tagName || '').toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (el.isContentEditable) return;
        const role = typeof el.getAttribute === 'function' ? String(el.getAttribute('role') || '') : '';
        if (role === 'textbox') return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        moveCompareQueue(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        moveCompareQueue(1);
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [compareOpen, moveCompareQueue]);

  const selectedEntertainment = useMemo(() => {
    const list = Array.isArray(entertainments) ? entertainments : [];
    return list.find(e => String(e?._id) === String(selectedEntertainmentId)) || null;
  }, [entertainments, selectedEntertainmentId]);

  const buildMockInternalBooking = useCallback(
    target => {
      const t = target && typeof target === 'object' ? target : {};
      const bookingKey = t.bookingKey || t.booking_key || '';
      const orderNumber = t.orderNumber || t.order_number || t.bookingNumber || '';
      const now = dayjs();
      const start = now.toDate();
      const end = now.add(60, 'minute').toDate();

      const ent = selectedEntertainment || null;
      const entertainmentName =
        String(ent?.entertainmentName || ent?.shortName || ent?.longName || ent?.type || 'Regiondo').trim() ||
        'Regiondo';
      const channel = 'backend';

      return {
        _id: `mock:${bookingKey || orderNumber || Date.now()}`,
        bookingNumber: String(orderNumber || bookingKey || 'MOCK').trim() || 'MOCK',
        branchId: selectedBranchId || null,
        entertainmentId: selectedEntertainmentId || null,
        entertainment: entertainmentName,
        resourceId: null,
        firstName: 'Regiondo',
        lastName: 'Mock',
        mail: 'regiondo-migration@invalid.local',
        phone: '',
        companyName: '',
        startTime: start,
        endTime: end,
        eventDurationMS: 60 * 60 * 1000,
        eventCreatedAt: start,
        status: 'blocked',
        fullyPaid: false,
        totalAmount: null,
        participantCount: 0,
        laneCount: 0,
        comment: '',
        customerComment: '',
        pricingData: {},
        appliedDiscountCode: '',
        contactData: {},
        channel,
        initialPayment: 'offline',
        resourceVoucherIds: [],
        ltSpecific: { participatingMembers: [] },
        lineItems: [],
        canceledLineItems: [],
        bookingType: 'standard',
        meta: { createdAt: start, createdBy: 'regiondo-migration-mock' },
        legacyMeta: {
          regiondo: {
            booking_key: String(bookingKey || '').trim() || null,
            order_number: String(orderNumber || '').trim() || null,
          },
        },
      };
    },
    [selectedBranchId, selectedEntertainment, selectedEntertainmentId]
  );

  useEffect(() => {
    if (!auth?.getAuthHeaders) return;
    if (!selectedBranchId || !selectedEntertainmentId) return;
    const k = `${selectedBranchId}|${selectedEntertainmentId}`;
    if (k && lastLatestRunKeyRef.current === k) return;
    lastLatestRunKeyRef.current = k;
    loadLatestRun();
  }, [selectedBranchId, selectedEntertainmentId, loadLatestRun]);

  const applyMatchingFromSession = useCallback(async () => {
    if (!savedSessionId) return;
    setIsApplyingSavedMatching(true);
    try {
      const res = await sendRequest({
        url: `/api/v1/internalBookingMigration/regiondo/session/${savedSessionId}/matching/context`,
        method: 'GET',
        headers: auth.getAuthHeaders(),
      });
      const m = res?.context?.matching || {};

      matchingDraftDirtyRef.current = true;
      setMatchingDraft(prev => {
        const next = { ...prev };

        const mergeObj = (targetKey, source) => {
          const tgt = { ...(next[targetKey] || {}) };
          if (source && typeof source === 'object') {
            for (const [k, v] of Object.entries(source)) {
              if (!k) continue;
              if (v === null || v === undefined) continue;
              if (typeof v === 'string' && v.trim() === '') continue;
              tgt[k] = v;
            }
          }
          next[targetKey] = tgt;
        };

        mergeObj('productByRegiondoProductName', m.productByRegiondoProductName);
        mergeObj('resourceByRegiondoProductName', m.resourceByRegiondoProductName);
        mergeObj('packageUpgradeByRegiondoProductName', m.packageUpgradeByRegiondoProductName);
        mergeObj('packageUpgradeNoneByRegiondoProductName', m.packageUpgradeNoneByRegiondoProductName);
        mergeObj('pricingByRegiondoKey', m.pricingByRegiondoKey);
        mergeObj('durationByRegiondoKey', m.durationByRegiondoKey);
        mergeObj('categoryByRegiondoKey', m.categoryByRegiondoKey);
        mergeObj('upgradeByRegiondoSoldItemKey', m.upgradeByRegiondoSoldItemKey);

        const nonePkg = next.packageUpgradeNoneByRegiondoProductName || {};
        if (nonePkg && typeof nonePkg === 'object') {
          const pkgMap = { ...(next.packageUpgradeByRegiondoProductName || {}) };
          for (const [k, v] of Object.entries(nonePkg)) {
            if (!k) continue;
            if (v === true) {
              pkgMap[k] = '';
            }
          }
          next.packageUpgradeByRegiondoProductName = pkgMap;
        }

        const ignored = m.ignoredRegiondoSoldItemKeys && typeof m.ignoredRegiondoSoldItemKeys === 'object'
          ? m.ignoredRegiondoSoldItemKeys
          : {};
        const ignoredNext = { ...(next.ignoredRegiondoSoldItemKeys || {}) };
        for (const [k, v] of Object.entries(ignored)) {
          if (!k) continue;
          if (v === true) {
            ignoredNext[k] = true;
            next.upgradeByRegiondoSoldItemKey = { ...(next.upgradeByRegiondoSoldItemKey || {}) };
            next.upgradeByRegiondoSoldItemKey[k] = '';
          }
        }
        next.ignoredRegiondoSoldItemKeys = ignoredNext;

        return next;
      });

      toast.success('Matching aus Session übernommen');
      appendLog(`Matching übernommen aus Session ${savedSessionId}`);
    } catch (e) {
      toast.error('Matching konnte nicht übernommen werden');
    } finally {
      setIsApplyingSavedMatching(false);
    }
  }, [
    savedSessionId,
    sendRequest,
    auth,
    appendLog,
  ]);

  useEffect(() => {
    if (!isConnected) return;

    const handleUpdate = payload => {
      if (!payload) {
        return;
      }
      if (payload.sessionId && payload.sessionId !== sessionId) {
        return;
      }
      if (payload.status) setStatus(prev => ({ ...prev, ...payload.status }));
      if (payload.progressInfo && typeof payload.progressInfo === 'object') {
        setProgressInfo(prev => {
          const next = payload.progressInfo;
          const nextKeys = next ? Object.keys(next) : [];
          if (!next || nextKeys.length === 0) return prev;

          if (!prev) return next;
          const prevKind = String(prev?.kind || '').trim();
          const nextKind = String(next?.kind || '').trim();
          if (prevKind && nextKind && prevKind !== nextKind) return next;

          return { ...prev, ...next, kind: nextKind || prevKind };
        });
      }
      if (payload.stats) setStats(prev => ({ ...prev, ...payload.stats }));
      if (payload.log) appendLog(payload.log);
      if (payload.error) {
        setError(payload.error);
        stopStartingSpinner();
      }
      if (payload.status) {
        const phase = String(payload.status.phase || '').trim();
        const progress = Number(payload.status.progress || 0);
        if ((phase === 'compare' && progress >= 25) || (phase && phase !== 'compare')) {
          stopStartingSpinner();
        }
      }
      if (payload.aiSuggest && payload.aiSuggest.status) {
        const s = String(payload.aiSuggest.status);
        if (s === 'done') {
          setIsAiSuggesting(false);
          appendLog('AI Matching: fertig. Lade Vorschläge…');
          loadMatchingContext(payload.sessionId || sessionId);
          toast.success('AI Vorschläge sind da');
        } else if (s === 'error') {
          setIsAiSuggesting(false);
          toast.error(payload.error || 'AI Matching fehlgeschlagen');
        } else if (s === 'queued' || s === 'running') {
          setIsAiSuggesting(true);
        }
      }
    };

    on('migrationUpdate', handleUpdate);
    return () => off('migrationUpdate', handleUpdate);
  }, [on, off, isConnected, sessionId, appendLog, loadMatchingContext, stopStartingSpinner]);

  useEffect(() => {
    if (!sessionId) return;
    if (lastAutoContextLoadRef.current.sessionId === sessionId && matchingContext) return;
    lastAutoContextLoadRef.current.sessionId = sessionId;
    loadMatchingContext(sessionId);
  }, [sessionId, loadMatchingContext, matchingContext]);

  useEffect(() => {
    const sid = sessionId;
    if (!sid) return;
    if (status.phase !== 'checks') return;
    if (Number(status.progress || 0) < 75) return;
    const f = async () => {
      const s = await loadSession(sid);
      if (isSessionWorking(s)) return;
      const report = s?.resultData?.dryRun || null;
      if (report) setDryRunReport(report);
    };
    f();
  }, [sessionId, status.phase, status.progress, loadSession]);

  useEffect(() => {
    if (!sessionId) return;
    if (!isConnected) return;
    emit('join_migration', { sessionId });
  }, [sessionId, isConnected, emit]);

  const start = useCallback(async () => {
    setError(null);
    setLogs([]);
    setIsStarting(true);
    try {
      const res = await sendRequest({
        url: '/api/v1/internalBookingMigration/regiondo/session',
        method: 'POST',
        headers: auth.getAuthHeaders(),
        body: {
          branchId: selectedBranchId,
          entertainmentId: selectedEntertainmentId,
          dateFrom,
          dateTo,
          includeCanceled,
          includeMissingInDb,
          convertFutureOnlinePaymentsToOffline,
          discountCodesApplyToUpgradesByDefault,
          mergeFrankfurtGolfComboBookings,
          sendBookingReviewMail,
          repairMode,
        },
      });
      if (res?.sessionId) {
        setSessionId(res.sessionId);
        appendLog('Session erstellt. Starte Vergleich…');
        loadMatchingContext(res.sessionId);
        setStatus({
          phase: 'compare',
          progress: 1,
          title: 'Vergleich läuft…',
          details: 'Regiondo API wird abgefragt und mit DB verglichen.',
        });
        setActiveStep(0);

        if (startCompareTimeoutRef.current) {
          clearTimeout(startCompareTimeoutRef.current);
          startCompareTimeoutRef.current = null;
        }
        startCompareTimeoutRef.current = setTimeout(() => {
          setIsStarting(false);
        }, 90000);
      } else {
        setError('Unerwartete Antwort beim Start.');
        stopStartingSpinner();
      }
    } catch (e) {
      setError('Start fehlgeschlagen.');
      stopStartingSpinner();
    }
  }, [
    sendRequest,
    auth,
    selectedBranchId,
    selectedEntertainmentId,
    dateFrom,
    dateTo,
    includeCanceled,
    includeMissingInDb,
    convertFutureOnlinePaymentsToOffline,
    discountCodesApplyToUpgradesByDefault,
    mergeFrankfurtGolfComboBookings,
    sendBookingReviewMail,
    repairMode,
    appendLog,
    loadMatchingContext,
    stopStartingSpinner,
  ]);

  const runAiSuggest = useCallback(async () => {
    if (!sessionId) return;
    setError(null);
    setIsAiSuggesting(true);
    try {
      appendLog('AI Matching: queued…');
      await sendRequest({
        url: `/api/v1/internalBookingMigration/regiondo/session/${sessionId}/matching/suggest`,
        method: 'POST',
        headers: auth.getAuthHeaders(),
      });
      toast.info('AI Matching läuft im Hintergrund…');
    } catch (e) {
      toast.error(e?.message || 'AI Vorschläge fehlgeschlagen');
      setIsAiSuggesting(false);
    } finally {
      // keep disabled until backend signals completion via socket
    }
  }, [sessionId, sendRequest, auth, appendLog]);

  const syncMissingLegacy = useCallback(async () => {
    if (!sessionId) return;
    try {
      appendLog('Legacy Sync: starte Regiondo→Booking Sync…');
      const res = await sendRequest({
        url: `/api/v1/internalBookingMigration/regiondo/session/${sessionId}/legacy/sync`,
        method: 'POST',
        headers: auth.getAuthHeaders(),
      });
      if (res?.jobId) {
        toast.success('Legacy Sync gestartet');
        appendLog(`Legacy Sync queued (Job ${res.jobId}). Compare läuft danach automatisch neu.`);
        startLegacySyncPolling(sessionId);
      }
    } catch (e) {
      toast.error(e?.message || 'Legacy Sync fehlgeschlagen');
    }
  }, [sessionId, sendRequest, auth, appendLog, startLegacySyncPolling]);

  const applyAiSuggestions = useCallback(() => {
    if (!aiSuggestions) return;
    const next = { ...matchingDraft };
    if (aiSuggestions.productByRegiondoProductName) {
      next.productByRegiondoProductName = {
        ...next.productByRegiondoProductName,
        ...aiSuggestions.productByRegiondoProductName,
      };
    }
    if (aiSuggestions.resourceByRegiondoProductName) {
      next.resourceByRegiondoProductName = {
        ...(next.resourceByRegiondoProductName || {}),
        ...aiSuggestions.resourceByRegiondoProductName,
      };
    }
    if (aiSuggestions.packageUpgradeByRegiondoProductName) {
      next.packageUpgradeByRegiondoProductName = {
        ...(next.packageUpgradeByRegiondoProductName || {}),
        ...aiSuggestions.packageUpgradeByRegiondoProductName,
      };
    }
    if (aiSuggestions.pricingByRegiondoKey) {
      next.pricingByRegiondoKey = {
        ...next.pricingByRegiondoKey,
        ...aiSuggestions.pricingByRegiondoKey,
      };
    }
    if (aiSuggestions.durationByRegiondoKey) {
      next.durationByRegiondoKey = {
        ...next.durationByRegiondoKey,
        ...aiSuggestions.durationByRegiondoKey,
      };
    }
    if (aiSuggestions.categoryByRegiondoKey) {
      next.categoryByRegiondoKey = {
        ...next.categoryByRegiondoKey,
        ...aiSuggestions.categoryByRegiondoKey,
      };
    }
    if (aiSuggestions.upgradeByRegiondoSoldItemKey) {
      next.upgradeByRegiondoSoldItemKey = {
        ...(next.upgradeByRegiondoSoldItemKey || {}),
        ...aiSuggestions.upgradeByRegiondoSoldItemKey,
      };
    }
    matchingDraftDirtyRef.current = true;
    setMatchingDraft(next);
    toast.info('AI Vorschläge in Entwurf übernommen');
  }, [aiSuggestions, matchingDraft]);

  useEffect(() => {
    if (!aiSuggestions) return;
    if (isAiSuggesting) return;

    setMatchingDraft(prev => {
      let changed = false;
      const next = { ...prev };

      const mergeIfEmpty = (target, source) => {
        if (!source || typeof source !== 'object') return;
        for (const [k, v] of Object.entries(source)) {
          if (v == null || v === '') continue;
          const cur = target[k];
          if (cur == null || cur === '') {
            target[k] = v;
            changed = true;
          }
        }
      };

      next.productByRegiondoProductName = { ...(prev.productByRegiondoProductName || {}) };
      next.resourceByRegiondoProductName = { ...(prev.resourceByRegiondoProductName || {}) };
      next.packageUpgradeByRegiondoProductName = { ...(prev.packageUpgradeByRegiondoProductName || {}) };
      next.pricingByRegiondoKey = { ...(prev.pricingByRegiondoKey || {}) };
      next.durationByRegiondoKey = { ...(prev.durationByRegiondoKey || {}) };
      next.categoryByRegiondoKey = { ...(prev.categoryByRegiondoKey || {}) };
      next.upgradeByRegiondoSoldItemKey = { ...(prev.upgradeByRegiondoSoldItemKey || {}) };
      next.ignoredRegiondoSoldItemKeys = { ...(prev.ignoredRegiondoSoldItemKeys || {}) };

      mergeIfEmpty(next.productByRegiondoProductName, aiSuggestions.productByRegiondoProductName);
      mergeIfEmpty(next.resourceByRegiondoProductName, aiSuggestions.resourceByRegiondoProductName);
      mergeIfEmpty(next.packageUpgradeByRegiondoProductName, aiSuggestions.packageUpgradeByRegiondoProductName);
      mergeIfEmpty(next.pricingByRegiondoKey, aiSuggestions.pricingByRegiondoKey);
      mergeIfEmpty(next.durationByRegiondoKey, aiSuggestions.durationByRegiondoKey);
      mergeIfEmpty(next.categoryByRegiondoKey, aiSuggestions.categoryByRegiondoKey);
      mergeIfEmpty(next.upgradeByRegiondoSoldItemKey, aiSuggestions.upgradeByRegiondoSoldItemKey);

      if (changed) {
        matchingDraftDirtyRef.current = true;
      }
      return changed ? next : prev;
    });
  }, [aiSuggestions, isAiSuggesting]);

  const saveMatching = useCallback(async () => {
    if (!sessionId) return;
    try {
      await sendRequest({
        url: `/api/v1/internalBookingMigration/regiondo/session/${sessionId}/matching`,
        method: 'PUT',
        headers: auth.getAuthHeaders(),
        body: { matching: matchingDraft },
      });
      toast.success('Matching gespeichert');
      matchingDraftDirtyRef.current = false;
      setStatus(prev => {
        const phase = String(prev?.phase || '').trim();
        const prog = Number(prev?.progress || 0);
        const checksRunning = phase === 'checks' && prog < 75;
        const importRunning = phase === 'import';
        if (checksRunning || importRunning) {
          return prev;
        }
        return {
          ...prev,
          phase: 'match',
          progress: Math.max(prev.progress || 0, 50),
          title: 'Matching gespeichert',
          details: 'Als nächstes kannst du den Dry-Run starten oder weiter anpassen.',
        };
      });
    } catch (e) {
      toast.error('Matching speichern fehlgeschlagen');
    }
  }, [sessionId, sendRequest, auth, matchingDraft]);

  const isSelectedEntertainmentCompleted = Boolean(selectedEntertainment?.regiondoMigration?.completedAt);

  const startDryRun = useCallback(async () => {
    if (!sessionId) return;
    if (status?.phase === 'checks' && Number(status?.progress || 0) < 75) return;
    setIsDryRunning(true);
    setDryRunReport(null);
    setProgressInfo(prev => {
      if (prev && String(prev?.kind || '').trim() === 'dryRun') return null;
      return prev;
    });
    try {
      await sendRequest({
        url: `/api/v1/internalBookingMigration/regiondo/session/${sessionId}/dryrun`,
        method: 'POST',
        headers: auth.getAuthHeaders(),
      });
      toast.info('Dry-Run gestartet');
      setStatus(prev => ({
        ...prev,
        phase: 'checks',
        progress: Math.max(prev.progress || 0, 56),
        title: 'Dry-Run läuft…',
        details: 'Prüfungen laufen im Backend.',
      }));
    } catch (e) {
      if (e?.statusCode === 404 || String(e?.message || '').includes('Session nicht gefunden')) {
        toast.error('Session nicht gefunden. Bitte Session neu starten.');
        resetSessionUi();
        return;
      }
      toast.error('Dry-Run konnte nicht gestartet werden');
    }
  }, [sessionId, sendRequest, auth, status?.phase, status?.progress, resetSessionUi]);

  const startImport = useCallback(async (opts = null) => {
    if (!sessionId) return;
    const prog = Number(status?.progress || 0);
    if (String(status?.phase || '').trim() === 'import' && Number.isFinite(prog) && prog < 100) return;
    const force = Boolean(opts && typeof opts === 'object' && opts.force === true);
    setIsImporting(true);
    try {
      await sendRequest({
        url: `/api/v1/internalBookingMigration/regiondo/session/${sessionId}/import${force ? '?force=true' : ''}`,
        method: 'POST',
        headers: auth.getAuthHeaders(),
        body: excludeBookingKeysSet.size > 0 ? { excludeBookingKeys: Array.from(excludeBookingKeysSet) } : undefined,
      });
      toast.info('Import gestartet');
      setStatus(prev => ({
        ...prev,
        phase: 'import',
        progress: 0,
        title: 'Import läuft…',
        details: 'InternalBookings werden erstellt.',
      }));
    } catch (e) {
      if (e?.statusCode === 404 || String(e?.message || '').includes('Session nicht gefunden')) {
        toast.error('Session nicht gefunden. Bitte Session neu starten.');
        resetSessionUi();
        return;
      }
      toast.error('Import konnte nicht gestartet werden');
    }
  }, [sessionId, sendRequest, auth, status?.phase, status?.progress, resetSessionUi, excludeBookingKeysSet]);

  const restartImport = useCallback(async () => {
    if (!sessionId) return;
    const importRunning = Boolean(String(status?.phase || '').trim() === 'import' && Number(status?.progress || 0) < 100);
    if (importRunning) return;
    if (isImporting) return;
    setImportReport(null);
    setProgressInfo(prev => {
      if (prev && String(prev?.kind || '').trim() === 'import') return null;
      return prev;
    });
    appendLog('Import erneut gestartet…');
    await startImport();
  }, [appendLog, isImporting, sessionId, startImport, status?.phase, status?.progress]);

  const forceRestartImport = useCallback(async () => {
    if (!sessionId) return;
    const importRunning = Boolean(String(status?.phase || '').trim() === 'import' && Number(status?.progress || 0) < 100);
    if (importRunning) return;
    if (isImporting) return;
    setForceImportDialogOpen(false);
    setImportReport(null);
    setProgressInfo(prev => {
      if (prev && String(prev?.kind || '').trim() === 'import') return null;
      return prev;
    });
    appendLog('Force Import: lösche alte InternalBookings & starte Import erneut…');
    await startImport({ force: true });
  }, [appendLog, isImporting, sessionId, startImport, status?.phase, status?.progress]);

  const dryRunInProgress = Boolean(status?.phase === 'checks' && Number(status?.progress || 0) < 75);
  const dryRunActuallyRunning = (() => {
    if (String(status?.phase || '').trim() !== 'checks') return false;
    if (!progressInfo || String(progressInfo?.kind || '').trim() !== 'dryRun') return true;
    const processed = Number(progressInfo?.processed);
    const total2 = Number(progressInfo?.total);
    if (!Number.isFinite(processed) || !Number.isFinite(total2) || total2 <= 0) return true;
    return processed < total2;
  })();
  const importInProgress = Boolean(status?.phase === 'import' && Number(status?.progress || 0) < 100);

  useEffect(() => {
    if (!regiondoAuthInfo?.hasCookie) return;
    if (!isDryRunning && !dryRunInProgress && !dryRunActuallyRunning) return;
    const errs = Array.isArray(dryRunReport?.samples?.regiondoBookingScrapeErrors)
      ? dryRunReport.samples.regiondoBookingScrapeErrors
      : [];
    const needsAuthRows = errs
      .filter(e => Boolean(e?.needsAdditionalAuth))
      .map(e => String(e?.bookingKey || '').trim())
      .filter(Boolean)
      .sort();
    if (needsAuthRows.length === 0) return;
    const sig = needsAuthRows.join('|');
    if (sig && lastRegiondoNeedsAuthSigRef.current === sig) return;
    lastRegiondoNeedsAuthSigRef.current = sig;
    setRegiondoAuthInfo(prev => ({ ...prev, hasCookie: false, isValid: false }));
    toast.warning('Regiondo Session abgelaufen – bitte neu einloggen.');
  }, [
    dryRunActuallyRunning,
    dryRunInProgress,
    dryRunReport?.samples?.regiondoBookingScrapeErrors,
    isDryRunning,
    regiondoAuthInfo?.hasCookie,
  ]);

  useEffect(() => {
    if (isDryRunning && dryRunInProgress) return;
    if (isDryRunning && !dryRunInProgress) setIsDryRunning(false);
  }, [isDryRunning, dryRunInProgress]);

  useEffect(() => {
    if (isImporting && importInProgress) return;
    if (isImporting && !importInProgress) setIsImporting(false);
  }, [isImporting, importInProgress]);

  useEffect(() => {
    const phase = String(status?.phase || '').trim();
    const prog = Number(status?.progress || 0);
    if (!sessionId) return;
    if (phase !== 'import') return;
    if (!Number.isFinite(prog) || prog < 100) return;
    if (importReport) return;
    const f = async () => {
      const s = await loadSession(sessionId);
      const imp = s?.resultData?.import || null;
      if (imp) setImportReport(imp);
    };
    f();
  }, [sessionId, status?.phase, status?.progress, loadSession, importReport]);

  useEffect(() => {
    if (!importReport) return;
    const sig = [
      importReport?.createdCount,
      importReport?.skippedCount,
      importReport?.failedCount,
      importReport?.paidOnlineCount,
      importReport?.paidOfflineCount,
    ]
      .map(v => String(v ?? ''))
      .join('|');
    if (sig && lastImportPulseSigRef.current === sig) return;
    lastImportPulseSigRef.current = sig;

    // Only pulse the "done" checkmark once per unique import summary.
    setImportDonePulse(prev => (prev ? prev : Date.now()));
  }, [importReport]);

  const resolveProductById = useCallback(
    productId => {
      const arr = matchingContext?.internal?.products || [];
      return arr.find(p => String(p.productId) === String(productId)) || null;
    },
    [matchingContext]
  );

  const pricingOptionsForProduct = useCallback(
    productId => {
      const p = resolveProductById(productId);
      return Array.isArray(p?.pricing) ? p.pricing : [];
    },
    [resolveProductById]
  );

  const durationOptionsForPricing = useCallback(
    (productId, pricingId) => {
      const opts = pricingOptionsForProduct(productId);
      const pr = opts.find(x => String(x.pricingId) === String(pricingId));
      return Array.isArray(pr?.durations) ? pr.durations : [];
    },
    [pricingOptionsForProduct]
  );

  const categoryOptionsForPricing = useCallback(
    (productId, pricingId) => {
      const opts = pricingOptionsForProduct(productId);
      const pr = opts.find(x => String(x.pricingId) === String(pricingId));
      return Array.isArray(pr?.categories) ? pr.categories : [];
    },
    [pricingOptionsForProduct]
  );

  const isRallyeRegiondoProductName = useCallback(name => {
    const s = String(name || '').trim().toLowerCase();
    return s.includes('rallye') && s.includes('zone');
  }, []);

  const findRallyeInternalProductId = useCallback(() => {
    const internalProducts = matchingContext?.internal?.products || [];
    if (!Array.isArray(internalProducts) || internalProducts.length === 0) return null;

    const scored = internalProducts
      .map(p => {
        const name = String(p?.name || '').toLowerCase();
        const pricings = Array.isArray(p?.pricing) ? p.pricing : [];
        const pricingNames = pricings.map(x => String(x?.name || x?.pricingName || x?.list || '').toLowerCase());
        const stadtralleyHits = pricingNames.filter(x => x.includes('stadtralley')).length;
        const rallyeHits = pricingNames.filter(x => x.includes('rallye')).length;
        const baseScore =
          (name.includes('stadtralley') ? 2 : 0) +
          (name.includes('rallye') ? 1 : 0) +
          Math.min(5, stadtralleyHits) +
          Math.min(2, rallyeHits) +
          (pricings.length >= 4 ? 1 : 0);
        return { productId: p?.productId, score: baseScore, pricingsCount: pricings.length, name: p?.name };
      })
      .filter(x => String(x.productId || '').trim());

    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.pricingsCount !== a.pricingsCount) return b.pricingsCount - a.pricingsCount;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });

    const best = scored[0] || null;
    if (!best || best.score < 3) return null;
    return String(best.productId);
  }, [matchingContext]);

  const inferRallyeResourceKey = useCallback(regiondoProductName => {
    const s = String(regiondoProductName || '').trim().toLowerCase();
    const parts = s.split('•').map(x => x.trim()).filter(Boolean);
    const tail = parts.length > 1 ? parts[parts.length - 1] : s;

    if (tail.includes('firm') || tail.includes('firma') || tail.includes('event')) return 'firmenevent';
    if (tail.includes('herren') || tail.includes('bachelor')) return 'jga_herren';
    if (tail.includes('damen') || tail.includes('bachelorette')) return 'jga_damen';
    if (tail.includes('schul')) return 'schulklassen';
    if (tail.includes('kinder') || tail.includes('geburt') || tail.includes('fuchs')) return 'kindergeburtstag';

    return null;
  }, []);

  const findRallyeResourceIdInList = useCallback((resourceKey, resources) => {
    const list = Array.isArray(resources) ? resources : [];
    const low = x => String(x || '').toLowerCase();

    const matchBy = pred => list.find(r => pred(low(r?.name))) || null;

    if (resourceKey === 'firmenevent') {
      return matchBy(n => n.includes('firmen') || n.includes('firmenevent') || (n.includes('city') && n.includes('firmen')));
    }
    if (resourceKey === 'jga_herren') {
      return matchBy(n => (n.includes('jga') && n.includes('herren')) || (n.includes('bachelor') && n.includes('herren')));
    }
    if (resourceKey === 'jga_damen') {
      return matchBy(n => (n.includes('jga') && n.includes('damen')) || (n.includes('bachelor') && n.includes('damen')));
    }
    if (resourceKey === 'schulklassen') {
      return matchBy(n => n.includes('schul'));
    }
    if (resourceKey === 'kindergeburtstag') {
      return matchBy(n => n.includes('kinder') || n.includes('geburt') || n.includes('fuch'));
    }
    return null;
  }, []);

  useEffect(() => {
    if (!matchingContext) return;
    if (isAiSuggesting) return;

    const rallyeProductId = findRallyeInternalProductId();
    if (!rallyeProductId) return;

    const regiondoPairs = matchingContext?.regiondo?.pairs || [];
    const regiondoProducts = matchingContext?.regiondo?.products || [];

    const seen = new Set();
    const allNames = [];
    for (const rp of regiondoProducts) {
      const key = String(rp || '').trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      allNames.push(key);
    }
    for (const pair of regiondoPairs) {
      const key = String(pair?.regiondoProductName || '').trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      allNames.push(key);
    }

    const rallyeNames = allNames.filter(isRallyeRegiondoProductName);
    if (rallyeNames.length === 0) return;

    const resourceCacheEntry = productResourcesCacheRef.current.get(String(rallyeProductId || '').trim()) || null;
    const resourceCacheSig = resourceCacheEntry
      ? [
          resourceCacheEntry.status || 'unknown',
          ...(Array.isArray(resourceCacheEntry.resources)
            ? resourceCacheEntry.resources.map(r => String(r?._id || r?.id || '')).filter(Boolean).sort()
            : []),
        ].join(':')
      : 'empty';
    const autoMappingSig = [
      String(sessionId || ''),
      String(rallyeProductId || ''),
      resourceCacheSig,
      ...rallyeNames.slice().sort(),
    ].join('|');
    if (autoMappingSig && lastRallyeAutoMappingSigRef.current === autoMappingSig) return;
    lastRallyeAutoMappingSigRef.current = autoMappingSig;

    setMatchingDraft(prev => {
      const next = { ...prev };
      const curMap = prev.productByRegiondoProductName || {};
      let didChange = false;

      for (const rp of rallyeNames) {
        const cur = String(curMap?.[rp] || '').trim();
        if (cur) continue;
        if (!next.productByRegiondoProductName) next.productByRegiondoProductName = { ...curMap };
        next.productByRegiondoProductName[rp] = rallyeProductId;
        didChange = true;
      }

      if (!didChange) return prev;
      matchingDraftDirtyRef.current = true;
      return next;
    });

    let cancelled = false;
    ensureProductResourcesLoaded(rallyeProductId).then(entry => {
      if (cancelled) return;
      const resources = Array.isArray(entry?.resources) ? entry.resources : [];
      if (resources.length === 0) return;

      setMatchingDraft(prev => {
        const next = { ...prev };
        const curResMap = prev.resourceByRegiondoProductName || {};
        let didChange = false;

        for (const rp of rallyeNames) {
          const curRes = String(curResMap?.[rp] || '').trim();
          if (curRes) continue;
          const key = inferRallyeResourceKey(rp);
          if (!key) continue;
          const hit = findRallyeResourceIdInList(key, resources);
          const id = hit?._id ? String(hit._id) : '';
          if (!id) continue;
          if (!next.resourceByRegiondoProductName) next.resourceByRegiondoProductName = { ...curResMap };
          next.resourceByRegiondoProductName[rp] = id;
          didChange = true;
        }

        if (!didChange) return prev;
        matchingDraftDirtyRef.current = true;
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [
    matchingContext,
    sessionId,
    isAiSuggesting,
    productResourcesCacheVersion,
    findRallyeInternalProductId,
    isRallyeRegiondoProductName,
    ensureProductResourcesLoaded,
    inferRallyeResourceKey,
    findRallyeResourceIdInList,
  ]);

  useEffect(() => {
    if (!matchingContext) return;
    if (isAiSuggesting) return;

    const regiondoPairs = matchingContext?.regiondo?.pairs || [];
    if (!Array.isArray(regiondoPairs) || regiondoPairs.length === 0) return;

    const patchPricing = {};
    const patchDuration = {};
    const patchCategory = {};
    let changed = false;

    for (const pair of regiondoPairs) {
      if (!pair?.key) continue;
      const productId = String(matchingDraft.productByRegiondoProductName?.[pair.regiondoProductName] || '').trim();
      if (!productId) continue;

      let pricingId = String(matchingDraft.pricingByRegiondoKey?.[pair.key] || '').trim();
      if (!pricingId) {
        const pricingOptions = pricingOptionsForProduct(productId);
        if (pricingOptions.length === 1 && pricingOptions[0]?.pricingId) {
          pricingId = String(pricingOptions[0].pricingId);
          patchPricing[pair.key] = pricingId;
          changed = true;
        }
      }

      if (!pricingId) continue;

      const durationCur = matchingDraft.durationByRegiondoKey?.[pair.key];
      const durationMissing = durationCur === '' || durationCur === null || durationCur === undefined;
      if (durationMissing) {
        const durationOptions = durationOptionsForPricing(productId, pricingId);
        if (durationOptions.length === 1 && durationOptions[0]?.durationMS != null) {
          patchDuration[pair.key] = Number(durationOptions[0].durationMS);
          changed = true;
        }
      }

      const categoryCur = String(matchingDraft.categoryByRegiondoKey?.[pair.key] || '').trim();
      if (!categoryCur) {
        const categoryOptions = categoryOptionsForPricing(productId, pricingId);
        if (categoryOptions.length === 1 && categoryOptions[0]?.name) {
          patchCategory[pair.key] = String(categoryOptions[0].name);
          changed = true;
        }
      }
    }

    if (!changed) return;
    matchingDraftDirtyRef.current = true;
    setMatchingDraft(prev => ({
      ...prev,
      pricingByRegiondoKey: { ...(prev.pricingByRegiondoKey || {}), ...patchPricing },
      durationByRegiondoKey: { ...(prev.durationByRegiondoKey || {}), ...patchDuration },
      categoryByRegiondoKey: { ...(prev.categoryByRegiondoKey || {}), ...patchCategory },
    }));
  }, [
    matchingContext,
    matchingDraft,
    pricingOptionsForProduct,
    durationOptionsForPricing,
    categoryOptionsForPricing,
    isAiSuggesting,
  ]);

  const MatchingSection = useMemo(() => {
    if (!matchingContext) return null;
    const regiondoPairs = matchingContext?.regiondo?.pairs || [];
    const regiondoProducts = matchingContext?.regiondo?.products || [];
    const soldItemCandidates = matchingContext?.regiondo?.soldItemCandidates || [];
    const isGolfMode = (() => {
      const pairs = Array.isArray(regiondoPairs) ? regiondoPairs : [];
      let scanned = 0;
      let golfHits = 0;
      for (const p of pairs) {
        const rawProduct = String(p?.regiondoRawProductName || '').trim();
        const rawVariation = String(p?.regiondoRawVariationName || '').trim();
        const raw = `${rawProduct} ${rawVariation}`.trim().toLowerCase();
        if (!raw) continue;
        scanned += 1;
        if (raw.includes('golf')) golfHits += 1;
      }
      if (scanned === 0) return false;
      return golfHits / scanned >= 0.4;
    })();
    const regiondoProductsAll = (() => {
      const seen = new Set();
      const out = [];
      for (const rp of regiondoProducts) {
        const key = String(rp || '').trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(key);
      }
      for (const pair of regiondoPairs) {
        const key = String(pair?.regiondoProductName || '').trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(key);
      }
      const hasRallyeSpecific = base => out.some(x => String(x || '').startsWith(`${base} • `));
      return out.filter(x => {
        const s = String(x || '').trim();
        const isRallyeBase = /rallye\s*zone/i.test(s) && !s.includes('•');
        if (!isRallyeBase) return true;
        return !hasRallyeSpecific(s);
      });
    })();

    const internalProducts = matchingContext?.internal?.products || [];
    const internalPackages = matchingContext?.internal?.upgrades?.packages || [];
    const internalUpsells = matchingContext?.internal?.upgrades?.upsells || [];
    const internalUpgradesForSoldItems = [...internalPackages, ...internalUpsells];
    const internalUpgradeById = (() => {
      const map = new Map();
      for (const up of internalUpgradesForSoldItems) {
        const id = String(up?.upgradeId || '').trim();
        if (!id) continue;
        if (map.has(id)) continue;
        map.set(id, up);
      }
      return map;
    })();
    const resolveUpgradeById = id => internalUpgradeById.get(String(id || '').trim()) || null;

    return (
      <Card ref={matchRef} variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={8}>
              <Typography variant='h6' sx={{ fontWeight: 800 }}>
                Step 2: Matching
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {isGolfMode
                  ? 'Golf: Variation → Product, Option → Pricing, Dauer → durationMS, Kategorie → category.name'
                  : 'Regiondo Product → Product, Variation → Pricing, Dauer → durationMS, Kategorie → category.name'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                <Button variant='outlined' onClick={() => loadMatchingContext(sessionId)} disabled={!sessionId}>
                  Reload
                </Button>
                <Button variant='outlined' onClick={runAiSuggest} disabled={!sessionId || isAiSuggesting}>
                  {isAiSuggesting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <span>AI läuft…</span>
                    </Box>
                  ) : (
                    'AI Vorschlag holen'
                  )}
                </Button>
                <Button
                  variant='contained'
                  onClick={saveMatching}
                  disabled={!sessionId || isAiSuggesting}
                >
                  Matching speichern
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 1.25 }}>
            <Typography variant='caption' color='text.secondary'>
              Gespeichertes Matching laden
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.75, flexWrap: 'wrap' }}>
              <FormControl fullWidth size='small' sx={{ flex: 1, minWidth: 280 }}>
                <InputLabel id='fz-saved-session-label'>Session</InputLabel>
                <Select
                  labelId='fz-saved-session-label'
                  label='Session'
                  value={savedSessionId}
                  onChange={e => setSavedSessionId(e.target.value)}
                  disabled={isLoadingSavedSessions || savedSessions.length === 0}
                  displayEmpty
                  MenuProps={selectMenuProps}
                  sx={{
                    bgcolor: rmSurface.selectBg,
                    '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                  }}
                >
                  <MenuItem value=''>{isLoadingSavedSessions ? 'Lade…' : '—'}</MenuItem>
                  {savedSessions.map(s => (
                    <MenuItem key={s.sessionId} value={s.sessionId}>
                      {`${dayjs(s?.matching?.lockedAt || s?.meta?.createdAt).format('DD.MM.YYYY HH:mm')} • ${s.dateFrom} → ${s.dateTo}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title='Matching übernehmen'>
                <span>
                  <IconButton
                    size='small'
                    onClick={applyMatchingFromSession}
                    disabled={!sessionId || !savedSessionId || isApplyingSavedMatching || isLoadingSavedSessions}
                    sx={{
                      width: 42,
                      height: 40,
                      borderRadius: 1.5,
                      border: rmSurface.b22,
                    }}
                  >
                    {isApplyingSavedMatching ? <CircularProgress size={18} /> : <CheckIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {isAiSuggesting && (
            <Box
              sx={{
                mb: 2,
                px: 1.25,
                py: 1,
                borderRadius: 2,
                border: rmSurface.b10,
                backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                  AI befüllt Matching…
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Du kannst gleich wieder editieren, sobald die Vorschläge da sind.
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ opacity: isAiSuggesting ? 0.55 : 1, pointerEvents: isAiSuggesting ? 'none' : 'auto' }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Product Mapping
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {regiondoProductsAll.map(rp => (
                <Grid item xs={12} md={6} key={rp}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      p: 1.25,
                      borderRadius: 2,
                      border: rmSurface.b08,
                      borderColor:
                        (matchingDraft.productByRegiondoProductName?.[rp] || '') === ''
                          ? 'error.main'
                          : (() => {
                              const pid = String(matchingDraft.productByRegiondoProductName?.[rp] || '').trim();
                              const cacheEntry = pid ? productResourcesCacheRef.current.get(pid) || null : null;
                              const resources = Array.isArray(cacheEntry?.resources) ? cacheEntry.resources : [];
                              const rid = String(matchingDraft.resourceByRegiondoProductName?.[rp] || '').trim();
                              if (resources.length > 0 && !rid) return 'warning.main';
                              return rmSurface.neutral08;
                            })(),
                      backgroundColor: rmSurface.s02,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography variant='caption' color='text.secondary'>
                        {isGolfMode ? 'Regiondo Variation (als Produkt)' : 'Regiondo Product'}
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 700 }}>
                        {rp}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography variant='caption' color='text.secondary'>
                        Internes Product
                      </Typography>
                      <FormControl fullWidth size='small' sx={{ mt: 0.75 }}>
                        <Select
                          value={matchingDraft.productByRegiondoProductName?.[rp] || ''}
                          disabled={isAiSuggesting}
                          onChange={e => {
                            const nextProductId = e.target.value;
                            matchingDraftDirtyRef.current = true;
                            setMatchingDraft(prev => {
                              const prevProductId = prev.productByRegiondoProductName?.[rp] || '';
                              const didChange = String(prevProductId || '') !== String(nextProductId || '');
                              return {
                                ...prev,
                                productByRegiondoProductName: {
                                  ...prev.productByRegiondoProductName,
                                  [rp]: nextProductId,
                                },
                                resourceByRegiondoProductName: didChange
                                  ? {
                                      ...(prev.resourceByRegiondoProductName || {}),
                                      [rp]: '',
                                    }
                                  : prev.resourceByRegiondoProductName,
                              };
                            });
                            if (nextProductId) {
                              ensureProductResourcesLoaded(nextProductId).then(entry => {
                                const list = Array.isArray(entry?.resources) ? entry.resources : [];
                                if (list.length === 0) return;
                                const firstId = String(list[0]?._id || '').trim();
                                if (!firstId) return;
                                setMatchingDraft(prev => {
                                  const curPid = String(prev.productByRegiondoProductName?.[rp] || '').trim();
                                  if (curPid !== String(nextProductId || '').trim()) return prev;
                                  const curRes = String(prev.resourceByRegiondoProductName?.[rp] || '').trim();
                                  if (curRes) return prev;
                                  matchingDraftDirtyRef.current = true;
                                  return {
                                    ...prev,
                                    resourceByRegiondoProductName: {
                                      ...(prev.resourceByRegiondoProductName || {}),
                                      [rp]: firstId,
                                    },
                                  };
                                });
                              });
                            }
                          }}
                          displayEmpty
                          MenuProps={selectMenuProps}
                          sx={{
                            bgcolor: rmSurface.selectBg,
                            '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                          }}
                        >
                          <MenuItem value=''>—</MenuItem>
                          {internalProducts.map(p => (
                            <MenuItem key={p.productId} value={p.productId}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography variant='caption' color='text.secondary'>
                        Ressource (falls benötigt)
                      </Typography>
                      <FormControl fullWidth size='small' sx={{ mt: 0.75 }}>
                        <Select
                          value={matchingDraft.resourceByRegiondoProductName?.[rp] || ''}
                          disabled={isAiSuggesting || (matchingDraft.productByRegiondoProductName?.[rp] || '') === ''}
                          onOpen={() => {
                            const pid = String(matchingDraft.productByRegiondoProductName?.[rp] || '').trim();
                            if (!pid) return;
                            ensureProductResourcesLoaded(pid);
                          }}
                          renderValue={val => {
                            const v = String(val || '').trim();
                            const pid = String(matchingDraft.productByRegiondoProductName?.[rp] || '').trim();
                            const cacheEntry = pid ? productResourcesCacheRef.current.get(pid) || null : null;
                            const resources = Array.isArray(cacheEntry?.resources) ? cacheEntry.resources : [];

                            if (!v) {
                              if (cacheEntry?.status === 'loading') return 'Lade…';
                              if (cacheEntry?.status === 'error') return '— (Fehler)';
                              if (cacheEntry && resources.length === 0) return '— (keine Ressourcen)';
                              return '—';
                            }

                            const r = resources.find(x => String(x?._id) === v) || null;
                            return r?.name || v;
                          }}
                          onChange={e => {
                            matchingDraftDirtyRef.current = true;
                            setMatchingDraft(prev => ({
                              ...prev,
                              resourceByRegiondoProductName: {
                                ...(prev.resourceByRegiondoProductName || {}),
                                [rp]: e.target.value,
                              },
                            }));
                          }}
                          displayEmpty
                          MenuProps={selectMenuProps}
                          sx={{
                            bgcolor: rmSurface.selectBg,
                            '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                          }}
                        >
                          <MenuItem value=''>—</MenuItem>
                          {(() => {
                            const pid = String(matchingDraft.productByRegiondoProductName?.[rp] || '').trim();
                            const cacheEntry = pid ? productResourcesCacheRef.current.get(pid) || null : null;
                            const resources = Array.isArray(cacheEntry?.resources) ? cacheEntry.resources : [];
                            return resources.map(r => (
                              <MenuItem key={String(r._id)} value={String(r._id)}>
                                {r.name || String(r._id)}
                              </MenuItem>
                            ));
                          })()}
                        </Select>
                      </FormControl>
                      {(() => {
                        const pid = String(matchingDraft.productByRegiondoProductName?.[rp] || '').trim();
                        const cacheEntry = pid ? productResourcesCacheRef.current.get(pid) || null : null;
                        if (cacheEntry?.status !== 'error') return null;
                        return (
                          <Typography variant='caption' color='error.main'>
                            Ressourcen konnten nicht geladen werden
                          </Typography>
                        );
                      })()}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography variant='caption' color='text.secondary'>
                        Paket (optional)
                      </Typography>
                      <FormControl fullWidth size='small' sx={{ mt: 0.75 }}>
                        <Select
                          value={
                            matchingDraft.packageUpgradeNoneByRegiondoProductName?.[rp] === true
                              ? '__NONE__'
                              : matchingDraft.packageUpgradeByRegiondoProductName?.[rp] || ''
                          }
                          disabled={isAiSuggesting}
                          renderValue={val => {
                            const id = String(val || '');
                            if (id === '__NONE__') return '— (kein Paket)';
                            if (!id) return '—';
                            const u = internalPackages.find(x => String(x.upgradeId) === id);
                            return formatUpgradeLine(u, { resolveUpgradeById });
                          }}
                          onChange={e => {
                            matchingDraftDirtyRef.current = true;
                            setMatchingDraft(prev => ({
                              ...prev,
                              packageUpgradeByRegiondoProductName: {
                                ...(prev.packageUpgradeByRegiondoProductName || {}),
                                [rp]: e.target.value === '__NONE__' ? '' : e.target.value,
                              },
                              packageUpgradeNoneByRegiondoProductName: {
                                ...(prev.packageUpgradeNoneByRegiondoProductName || {}),
                                [rp]: e.target.value === '__NONE__',
                              },
                            }));
                          }}
                          displayEmpty
                          MenuProps={selectMenuProps}
                          sx={{
                            bgcolor: rmSurface.selectBg,
                            '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                          }}
                        >
                          <MenuItem value=''>—</MenuItem>
                          <MenuItem value='__NONE__'>— (kein Paket)</MenuItem>
                          {internalPackages.map(u => (
                            <MenuItem key={u.upgradeId} value={u.upgradeId}>
                              {formatUpgradeLine(u, { resolveUpgradeById })}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Upsell Matching (Regiondo solditemslist → internes Upgrade)
            </Typography>
            <Box
              sx={{
                maxHeight: 260,
                overflow: 'auto',
                borderRadius: 2,
                border: rmSurface.b08,
                mb: 2,
              }}
            >
              <Box sx={{ p: 1.5 }}>
                {soldItemCandidates.length === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    Keine Upsell-Kandidaten gefunden (noch keine solditemslist-Daten in der DB für diesen Zeitraum).
                  </Typography>
                ) : (
                  soldItemCandidates.map(c => (
                    <Box
                      key={c.key}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        backgroundColor: rmSurface.s03,
                        mb: 1,
                        border: rmSurface.b06,
                        borderColor:
                          matchingDraft.ignoredRegiondoSoldItemKeys?.[c.key] === true
                            ? rmSurface.neutral06
                            : (matchingDraft.upgradeByRegiondoSoldItemKey?.[c.key] || '') === ''
                              ? 'warning.main'
                              : rmSurface.neutral06,
                      }}
                    >
                      <Grid container spacing={1.5} alignItems='center'>
                        <Grid item xs={12} md={6}>
                          <Typography variant='caption' color='text.secondary'>
                            Regiondo (ticket_name)
                          </Typography>
                          <Typography variant='body2' sx={{ fontWeight: 700 }}>
                            {c.ticketName}
                          </Typography>
                          <Typography variant='body2' sx={{ opacity: 0.8 }}>
                            Vorkommen: {c.occurrences || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <Typography variant='caption' color='text.secondary'>
                          Internes Upgrade (Upsell/Paket)
                        </Typography>
                          <FormControl fullWidth size='small' sx={{ mt: 0.75 }}>
                            <Select
                              value={
                                matchingDraft.ignoredRegiondoSoldItemKeys?.[c.key] === true
                                  ? '__IGNORE__'
                                  : matchingDraft.upgradeByRegiondoSoldItemKey?.[c.key] || ''
                              }
                              disabled={isAiSuggesting}
                              renderValue={val => {
                                const v = String(val || '');
                                if (!v) return '—';
                                if (v === '__IGNORE__') return '— (kein Upsell)';
                                const u = internalUpgradesForSoldItems.find(x => String(x.upgradeId) === v);
                                return formatUpgradeLine(u, { resolveUpgradeById });
                              }}
                              onChange={e => {
                                matchingDraftDirtyRef.current = true;
                                setMatchingDraft(prev => ({
                                  ...prev,
                                  ignoredRegiondoSoldItemKeys: {
                                    ...(prev.ignoredRegiondoSoldItemKeys || {}),
                                    [c.key]: e.target.value === '__IGNORE__',
                                  },
                                  upgradeByRegiondoSoldItemKey:
                                    e.target.value === '__IGNORE__'
                                      ? {
                                          ...(prev.upgradeByRegiondoSoldItemKey || {}),
                                          [c.key]: '',
                                        }
                                      : {
                                          ...(prev.upgradeByRegiondoSoldItemKey || {}),
                                          [c.key]: e.target.value,
                                        },
                                }));
                              }}
                              displayEmpty
                              MenuProps={selectMenuProps}
                              sx={{
                                bgcolor: rmSurface.selectBg,
                                '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                              }}
                            >
                              <MenuItem value=''>—</MenuItem>
                              <MenuItem value='__IGNORE__'>— (kein Upsell)</MenuItem>
                              {internalUpgradesForSoldItems.map(u => (
                                <MenuItem key={u.upgradeId} value={u.upgradeId}>
                                  {formatUpgradeLine(u, { resolveUpgradeById })}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  ))
                )}
              </Box>
            </Box>

          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            {isGolfMode ? 'Option/Dauer/Kategorie Mapping (pro Paar)' : 'Variation/Dauer/Kategorie Mapping (pro Paar)'}
          </Typography>
          <Box
            sx={{
              maxHeight: 420,
              overflow: 'auto',
              borderRadius: 2,
              border: rmSurface.b08,
            }}
          >
            <Box sx={{ p: 1.5 }}>
              {regiondoPairs.map(pair => {
                const productId = matchingDraft.productByRegiondoProductName?.[pair.regiondoProductName] || '';
                const pricingId = matchingDraft.pricingByRegiondoKey?.[pair.key] || '';
                const durationMS = matchingDraft.durationByRegiondoKey?.[pair.key] ?? '';
                const categoryName = matchingDraft.categoryByRegiondoKey?.[pair.key] || '';
                const rawRegiondoLine = [pair.regiondoRawProductName, pair.regiondoRawVariationName, pair.regiondoRawOptionName]
                  .map(v => String(v || '').trim())
                  .filter(Boolean)
                  .join(' / ');
                const normalizedRegiondoLine = [pair.regiondoProductName, pair.regiondoVariationName]
                  .map(v => String(v || '').trim())
                  .filter(Boolean)
                  .join(' / ');
                const showRawRegiondoLine = Boolean(rawRegiondoLine && rawRegiondoLine !== normalizedRegiondoLine);
                const hasMissing =
                  !productId ||
                  !pricingId ||
                  durationMS === '' ||
                  durationMS === null ||
                  durationMS === undefined ||
                  !categoryName;
                const pricingOptions = productId ? pricingOptionsForProduct(productId) : [];
                const durationOptions = productId && pricingId ? durationOptionsForPricing(productId, pricingId) : [];
                const categoryOptions = productId && pricingId ? categoryOptionsForPricing(productId, pricingId) : [];

                return (
                  <Box
                    key={pair.key}
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      backgroundColor: rmSurface.s03,
                      mb: 1,
                      ml: pair.isOptionPair ? 3 : 0,
                      border: rmSurface.b06,
                      borderColor: hasMissing ? 'error.main' : rmSurface.neutral06,
                    }}
                  >
                    <Grid container spacing={1.5} alignItems='center'>
                      <Grid item xs={12} md={4}>
                        <Typography variant='caption' color='text.secondary'>
                          {pair.isOptionPair ? 'Regiondo Option' : 'Regiondo'}
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 700 }}>
                          {pair.regiondoProductName}
                        </Typography>
                        <Typography variant='body2' sx={{ opacity: 0.8 }}>
                          {pair.isOptionPair && pair.regiondoRawVariationName
                            ? `${pair.regiondoRawVariationName} → ${pair.regiondoVariationName}`
                            : pair.regiondoVariationName}
                        </Typography>
                        {showRawRegiondoLine && (
                          <Typography variant='caption' sx={{ opacity: 0.6, display: 'block' }}>
                            raw: {rawRegiondoLine}
                          </Typography>
                        )}
                        <Typography variant='body2' sx={{ opacity: 0.8 }}>
                          {pair.regiondoDurationMS ? `${Math.round(pair.regiondoDurationMS / 60000)} min` : '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant='caption' color='text.secondary'>
                          Pricing
                        </Typography>
                        <FormControl fullWidth size='small' sx={{ mt: 0.75 }}>
                          <Select
                            value={pricingId}
                              onChange={e => {
                                matchingDraftDirtyRef.current = true;
                                setMatchingDraft(prev => ({
                                ...prev,
                                pricingByRegiondoKey: {
                                  ...prev.pricingByRegiondoKey,
                                  [pair.key]: e.target.value,
                                },
                                }));
                              }}
                            disabled={!productId || isAiSuggesting}
                            displayEmpty
                            MenuProps={selectMenuProps}
                            sx={{
                              bgcolor: rmSurface.selectBg,
                              '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                            }}
                          >
                            <MenuItem value=''>—</MenuItem>
                            {pricingOptions.map(pr => (
                              <MenuItem key={pr.pricingId} value={pr.pricingId}>
                                {pr.list}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant='caption' color='text.secondary'>
                          Dauer (intern)
                        </Typography>
                        <FormControl fullWidth size='small' sx={{ mt: 0.75 }}>
                          <Select
                            value={durationMS === '' ? '' : Number(durationMS)}
                              onChange={e => {
                                matchingDraftDirtyRef.current = true;
                                setMatchingDraft(prev => ({
                                ...prev,
                                durationByRegiondoKey: {
                                  ...prev.durationByRegiondoKey,
                                  [pair.key]: Number(e.target.value),
                                },
                                }));
                              }}
                            disabled={!productId || !pricingId || isAiSuggesting}
                            displayEmpty
                            MenuProps={selectMenuProps}
                            sx={{
                              bgcolor: rmSurface.selectBg,
                              '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                            }}
                          >
                            <MenuItem value=''>—</MenuItem>
                            {durationOptions.map(d => (
                              <MenuItem key={d.durationMS} value={d.durationMS}>
                                {Math.round(d.durationMS / 60000)} min
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography variant='caption' color='text.secondary'>
                          Kategorie
                        </Typography>
                        <FormControl fullWidth size='small' sx={{ mt: 0.75 }}>
                          <Select
                            value={categoryName}
                              onChange={e => {
                                matchingDraftDirtyRef.current = true;
                                setMatchingDraft(prev => ({
                                ...prev,
                                categoryByRegiondoKey: {
                                  ...prev.categoryByRegiondoKey,
                                  [pair.key]: e.target.value,
                                },
                                }));
                              }}
                            disabled={!productId || !pricingId || isAiSuggesting}
                            displayEmpty
                            MenuProps={selectMenuProps}
                            sx={{
                              bgcolor: rmSurface.selectBg,
                              '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                            }}
                          >
                            <MenuItem value=''>—</MenuItem>
                            {categoryOptions.map(c => (
                              <MenuItem key={c.categoryId} value={c.name}>
                                {c.shortName || c.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }, [
    matchingContext,
    matchingDraft,
    sessionId,
    loadMatchingContext,
    runAiSuggest,
    saveMatching,
    aiSuggestions,
    pricingOptionsForProduct,
    durationOptionsForPricing,
    categoryOptionsForPricing,
    isAiSuggesting,
    selectMenuProps,
    ensureProductResourcesLoaded,
    productResourcesCacheVersion,
    openUnmatchedModal,
    savedSessionId,
    savedSessions,
    isLoadingSavedSessions,
    isApplyingSavedMatching,
    applyMatchingFromSession,
  ]);

  const ChecksAndImportSection = useMemo(() => {
    if (!sessionId) return null;
    const counters = dryRunReport?.counters || null;
    const total = counters?.total ?? null;
    const usable = counters?.usable ?? null;
    const ok = typeof total === 'number' && typeof usable === 'number' ? usable === total : null;
    const notFullyCheckedBreakdown = (() => {
      const prepared = Array.isArray(preparedSuccessRows) ? preparedSuccessRows : [];
      const list =
        prepared.length > 0
          ? prepared
          : Array.isArray(dryRunReport?.samples?.successChecked)
            ? dryRunReport.samples.successChecked
            : Array.isArray(dryRunReport?.samples?.success)
              ? dryRunReport.samples.success
              : [];

      const collectKeys = rows => {
        const set = new Set();
        if (!Array.isArray(rows)) return set;
        for (const r of rows) {
          const bk = String(r?.bookingKey || '').trim();
          if (bk) set.add(bk);
        }
        return set;
      };

      const keysLineItemsCheckErrors = collectKeys(dryRunReport?.samples?.lineItemsCheckErrors);
      const keysRegiondoTotalCheckErrors = collectKeys(dryRunReport?.samples?.regiondoTotalCheckErrors);
      const keysLineItemsMismatch = collectKeys(dryRunReport?.samples?.lineItemsMismatch);
      const keysRegiondoTotalMismatch = collectKeys(dryRunReport?.samples?.regiondoTotalMismatch);
      const keysRegiondoTotalMismatchButCheckoutOk = collectKeys(dryRunReport?.samples?.regiondoTotalMismatchButCheckoutOk);
      const keysRegiondoAdjustmentsUnresolved = collectKeys(dryRunReport?.samples?.regiondoAdjustmentsUnresolved);
      const keysRegiondoTotalsUnclearNotImportable = collectKeys(
        dryRunReport?.samples?.regiondoTotalsUnclearNotImportable
      );

      const badKeys = new Set([
        ...keysLineItemsCheckErrors,
        ...keysRegiondoTotalCheckErrors,
        ...keysLineItemsMismatch,
        ...keysRegiondoTotalMismatch,
        ...keysRegiondoAdjustmentsUnresolved,
        ...keysRegiondoTotalsUnclearNotImportable,
      ]);

      const listKeys = new Set(list.map(r => String(r?.bookingKey || '').trim()).filter(Boolean));
      const badKeysInList = new Set([...badKeys].filter(k => listKeys.has(k)));

      const fullyCheckedCount =
        prepared.length > 0
          ? prepared.length
          : Array.isArray(dryRunReport?.samples?.successChecked)
            ? list.length
            : Math.max(0, list.length - badKeysInList.size);

      return {
        fullyCheckedCount,
        badKeysInListCount: prepared.length > 0 ? 0 : badKeysInList.size,
        sources: {
          lineItemsCheckErrors: keysLineItemsCheckErrors.size,
          regiondoTotalCheckErrors: keysRegiondoTotalCheckErrors.size,
          lineItemsMismatch: keysLineItemsMismatch.size,
          regiondoTotalMismatchButCheckoutOk: keysRegiondoTotalMismatchButCheckoutOk.size,
          regiondoTotalsUnclearNotImportable: keysRegiondoTotalsUnclearNotImportable.size,
        },
      };
    })();
    const importableFullyCheckedCount = Number(notFullyCheckedBreakdown?.fullyCheckedCount || 0);
    const importableBaseListForExclude = (() => {
      const prepared = Array.isArray(preparedSuccessRows) ? preparedSuccessRows : [];
      if (prepared.length > 0) return prepared;
      if (Array.isArray(dryRunReport?.samples?.successChecked)) return dryRunReport.samples.successChecked;
      if (Array.isArray(dryRunReport?.samples?.success)) return dryRunReport.samples.success;
      return [];
    })();
    const excludedInRunCount = (() => {
      if (!importableBaseListForExclude || importableBaseListForExclude.length === 0) return 0;
      let n = 0;
      for (const r of importableBaseListForExclude) {
        const bk = String(r?.bookingKey || '').trim();
        if (bk && excludeBookingKeysSet.has(bk)) n++;
      }
      return n;
    })();
    const baseNotUsableCount =
      typeof counters?.total === 'number' && typeof counters?.usable === 'number'
        ? Math.max(0, Number(counters.total) - Number(counters.usable))
        : null;
    const notFullyCheckedUsableCount =
      typeof counters?.usable === 'number' ? Math.max(0, Number(counters.usable) - importableFullyCheckedCount) : null;
    const isChecksRunning = String(status?.phase || '').trim() === 'checks' && Number(status?.progress || 0) < 75;
    const checksProgress = (() => {
      if (progressInfo && String(progressInfo?.kind || '').trim() === 'dryRun') {
        const processed = Number(progressInfo?.processed);
        const total2 = Number(progressInfo?.total);
        if (Number.isFinite(processed) && Number.isFinite(total2) && total2 > 0) {
          return Math.max(0, Math.min(100, Math.round((processed / total2) * 100)));
        }
      }
      if (isChecksRunning) return 0;
      return Math.max(0, Math.min(100, Number(status?.progress || 0)));
    })();
    const checksDetailsFromStatus = String(status?.details || '').trim();
    const checksDetailsFromProgressInfo =
      progressInfo && String(progressInfo?.kind || '').trim() === 'dryRun'
        ? (() => {
            const processed = Number(progressInfo?.processed);
            const total2 = Number(progressInfo?.total);
            if (!Number.isFinite(processed) || !Number.isFinite(total2) || total2 <= 0) return '';
            return `${processed}/${total2} geprüft`;
          })()
        : '';
    const checksDetails = checksDetailsFromProgressInfo || checksDetailsFromStatus;
    const mode = stepKey === 'import' ? 'import' : 'checks';
    const overallProgress = Math.max(0, Math.min(100, Number(status?.progress || 0)));
    const isImportRunning = String(status?.phase || '').trim() === 'import' && overallProgress < 100;
    const importDetailsFromProgressInfo =
      progressInfo && String(progressInfo?.kind || '').trim() === 'import'
        ? (() => {
            const processed = Number(progressInfo?.processed);
            const total2 = Number(progressInfo?.total);
            const created = Number(progressInfo?.createdCount);
            const skipped = Number(progressInfo?.skippedCount);
            const failed = Number(progressInfo?.failedCount);
            const parts = [];
            if (Number.isFinite(processed) && Number.isFinite(total2) && total2 > 0) {
              parts.push(`${processed}/${total2} verarbeitet`);
            }
            if (Number.isFinite(created)) parts.push(`created ${created}`);
            if (Number.isFinite(skipped)) parts.push(`skipped ${skipped}`);
            if (Number.isFinite(failed)) parts.push(`failed ${failed}`);
            return parts.join(' • ');
          })()
        : '';

    const openImportCompareQueue = ({ list, title, filterFn = null }) => {
      const rows = Array.isArray(list) ? list : [];
      const filtered = typeof filterFn === 'function' ? rows.filter(filterFn) : rows;
      const items = filtered
        .map(r => {
          const bk = String(r?.bookingKey || '').trim();
          const ib = String(r?.internalBookingId || '').trim();
          const on = String(r?.orderNumber || r?.bookingNumber || '').trim();
          if (!ib && !bk) return null;
          return {
            target: { bookingKey: bk, orderNumber: on, internalBookingId: ib },
            context: { kind: 'import', title, row: r, bookingKey: bk },
          };
        })
        .filter(Boolean);
      if (items.length === 0) {
        toast.info('Keine Beispiele vorhanden.');
        return;
      }
      openCompareQueue(items);
    };

    const openImportPaymentQueue = kind => {
      const created = Array.isArray(importReport?.created) ? importReport.created : [];
      const skipped = Array.isArray(importReport?.skippedSamples) ? importReport.skippedSamples : [];
      const rows = [...created, ...skipped];
      const hasPaymentField = rows.some(r => r && typeof r === 'object' && r.initialPayment != null);
      if (!hasPaymentField) {
        toast.info('Für Online/Offline/Unbekannt sind in diesem Import-Report keine Beispiele gespeichert. Bitte Import erneut starten.');
        return;
      }
      const k = String(kind || '').trim().toLowerCase();
      const filterFn =
        k === 'online'
          ? r => String(r?.initialPayment || '').toLowerCase() === 'online'
          : k === 'offline'
            ? r => String(r?.initialPayment || '').toLowerCase() === 'offline'
            : r => {
                const p = String(r?.initialPayment || '').toLowerCase();
                return p !== 'online' && p !== 'offline';
              };
      openImportCompareQueue({
        list: rows,
        title: k === 'online' ? 'Online bezahlt' : k === 'offline' ? 'Offline bezahlt' : 'Nicht bezahlt/Unbekannt',
        filterFn,
      });
    };

    const paymentMethodCounts = (() => {
      const created = Array.isArray(importReport?.created) ? importReport.created : [];
      const totalRaw = Number(importReport?.createdCount);
      const total = Number.isFinite(totalRaw) ? totalRaw : created.length;

      const onlineRaw = Number(importReport?.paymentMethodOnlineCount);
      const offlineRaw = Number(importReport?.paymentMethodOfflineCount);
      const online = Number.isFinite(onlineRaw)
        ? onlineRaw
        : created.filter(r => String(r?.initialPayment || '').toLowerCase() === 'online').length;
      const offline = Number.isFinite(offlineRaw)
        ? offlineRaw
        : created.filter(r => String(r?.initialPayment || '').toLowerCase() === 'offline').length;
      const unknown = Math.max(0, total - online - offline);
      return { total, online, offline, unknown };
    })();

    const CheckRow = ({ label, value, good, blocksImport = false, detailsKey = null, hasDetails = false }) => {
      const checkRowTheme = useTheme();
      const k = String(label || '');
      const prev = checksPopStateRef.current.get(k);
      const shouldPop = Boolean(good === true && prev?.good !== true);
      checksPopStateRef.current.set(k, { good: Boolean(good), value: String(value ?? '') });

      const Icon = blocksImport && !good ? HighlightOffIcon : CheckCircleIcon;
      const iconColor = good
        ? checkRowTheme.palette.success.dark
        : blocksImport
          ? checkRowTheme.palette.error.dark
          : checkRowTheme.palette.warning.dark;

      return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 0.75,
          px: 1,
          borderRadius: 2,
          backgroundColor: rmSurface.s03,
          border: rmSurface.b06,
          cursor: hasDetails ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (!hasDetails || !detailsKey) return;
          setDryRunDetailsKey(detailsKey);
          setDryRunDetailsOpen(true);
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon
            sx={{
              color: iconColor,
              transform: good ? 'scale(1.0)' : 'scale(0.96)',
              animation: shouldPop
                ? 'fzCheckPop 520ms cubic-bezier(0.2, 0.8, 0.2, 1) both'
                : 'none',
              '@keyframes fzCheckPop': {
                '0%': { transform: 'scale(0.2)', opacity: 0 },
                '55%': { transform: 'scale(1.12)', opacity: 1 },
                '100%': { transform: 'scale(1.0)', opacity: 1 },
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }}
          />
          <Typography variant='body2' sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
        </Box>
        <Typography variant='body2' color='text.secondary'>
          {value}
        </Typography>
      </Box>
      );
    };

    return (
      <Card ref={checksRef} variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={8}>
              <Typography variant='h6' sx={{ fontWeight: 800 }}>
                {mode === 'checks' ? 'Step 3: Checks (Dry-Run)' : 'Step 4: Import (Live)'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {mode === 'checks'
                  ? 'Dry-Run prüft die Daten (inkl. Details zu Mismatches/fehlenden Mappings).'
                  : 'Startet den Live-Import. Details siehst du im Report unten.'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                {mode === 'checks' ? (
                  <Button variant='outlined' onClick={startDryRun} disabled={!sessionId || isDryRunning || dryRunInProgress}>
                    {isDryRunning || dryRunInProgress ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <span>Dry-Run…</span>
                      </Box>
                    ) : (
                      'Dry-Run starten'
                    )}
                  </Button>
                ) : null}
                {mode === 'import' ? (
                  <>
                    <Button variant='contained' onClick={startImport} disabled={!sessionId || isImporting || importInProgress}>
                      {isImporting || importInProgress ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <span>Import…</span>
                        </Box>
                      ) : (
                        'Import starten'
                      )}
                    </Button>
                    <Button
                      variant='outlined'
                      color='warning'
                      onClick={restartImport}
                      disabled={!sessionId || isImporting || importInProgress}
                    >
                      Import erneut starten
                    </Button>
                    <Button
                      variant='outlined'
                      color='error'
                      onClick={() => setForceImportDialogOpen(true)}
                      disabled={!sessionId || isImporting || importInProgress}
                    >
                      Force (alte löschen)
                    </Button>
                  </>
                ) : null}
              </Box>
            </Grid>
          </Grid>

          {mode === 'checks' ? (
            <Box sx={{ mt: 1.5 }}>
              {!(regiondoAuthInfo?.hasCookie && regiondoAuthInfo?.isValid) ? (
                <Box sx={{ maxWidth: 520 }}>
                  <Alert severity={regiondoAuthInfo?.hasCookie ? 'error' : 'warning'} sx={{ mb: 1.5 }}>
                    {regiondoAuthInfo?.hasCookie
                      ? 'Regiondo Session ist abgelaufen. Bitte neu einloggen, sonst kann der Scrape-Resolver nicht laufen.'
                      : 'Regiondo ist nicht eingeloggt. Der Scrape-Resolver kann ohne Login nicht laufen. Bitte einmal einloggen und dann den Dry-Run erneut starten.'}
                  </Alert>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <TextField
                        label='Regiondo E-Mail'
                        value={regiondoUsername}
                        onChange={e => setRegiondoUsername(e.target.value)}
                        size='small'
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label='Regiondo Passwort'
                        value={regiondoPassword}
                        onChange={e => setRegiondoPassword(e.target.value)}
                        size='small'
                        type='password'
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant='contained'
                        disabled={isRegiondoLoggingIn || !regiondoUsername || !regiondoPassword}
                        onClick={loginRegiondoReseller}
                        fullWidth
                      >
                        {isRegiondoLoggingIn ? 'Login…' : 'Einloggen & Session speichern'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Alert severity='success' sx={{ mb: 0 }}>
                  Regiondo Session ist aktiv. Scraping kann verwendet werden.
                </Alert>
              )}
            </Box>
          ) : null}

          <Box sx={{ mt: 1.5 }}>
            <Button
              variant='contained'
              color='success'
              fullWidth
              size='large'
              disabled={(() => {
                if (!sessionId) return true;
                if (mode === 'checks') {
                  const prepared = Array.isArray(preparedSuccessRows) ? preparedSuccessRows : [];
                  let list =
                    prepared.length > 0
                      ? prepared
                      : Array.isArray(dryRunReport?.samples?.successChecked)
                        ? dryRunReport.samples.successChecked
                        : Array.isArray(dryRunReport?.samples?.success)
                          ? dryRunReport.samples.success
                          : [];
                  const badKeys = new Set(
                    [
                      ...(Array.isArray(dryRunReport?.samples?.lineItemsCheckErrors) ? dryRunReport.samples.lineItemsCheckErrors : []),
                      ...(Array.isArray(dryRunReport?.samples?.regiondoTotalCheckErrors) ? dryRunReport.samples.regiondoTotalCheckErrors : []),
                      ...(Array.isArray(dryRunReport?.samples?.lineItemsMismatch) ? dryRunReport.samples.lineItemsMismatch : []),
                      ...(Array.isArray(dryRunReport?.samples?.regiondoTotalsUnclearNotImportable) ? dryRunReport.samples.regiondoTotalsUnclearNotImportable : []),
                    ]
                      .map(r => String(r?.bookingKey || '').trim())
                      .filter(Boolean)
                  );
                  if (badKeys.size > 0) {
                    list = list.filter(r => {
                      const bk = String(r?.bookingKey || '').trim();
                      return bk && !badKeys.has(bk);
                    });
                  }
                  return isChecksRunning || isDryRunning || dryRunInProgress || dryRunActuallyRunning || list.length === 0;
                }
                const list = Array.isArray(importReport?.created) ? importReport.created : [];
                const total = Number(importReport?.createdCount);
                if (Number.isFinite(total) && total > 0) return false;
                return list.length === 0;
              })()}
              onClick={() => {
                if (mode === 'checks') {
                  if (isChecksRunning || isDryRunning || dryRunInProgress || dryRunActuallyRunning) return;
                  const prepared = Array.isArray(preparedSuccessRows) ? preparedSuccessRows : [];
                  let list =
                    prepared.length > 0
                      ? prepared
                      : Array.isArray(dryRunReport?.samples?.successChecked)
                        ? dryRunReport.samples.successChecked
                        : Array.isArray(dryRunReport?.samples?.success)
                          ? dryRunReport.samples.success
                          : [];
                  const badKeys = new Set(
                    [
                      ...(Array.isArray(dryRunReport?.samples?.lineItemsCheckErrors) ? dryRunReport.samples.lineItemsCheckErrors : []),
                      ...(Array.isArray(dryRunReport?.samples?.regiondoTotalCheckErrors) ? dryRunReport.samples.regiondoTotalCheckErrors : []),
                      ...(Array.isArray(dryRunReport?.samples?.lineItemsMismatch) ? dryRunReport.samples.lineItemsMismatch : []),
                      ...(Array.isArray(dryRunReport?.samples?.regiondoTotalsUnclearNotImportable) ? dryRunReport.samples.regiondoTotalsUnclearNotImportable : []),
                    ]
                      .map(r => String(r?.bookingKey || '').trim())
                      .filter(Boolean)
                  );
                  if (prepared.length === 0 && !Array.isArray(dryRunReport?.samples?.successChecked)) {
                    // kept for backwards compatibility (older reports), but all known error keys
                    // are already covered by badKeys below.
                  }
                  if (badKeys.size > 0) {
                    list = list.filter(r => {
                      const bk = String(r?.bookingKey || '').trim();
                      if (!bk) return false;
                      return !badKeys.has(bk);
                    });
                  }
                  const items = list
                    .map(r => {
                      const bk = String(r?.bookingKey || '').trim();
                      const on = String(r?.orderNumber || '').trim();
                      if (!bk) return null;
                      return {
                        target: { bookingKey: bk, orderNumber: on },
                        context: { kind: 'dryRun', detailsKey: 'successChecked', row: { bookingKey: bk, orderNumber: on || null } },
                      };
                    })
                    .filter(Boolean);
                  openCompareQueue(items);
                  return;
                }

                const list = Array.isArray(importReport?.created) ? importReport.created : [];
                const items = list
                  .map(r => {
                    const bk = String(r?.bookingKey || '').trim();
                    const ib = String(r?.internalBookingId || '').trim();
                    const on = String(r?.orderNumber || r?.bookingNumber || '').trim();
                    if (!ib && !bk) return null;
                    return {
                      target: { bookingKey: bk, orderNumber: on, internalBookingId: ib },
                      context: { kind: 'import', title: 'Created', row: r, bookingKey: bk },
                    };
                  })
                  .filter(Boolean);
                openCompareQueue(items);
              }}
              sx={{
                py: 1.25,
                fontWeight: 900,
                letterSpacing: 0.2,
                textTransform: 'none',
              }}
            >
              {`Alle erfolgreichen Side-by-side ansehen (${(() => {
                if (mode === 'checks') {
                  if (isChecksRunning || isDryRunning || dryRunInProgress || dryRunActuallyRunning) return 0;
                  const prepared = Array.isArray(preparedSuccessRows) ? preparedSuccessRows : [];
                  if (prepared.length > 0) {
                    const badKeys = new Set(
                      [
                        ...(Array.isArray(dryRunReport?.samples?.lineItemsCheckErrors) ? dryRunReport.samples.lineItemsCheckErrors : []),
                        ...(Array.isArray(dryRunReport?.samples?.regiondoTotalCheckErrors) ? dryRunReport.samples.regiondoTotalCheckErrors : []),
                        ...(Array.isArray(dryRunReport?.samples?.lineItemsMismatch) ? dryRunReport.samples.lineItemsMismatch : []),
                        ...(Array.isArray(dryRunReport?.samples?.regiondoTotalsUnclearNotImportable) ? dryRunReport.samples.regiondoTotalsUnclearNotImportable : []),
                      ]
                        .map(r => String(r?.bookingKey || '').trim())
                        .filter(Boolean)
                    );
                    if (badKeys.size === 0) return prepared.length;
                    return prepared.filter(r => {
                      const bk = String(r?.bookingKey || '').trim();
                      return bk && !badKeys.has(bk);
                    }).length;
                  }
                  const list = Array.isArray(dryRunReport?.samples?.successChecked)
                    ? dryRunReport.samples.successChecked
                    : Array.isArray(dryRunReport?.samples?.success)
                      ? dryRunReport.samples.success
                      : [];
                  const badKeys = new Set(
                    [
                      ...(Array.isArray(dryRunReport?.samples?.lineItemsCheckErrors) ? dryRunReport.samples.lineItemsCheckErrors : []),
                      ...(Array.isArray(dryRunReport?.samples?.regiondoTotalCheckErrors) ? dryRunReport.samples.regiondoTotalCheckErrors : []),
                      ...(Array.isArray(dryRunReport?.samples?.lineItemsMismatch) ? dryRunReport.samples.lineItemsMismatch : []),
                      ...(Array.isArray(dryRunReport?.samples?.regiondoTotalsUnclearNotImportable) ? dryRunReport.samples.regiondoTotalsUnclearNotImportable : []),
                    ]
                      .map(r => String(r?.bookingKey || '').trim())
                      .filter(Boolean)
                  );
                  if (badKeys.size === 0) return list.length;
                  return list.filter(r => {
                    const bk = String(r?.bookingKey || '').trim();
                    return bk && !badKeys.has(bk);
                  }).length;
                }
                const sample = Array.isArray(importReport?.created) ? importReport.created.length : 0;
                const total = Number(importReport?.createdCount);
                if (Number.isFinite(total) && total > 0) {
                  if (sample > 0 && sample < total) return `${sample}/${total}`;
                  return total;
                }
                return sample || 0;
              })()})`}
            </Button>
          </Box>

          {mode === 'checks' ? (
            <Box sx={{ mt: 1 }}>
              <Button
                variant='outlined'
                color='inherit'
                fullWidth
                disabled={(() => {
                  if (!sessionId) return true;
                  if (isChecksRunning || isDryRunning || dryRunInProgress || dryRunActuallyRunning) return true;
                  const list = Array.isArray(dryRunReport?.samples?.bookingsWithComment)
                    ? dryRunReport.samples.bookingsWithComment
                    : [];
                  const n = typeof dryRunReport?.counters?.bookingsWithComment === 'number'
                    ? Number(dryRunReport.counters.bookingsWithComment)
                    : list.length;
                  return !(Number.isFinite(n) && n > 0);
                })()}
                onClick={() => {
                  if (isChecksRunning || isDryRunning || dryRunInProgress || dryRunActuallyRunning) return;
                  const list = Array.isArray(dryRunReport?.samples?.bookingsWithComment)
                    ? dryRunReport.samples.bookingsWithComment
                    : [];
                  const items = list
                    .map(r => {
                      const bk = String(r?.bookingKey || '').trim();
                      const on = String(r?.orderNumber || '').trim();
                      if (!bk) return null;
                      return {
                        target: { bookingKey: bk, orderNumber: on || null },
                        context: { kind: 'dryRun', detailsKey: 'bookingsWithComment', row: r },
                      };
                    })
                    .filter(Boolean);
                  openCompareQueue(items);
                }}
                sx={{ py: 1.1, fontWeight: 800, textTransform: 'none' }}
              >
                {`Alle mit Kommentar ansehen (${(() => {
                  const list = Array.isArray(dryRunReport?.samples?.bookingsWithComment)
                    ? dryRunReport.samples.bookingsWithComment
                    : [];
                  const nRaw = dryRunReport?.counters?.bookingsWithComment;
                  const n = typeof nRaw === 'number' ? Number(nRaw) : list.length;
                  return Number.isFinite(n) ? n : list.length;
                })()})`}
              </Button>
            </Box>
          ) : null}

          {mode === 'checks' && isChecksRunning && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  Dry-Run Fortschritt
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {checksDetails ? `${checksDetails} • ` : ''}
                  {Math.round(checksProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={checksProgress}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  backgroundColor: rmSurface.s08,
                  '& .MuiLinearProgress-bar': { borderRadius: 999 },
                }}
              />
            </Box>
          )}

          {mode === 'import' && isImportRunning && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  Import Fortschritt
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {importDetailsFromProgressInfo ? `${importDetailsFromProgressInfo} • ` : ''}
                  {Math.round(overallProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={overallProgress}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  backgroundColor: rmSurface.s08,
                  '& .MuiLinearProgress-bar': { borderRadius: 999 },
                }}
              />
            </Box>
          )}

          {mode === 'import' && !dryRunReport ? (
            <Alert severity='warning' sx={{ mt: 2 }}>
              Kein Dry-Run Report vorhanden. Normalerweise sollte Step 3 (Dry-Run) vorher laufen, damit du die Mismatches siehst.
            </Alert>
          ) : null}

          {mode === 'checks' && counters && !isChecksRunning && !isDryRunning && !dryRunInProgress && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <CheckRow
                    label='Mapping ok (Basis)'
                    value={`${counters.usable}/${counters.total}`}
                    good={ok === true}
                    blocksImport
                    detailsKey='success'
                    hasDetails={
                      (Array.isArray(preparedSuccessRows) && preparedSuccessRows.length > 0) ||
                      (Array.isArray(dryRunReport?.samples?.success) && dryRunReport.samples.success.length > 0)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CheckRow
                    label='Importierbar (voll geprüft)'
                    value={`${(() => {
                      const prepared = Array.isArray(preparedSuccessRows) ? preparedSuccessRows : [];
                      return prepared.length > 0 ? prepared.length : importableFullyCheckedCount;
                    })()}/${counters.total}${
                      (() => {
                        const parts = [];
                        if (baseNotUsableCount != null && baseNotUsableCount > 0) parts.push(`${baseNotUsableCount} nicht importierbar`);
                        if (notFullyCheckedUsableCount != null && notFullyCheckedUsableCount > 0)
                          parts.push(`${notFullyCheckedUsableCount} nicht voll geprüft`);
                        return parts.length > 0 ? ` (${parts.join(', ')})` : '';
                      })()
                    }`}
                    good={(() => {
                      const prepared = Array.isArray(preparedSuccessRows) ? preparedSuccessRows : [];
                      if (prepared.length > 0) {
                        return typeof counters?.total === 'number' && prepared.length === counters.total;
                      }
                      const list = Array.isArray(dryRunReport?.samples?.successChecked) ? dryRunReport.samples.successChecked : [];
                      if (!Array.isArray(dryRunReport?.samples?.successChecked)) return false;
                      return typeof counters?.total === 'number' && list.length === counters.total;
                    })()}
                    blocksImport
                    detailsKey={Array.isArray(preparedSuccessRows) && preparedSuccessRows.length > 0 ? 'successChecked' : Array.isArray(dryRunReport?.samples?.successChecked) ? 'successChecked' : 'success'}
                    hasDetails={(() => {
                      const prepared = Array.isArray(preparedSuccessRows) ? preparedSuccessRows : [];
                      if (prepared.length > 0) return true;
                      const key = Array.isArray(dryRunReport?.samples?.successChecked) ? 'successChecked' : 'success';
                      const list = Array.isArray(dryRunReport?.samples?.[key]) ? dryRunReport.samples[key] : [];
                      return list.length > 0;
                    })()}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CheckRow
                    label='Ausgeschlossen (vom Import)'
                    value={`${excludedInRunCount}/${importableBaseListForExclude.length}`}
                    good={excludedInRunCount === 0}
                    detailsKey='excludedByUser'
                    hasDetails={excludedInRunCount > 0}
                  />
                </Grid>
                {typeof counters?.regiondoTotalsUnclearNotImportable === 'number' && counters.regiondoTotalsUnclearNotImportable > 0 ? (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Nicht importierbar: Regiondo Totals ungeklärt'
                      value={counters.regiondoTotalsUnclearNotImportable}
                      good={counters.regiondoTotalsUnclearNotImportable === 0}
                      blocksImport
                      detailsKey='regiondoTotalsUnclearNotImportable'
                      hasDetails={Array.isArray(dryRunReport?.samples?.regiondoTotalsUnclearNotImportable) && dryRunReport.samples.regiondoTotalsUnclearNotImportable.length > 0}
                    />
                  </Grid>
                ) : null}
                {notFullyCheckedUsableCount != null && notFullyCheckedUsableCount > 0 ? (
                  <>
                    <Grid item xs={12}>
                      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.25 }}>
                        Quellen für „nicht voll geprüft“
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CheckRow
                        label='Nicht voll geprüft: Cart/LineItems Check Fehler'
                        value={notFullyCheckedBreakdown?.sources?.lineItemsCheckErrors || 0}
                        good={(notFullyCheckedBreakdown?.sources?.lineItemsCheckErrors || 0) === 0}
                        blocksImport
                        detailsKey='lineItemsCheckErrors'
                        hasDetails={(notFullyCheckedBreakdown?.sources?.lineItemsCheckErrors || 0) > 0}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CheckRow
                        label='Nicht voll geprüft: Regiondo Total Check Fehler'
                        value={notFullyCheckedBreakdown?.sources?.regiondoTotalCheckErrors || 0}
                        good={(notFullyCheckedBreakdown?.sources?.regiondoTotalCheckErrors || 0) === 0}
                        blocksImport
                        detailsKey='regiondoTotalCheckErrors'
                        hasDetails={(notFullyCheckedBreakdown?.sources?.regiondoTotalCheckErrors || 0) > 0}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CheckRow
                        label='Nicht voll geprüft: LineItems Mismatch'
                        value={notFullyCheckedBreakdown?.sources?.lineItemsMismatch || 0}
                        good={(notFullyCheckedBreakdown?.sources?.lineItemsMismatch || 0) === 0}
                        blocksImport
                        detailsKey='lineItemsMismatch'
                        hasDetails={(notFullyCheckedBreakdown?.sources?.lineItemsMismatch || 0) > 0}
                      />
                    </Grid>
                  </>
                ) : null}

                {(() => {
                  const list = Array.isArray(dryRunReport?.samples?.discountCodesRounding)
                    ? dryRunReport.samples.discountCodesRounding
                    : [];
                  const count = list.length;
                  if (!(count > 0)) return null;
                  return (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Rabattcode: Abweichung < 1€ (ok)'
                      value={count}
                      good
                      detailsKey='discountCodesRounding'
                      hasDetails={count > 0}
                    />
                  </Grid>
                  );
                })()}
                {(() => {
                  const list = Array.isArray(dryRunReport?.samples?.discountCodesMismatch)
                    ? dryRunReport.samples.discountCodesMismatch
                    : [];
                  const count = list.length;
                  if (!(count > 0)) return null;
                  return (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Rabattcode: Abweichung > 1€'
                      value={count}
                      good={count === 0}
                      blocksImport
                      detailsKey='discountCodesMismatch'
                      hasDetails={count > 0}
                    />
                  </Grid>
                  );
                })()}
                <Grid item xs={12} md={6}>
                  <CheckRow
                    label='Fehlende Produkt-Zuordnungen'
                    value={counters.missingProductMapping}
                    good={counters.missingProductMapping === 0}
                    blocksImport
                    detailsKey='missingProductMapping'
                    hasDetails={(counters.missingProductMapping || 0) > 0}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CheckRow
                    label='Fehlende Pricing-Mappings'
                    value={counters.missingPricing}
                    good={counters.missingPricing === 0}
                    blocksImport
                    detailsKey='missingPricing'
                    hasDetails={(counters.missingPricing || 0) > 0}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CheckRow
                    label='Fehlende Duration-Mappings'
                    value={counters.missingDuration}
                    good={counters.missingDuration === 0}
                    blocksImport
                    detailsKey='missingDuration'
                    hasDetails={(counters.missingDuration || 0) > 0}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CheckRow
                    label='Fehlende Kategorie-Mappings'
                    value={counters.missingCategory}
                    good={counters.missingCategory === 0}
                    blocksImport
                    detailsKey='missingCategory'
                    hasDetails={(counters.missingCategory || 0) > 0}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CheckRow
                    label='Fehlende Adressdaten (Regiondo)'
                    value={counters.missingAddress}
                    good={counters.missingAddress === 0}
                    detailsKey='missingAddress'
                    hasDetails={(counters.missingAddress || 0) > 0}
                  />
                </Grid>
                {typeof counters.missingPackageMapping === 'number' && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Fehlende Paket-Mappings'
                      value={counters.missingPackageMapping}
                      good={counters.missingPackageMapping === 0}
                      detailsKey='missingPackageMapping'
                      hasDetails={(counters.missingPackageMapping || 0) > 0}
                    />
                  </Grid>
                )}
                {typeof counters.missingUpsellMapping === 'number' && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Fehlende Upsell-Mappings'
                      value={counters.missingUpsellMapping}
                      good={counters.missingUpsellMapping === 0}
                      detailsKey='missingUpsellMapping'
                      hasDetails={(counters.missingUpsellMapping || 0) > 0}
                    />
                  </Grid>
                )}
                {typeof counters.lineItemsCheckErrors === 'number' && counters.lineItemsCheckErrors > 0 && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Cart/LineItems nicht prüfbar'
                      value={counters.lineItemsCheckErrors}
                      good={counters.lineItemsCheckErrors === 0}
                      blocksImport
                      detailsKey='lineItemsCheckErrors'
                      hasDetails={counters.lineItemsCheckErrors > 0}
                    />
                  </Grid>
                )}
                
                {typeof counters.regiondoTotalCheckErrors === 'number' && counters.regiondoTotalCheckErrors > 0 && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Regiondo Total Check nicht prüfbar'
                      value={counters.regiondoTotalCheckErrors}
                      good={counters.regiondoTotalCheckErrors === 0}
                      blocksImport
                      detailsKey='regiondoTotalCheckErrors'
                      hasDetails={counters.regiondoTotalCheckErrors > 0}
                    />
                  </Grid>
                )}
                {typeof counters.regiondoTotalMismatchButCheckoutOk === 'number' && counters.regiondoTotalMismatchButCheckoutOk > 0 && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Regiondo Total Mismatch (Checkout ok)'
                      value={counters.regiondoTotalMismatchButCheckoutOk}
                      good
                      detailsKey='regiondoTotalMismatchButCheckoutOk'
                      hasDetails={counters.regiondoTotalMismatchButCheckoutOk > 0}
                    />
                  </Grid>
                )}
                {typeof counters.checkoutTotalCheckErrors === 'number' && counters.checkoutTotalCheckErrors > 0 && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Discount/Checkout Check fehlgeschlagen'
                      value={counters.checkoutTotalCheckErrors}
                      good={counters.checkoutTotalCheckErrors === 0}
                      detailsKey='checkoutTotalCheckErrors'
                      hasDetails={counters.checkoutTotalCheckErrors > 0}
                    />
                  </Grid>
                )}
                {typeof counters.giftcertApplied === 'number' && counters.giftcertApplied > 0 && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Gutschein angewendet'
                      value={counters.giftcertApplied}
                      good
                      detailsKey='giftcertApplied'
                      hasDetails={counters.giftcertApplied > 0}
                    />
                  </Grid>
                )}
                {typeof counters.giftcertRoundingOk === 'number' && counters.giftcertRoundingOk > 0 && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Gutschein: Abweichung < 1€ (ok)'
                      value={counters.giftcertRoundingOk}
                      good
                      detailsKey='giftcertRoundingOk'
                      hasDetails={counters.giftcertRoundingOk > 0}
                    />
                  </Grid>
                )}
                {typeof counters.regiondoAdjustmentsOk === 'number' && counters.regiondoAdjustmentsOk > 0 && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Gutschein (ok)'
                      value={counters.regiondoAdjustmentsOk}
                      good
                      detailsKey='regiondoAdjustmentsOk'
                      hasDetails={counters.regiondoAdjustmentsOk > 0}
                    />
                  </Grid>
                )}
                
                {typeof counters.regiondoPosScrapeOk === 'number' && counters.regiondoPosScrapeOk > 0 && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='B2B-Rabatt (Scrape ok)'
                      value={counters.regiondoPosScrapeOk}
                      good
                      detailsKey='regiondoPosScrapeOk'
                      hasDetails={counters.regiondoPosScrapeOk > 0}
                    />
                  </Grid>
                )}
                {typeof counters.regiondoBookingScrapeErrors === 'number' && counters.regiondoBookingScrapeErrors > 0 && (
                  <Grid item xs={12} md={6}>
                    <CheckRow
                      label='Scraping-Fehler (BookingView)'
                      value={counters.regiondoBookingScrapeErrors}
                      good={counters.regiondoBookingScrapeErrors === 0}
                      detailsKey='regiondoBookingScrapeErrors'
                      hasDetails={counters.regiondoBookingScrapeErrors > 0}
                    />
                  </Grid>
                )}
                
              </Grid>
            </>
          )}

          {mode === 'import' && importReport && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={1.5} alignItems='stretch'>
                <Grid item xs={12} md={2}>
                  <StatCard
                    title='Created'
                    value={importReport.createdCount ?? '—'}
                    subtitle='Import'
                    color='success'
                    isDone
                    showDoneIcon
                    donePulseKey={importDonePulse}
                    onClick={() => {
                      const sample = Array.isArray(importReport?.created) ? importReport.created.length : 0;
                      const total = Number(importReport?.createdCount);
                      if (Number.isFinite(total) && total > 0 && sample > 0 && sample < total) {
                        toast.info(`Im Report sind nur ${sample}/${total} Beispiele gespeichert.`);
                      }
                      openImportCompareQueue({ list: importReport?.created, title: 'Created' });
                    }}
                    action={
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => {
                          setImportDetailsTab(0);
                          setImportDetailsOpen(true);
                        }}
                      >
                        Details
                      </Button>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <StatCard
                    title='Skipped'
                    value={importReport.skippedCount ?? '—'}
                    subtitle='Import'
                    color='info'
                    isDone
                    showDoneIcon
                    donePulseKey={importDonePulse}
                    onClick={() => {
                      openImportCompareQueue({ list: importReport?.skippedSamples, title: 'Skipped' });
                    }}
                    action={
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => {
                          setImportDetailsTab(1);
                          setImportDetailsOpen(true);
                        }}
                      >
                        Details
                      </Button>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <StatCard
                    title='Failed'
                    value={importReport.failedCount ?? '—'}
                    subtitle='Import'
                    color={Number(importReport.failedCount || 0) > 0 ? 'warning' : 'success'}
                    isDone
                    showDoneIcon
                    donePulseKey={importDonePulse}
                    onClick={() => {
                      openImportCompareQueue({ list: importReport?.failedSamples, title: 'Failed' });
                    }}
                    action={
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => {
                          setImportDetailsTab(2);
                          setImportDetailsOpen(true);
                        }}
                      >
                        Details
                      </Button>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <StatCard
                    title='Zahlungsart: Online'
                    value={paymentMethodCounts.online ?? '—'}
                    subtitle='Regiondo'
                    color='info'
                    isDone
                    showDoneIcon
                    donePulseKey={importDonePulse}
                    onClick={() => openImportPaymentQueue('online')}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <StatCard
                    title='Zahlungsart: Offline'
                    value={paymentMethodCounts.offline ?? '—'}
                    subtitle='Regiondo'
                    color='info'
                    isDone
                    showDoneIcon
                    donePulseKey={importDonePulse}
                    onClick={() => openImportPaymentQueue('offline')}
                  />
                </Grid>
                {paymentMethodCounts.unknown > 0 ? (
                  <Grid item xs={12} md={2}>
                    <StatCard
                      title='Zahlungsart: Unbekannt'
                      value={paymentMethodCounts.unknown}
                      subtitle='Regiondo'
                      color='warning'
                      isDone
                      showDoneIcon
                      donePulseKey={importDonePulse}
                      onClick={() => openImportPaymentQueue('unknown')}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant='outlined'
                    color='success'
                    onClick={() => {
                      if (!selectedEntertainmentId) return;
                      setFinalizeDialogMode('complete');
                      setFinalizeDialogOpen(true);
                    }}
                    disabled={!selectedEntertainmentId || isFinalizingEntertainment}
                    sx={{ height: '100%' }}
                  >
                    Entertainment final migriert
                  </Button>
                </Grid>
              </Grid>

              {Array.isArray(importReport?.convertedFutureOnlinePaymentsToOfflineOrderNumbers) &&
              importReport.convertedFutureOnlinePaymentsToOfflineOrderNumbers.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Box>
                      <Typography variant='subtitle2' sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                        Regiondo Buchungsnummern (manuell stornieren)
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Diese Buchungen wurden beim Import von{' '}
                        <Box component='span' sx={{ fontWeight: 600 }}>
                          online bezahlt
                        </Box>{' '}
                        zu{' '}
                        <Box component='span' sx={{ fontWeight: 600 }}>
                          offline
                        </Box>{' '}
                        umgestellt, weil sie mehr als 2 Monate in der Zukunft liegen.
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {importReport?.convertedFutureOnlinePaymentsToOfflineOrderNumbersTruncated === true ? (
                        <Chip size='small' color='warning' label='Liste evtl. gekürzt' />
                      ) : null}
                      <Button
                        variant='contained'
                        size='small'
                        onClick={() =>
                          copyToClipboard(
                            (importReport?.convertedFutureOnlinePaymentsToOfflineOrderNumbers || []).join('\n')
                          )
                        }
                      >
                        Kopieren
                      </Button>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: rmSurface.b10,
                      backgroundColor: rmSurface.s02,
                      p: 1.25,
                      maxHeight: 220,
                      overflow: 'auto',
                      whiteSpace: 'pre',
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: 12,
                      lineHeight: 1.55,
                    }}
                  >
                    {(importReport?.convertedFutureOnlinePaymentsToOfflineOrderNumbers || []).join('\n')}
                  </Box>
                </Box>
              ) : null}
            </>
          )}

          {mode === 'import' && !importReport && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant='outlined'
                  color='success'
                  onClick={() => {
                    if (!selectedEntertainmentId) return;
                    setFinalizeDialogMode(isSelectedEntertainmentCompleted ? 'undo' : 'complete');
                    setFinalizeDialogOpen(true);
                  }}
                  disabled={!sessionId || !selectedEntertainmentId || isFinalizingEntertainment}
                >
                  {isFinalizingEntertainment
                    ? 'Speichere…'
                    : isSelectedEntertainmentCompleted
                      ? 'Final-Migration zurücksetzen'
                      : 'Entertainment final migriert'}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    );
  }, [
    sessionId,
    stepKey,
    startDryRun,
    startImport,
    isDryRunning,
    isImporting,
    dryRunInProgress,
    importInProgress,
    dryRunReport,
    preparedSuccessRows,
    importReport,
    progressInfo,
    status?.phase,
    status?.progress,
    status?.details,
    importDonePulse,
    selectedEntertainmentId,
    selectedBranchId,
    isFinalizingEntertainment,
    isSelectedEntertainmentCompleted,
    showCompleted,
    auth,
    sendRequest,
    getBranches,
    getAllEntertainmentsByBranch,
  ]);

  return (
    <Box sx={{ ...viewRootTextPrimarySx, maxWidth: 1400, mx: 'auto' }}>
      <Box ref={compareRef} sx={{ mb: 2 }}>
        <Typography variant='h4' sx={{ fontWeight: 800, letterSpacing: -0.6 }}>
          Regiondo → InternalBooking Migration
        </Typography>
      </Box>

      {!auth?.checkRightAny?.('internalBooking:migration:regiondo') && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Dir fehlt das Recht{' '}
          <Box component='span' sx={{ fontWeight: 600 }}>
            internalBooking:migration:regiondo
          </Box>
          . Bitte Rolle anpassen.
        </Alert>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          bgcolor: rmSurface.stickyStepperBg,
          backdropFilter: 'blur(10px)',
          borderBottom: rmSurface.borderBottom08,
          py: 1,
          mb: 2,
        }}
      >
        <Stepper
          nonLinear
          activeStep={activeStep}
          alternativeLabel
          sx={{
            '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
            '& .MuiStepIcon-root.Mui-completed': { color: 'primary.main' },
            '& .MuiStepLabel-label.Mui-active': { color: 'primary.main', fontWeight: 700 },
            '& .MuiStepLabel-label.Mui-completed': { color: 'primary.main', fontWeight: 700 },
            '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: 'primary.main' },
            '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: 'primary.main' },
          }}
        >
          {steps.map((s, idx) => (
            <Step key={s.key} completed={idx < activeStep}>
              <StepButton
                onClick={() => {
                  if (!canEnterStep(idx)) {
                    const msg =
                      idx === 1
                        ? 'Bitte zuerst eine Session starten (Step 1: Vergleich).'
                        : idx === 2
                          ? 'Bitte zuerst Matching speichern.'
                          : idx === 3
                            ? 'Bitte zuerst den Dry-Run laufen lassen.'
                            : 'Dieser Schritt ist noch nicht verfügbar.';
                    toast.info(msg);
                    return;
                  }
                  userStepOverrideRef.current = true;
                  setActiveStep(idx);
                }}
              >
                <StepLabel>{s.title}</StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 1, px: 1 }}>
          <Button variant='outlined' size='small' onClick={goPrev} disabled={activeStep === 0}>
            Zurück
          </Button>
          <Button
            variant='contained'
            size='small'
            onClick={goNext}
            disabled={activeStep >= steps.length - 1 || !canGoNext}
          >
            Weiter
          </Button>
        </Box>
      </Box>

      {stepKey === 'compare' ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
        {stepKey === 'compare' && (
          <Grid item xs={12} md={4}>
            <Card variant='outlined'>
              <CardContent>
                <Typography variant='overline' color='text.secondary'>
                  Auswahl
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ '& .MuiFormControlLabel-label': { opacity: 0.9 } }}
                      control={
                        <Checkbox
                          checked={showCompleted}
                          onChange={e => setShowCompleted(e.target.checked)}
                          size='small'
                        />
                      }
                      label='Auch abgeschlossene anzeigen'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='caption' color='text.secondary'>
                      Filiale
                    </Typography>
                    <FormControl fullWidth size='small' sx={{ mt: 0.75 }}>
                      <InputLabel id='fz-branch-label'>Filiale</InputLabel>
                      <Select
                        labelId='fz-branch-label'
                        label='Filiale'
                        value={selectedBranchId}
                        onChange={e => setSelectedBranchId(e.target.value)}
                        MenuProps={selectMenuProps}
                        sx={{
                          bgcolor: rmSurface.selectBg,
                          '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                        }}
                      >
                        {branches.map(b => (
                          <MenuItem key={b._id} value={b._id}>
                            {b.longName || b.locationName || b._id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='caption' color='text.secondary'>
                      Entertainment
                    </Typography>
                    <FormControl fullWidth size='small' sx={{ mt: 0.75 }}>
                      <InputLabel id='fz-ent-label'>Entertainment</InputLabel>
                      <Select
                        labelId='fz-ent-label'
                        label='Entertainment'
                        value={selectedEntertainmentId}
                        onChange={e => setSelectedEntertainmentId(e.target.value)}
                        MenuProps={selectMenuProps}
                        sx={{
                          bgcolor: rmSurface.selectBg,
                          '& .MuiSelect-icon': { color: rmSurface.selectIcon },
                        }}
                      >
                        {entertainments.map(e => (
                          <MenuItem key={e._id} value={e._id}>
                            {e.longName || e.entertainmentName || e._id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='caption' color='text.secondary'>
                      Von
                    </Typography>
                    <TextField
                      type='date'
                      value={dateFrom}
                      onChange={e => setDateFrom(e.target.value)}
                      size='small'
                      fullWidth
                      sx={{ mt: 0.75 }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ 'aria-label': 'Von' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='caption' color='text.secondary'>
                      Bis
                    </Typography>
                    <TextField
                      type='date'
                      value={dateTo}
                      onChange={e => setDateTo(e.target.value)}
                      size='small'
                      fullWidth
                      sx={{ mt: 0.75 }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ 'aria-label': 'Bis' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ mt: 0.5, '& .MuiFormControlLabel-label': { opacity: 0.9 } }}
                      control={
                        <Checkbox
                          checked={includeCanceled}
                          onChange={e => setIncludeCanceled(e.target.checked)}
                          size='small'
                        />
                      }
                      label='Stornierte Buchungen berücksichtigen'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ '& .MuiFormControlLabel-label': { opacity: 0.9 } }}
                      control={
                        <Checkbox
                          checked={includeMissingInDb}
                          onChange={e => setIncludeMissingInDb(e.target.checked)}
                          size='small'
                        />
                      }
                      label='Auch Buchungen importieren, die in der Legacy-DB fehlen'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ '& .MuiFormControlLabel-label': { opacity: 0.9 } }}
                      control={
                        <Checkbox
                          checked={convertFutureOnlinePaymentsToOffline}
                          onChange={async e => {
                            const next = Boolean(e.target.checked);
                            setConvertFutureOnlinePaymentsToOffline(next);
                            if (sessionId) {
                              try {
                                await persistSessionSettings(sessionId, { convertFutureOnlinePaymentsToOffline: next });
                                toast.success(
                                  next
                                    ? 'Zukünftige Online-Zahlungen werden zu Offline umgestellt'
                                    : 'Zukünftige Online-Zahlungen werden nicht umgestellt'
                                );
                              } catch (_) {}
                            }
                          }}
                          size='small'
                        />
                      }
                      label='Buchungen die mehr als zwei Monate in der Zukunft liegen in Offline-Zahlungen umwandeln (nur bei Online bezahlt)'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ '& .MuiFormControlLabel-label': { opacity: 0.9 } }}
                      control={
                        <Checkbox
                          checked={discountCodesApplyToUpgradesByDefault}
                          onChange={async e => {
                            const next = Boolean(e.target.checked);
                            setDiscountCodesApplyToUpgradesByDefault(next);
                            if (sessionId) {
                              try {
                                await persistSessionSettings(sessionId, { discountCodesApplyToUpgradesByDefault: next });
                                toast.success(
                                  next
                                    ? 'Rabattcodes werden standardmäßig auch auf Pakete/Upsells angewendet'
                                    : 'Rabattcodes werden standardmäßig nur auf Buchung angewendet'
                                );
                              } catch (_) {}
                            }
                          }}
                          size='small'
                        />
                      }
                      label='Rabattcodes auch auf Pakete/Upsells anwenden'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ '& .MuiFormControlLabel-label': { opacity: 0.9 } }}
                      control={
                        <Checkbox
                          checked={mergeFrankfurtGolfComboBookings}
                          onChange={async e => {
                            const next = Boolean(e.target.checked);
                            setMergeFrankfurtGolfComboBookings(next);
                            if (sessionId) {
                              try {
                                await persistSessionSettings(sessionId, { mergeFrankfurtGolfComboBookings: next });
                                toast.success(
                                  next
                                    ? 'Golf Kombi-Spezialbehandlung aktiviert'
                                    : 'Golf Kombi-Spezialbehandlung deaktiviert'
                                );
                              } catch (_) {}
                            }
                          }}
                          size='small'
                        />
                      }
                      label='Golf Kombis zu einer Combo-Buchung zusammenführen'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ '& .MuiFormControlLabel-label': { opacity: 0.9 } }}
                      control={
                        <Checkbox
                          checked={sendBookingReviewMail}
                          onChange={async e => {
                            const next = Boolean(e.target.checked);
                            setSendBookingReviewMail(next);
                            if (sessionId) {
                              try {
                                await persistSessionSettings(sessionId, { sendBookingReviewMail: next });
                                toast.success(
                                  next
                                    ? 'Review-Mail nach Import aktiviert'
                                    : 'Review-Mail nach Import deaktiviert'
                                );
                              } catch (_) {}
                            }
                          }}
                          size='small'
                        />
                      }
                      label='Nach dem Import automatisch die Review-Mail an den Kunden senden'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ '& .MuiFormControlLabel-label': { opacity: 0.9 } }}
                      control={
                        <Checkbox
                          checked={repairMode}
                          onChange={async e => {
                            const next = Boolean(e.target.checked);
                            setRepairMode(next);
                            if (sessionId) {
                              try {
                                await persistSessionSettings(sessionId, { repairMode: next });
                                toast.success(next ? 'Reparaturmodus aktiviert' : 'Reparaturmodus deaktiviert');
                              } catch (_) {}
                            }
                          }}
                          size='small'
                        />
                      }
                      label='Reparaturmodus (bereits importierte Regiondo-Buchungen ersetzen)'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant='contained' fullWidth disabled={!canStart || isStarting} onClick={start}>
                      {isStarting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} />
                          <span>Starte…</span>
                        </Box>
                      ) : (
                        'Migration starten'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={stepKey === 'compare' ? 8 : 12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <StatCard
                title='Regiondo (Future)'
                value={stats.regiondoCount}
                subtitle='API'
                color='primary'
                isDone={status.phase !== 'idle' && status.progress >= 25}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                title='Legacy DB (Future)'
                value={stats.dbCount}
                subtitle='Booking'
                color='secondary'
                isDone={status.phase !== 'idle' && status.progress >= 25}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                title='Adress-Coverage'
                value={`${stats.addressCoveragePct}%`}
                subtitle='buyer_data'
                color='success'
                isDone={status.phase !== 'idle' && status.progress >= 25}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StatCard
                title='Fehlt in DB'
                value={stats.missingInDb}
                subtitle='Regiondo → DB'
                color={stats.missingInDb > 0 ? 'warning' : 'success'}
                isDone={false}
                action={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() => openUnmatchedModal(0)}
                      disabled={!sessionId || stats.missingInDb <= 0}
                    >
                      Anzeigen
                    </Button>
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={syncMissingLegacy}
                      disabled={!sessionId || isLegacySyncing || stats.missingInDb <= 0}
                    >
                      {isLegacySyncing ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <span>Sync…</span>
                        </Box>
                      ) : (
                        'Fehlende API-Einträge holen'
                      )}
                    </Button>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StatCard
                title='Nur in DB'
                value={stats.extraInDb}
                subtitle='DB → Regiondo'
                color={stats.extraInDb > 0 ? 'warning' : 'success'}
                isDone={status.phase !== 'idle' && status.progress >= 25}
                action={
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => openUnmatchedModal(1)}
                    disabled={!sessionId || stats.extraInDb <= 0}
                  >
                    Anzeigen
                  </Button>
                }
              />
            </Grid>
          </Grid>
        </Grid>
        </Grid>
      ) : null}

      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={8}>
              <Typography variant='h6' sx={{ fontWeight: 800, mb: 0.5 }}>
                {status.title}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {status.details}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Chip
                  label={isConnected ? 'Live verbunden' : 'Offline'}
                  color={isConnected ? 'success' : 'default'}
                  variant={isConnected ? 'filled' : 'outlined'}
                />
                <Chip
                  label={`Progress ${(() => {
                    if (stepKey === 'checks' && progressInfo && String(progressInfo?.kind || '').trim() === 'dryRun') {
                      const processed = Number(progressInfo?.processed);
                      const total2 = Number(progressInfo?.total);
                      if (Number.isFinite(processed) && Number.isFinite(total2) && total2 > 0) {
                        return Math.max(0, Math.min(100, Math.round((processed / total2) * 100)));
                      }
                    }
                    if (stepKey === 'checks' && String(status?.phase || '').trim() === 'checks' && Number(status?.progress || 0) < 75) {
                      return 0;
                    }
                    if (stepKey === 'import' && String(status?.phase || '').trim() === 'import') {
                      if (progressInfo && String(progressInfo?.kind || '').trim() === 'import') {
                        const processed = Number(progressInfo?.processed);
                        const total2 = Number(progressInfo?.total);
                        if (Number.isFinite(processed) && Number.isFinite(total2) && total2 > 0) {
                          return Math.max(0, Math.min(100, Math.round((processed / total2) * 100)));
                        }
                      }
                      return Math.round(Number(status?.progress || 0));
                    }
                    return Math.round(Number(status.progress || 0));
                  })()}%`}
                  color='info'
                  variant='outlined'
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {stepKey === 'match' ? MatchingSection : null}
      {stepKey === 'checks' || stepKey === 'import' ? ChecksAndImportSection : null}

      <Dialog open={importDetailsOpen} onClose={() => setImportDetailsOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Import Details</DialogTitle>
        <DialogContent dividers>
          {!importReport ? (
            <Typography variant='body2' color='text.secondary'>
              Keine Daten vorhanden.
            </Typography>
          ) : (
            <>
              <Tabs value={importDetailsTab} onChange={(_, v) => setImportDetailsTab(v)} sx={{ mb: 2 }}>
                <Tab label='Created' />
                <Tab label='Skipped' />
                <Tab label='Failed' />
                <Tab label='Mismatches' />
              </Tabs>

              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Summary
              </Typography>
              <Box sx={{ p: 1.25, borderRadius: 2, backgroundColor: rmSurface.finalizeHint, mb: 2 }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  created={importReport.createdCount ?? '—'}, skipped={importReport.skippedCount ?? '—'}, failed=
                  {importReport.failedCount ?? '—'}
                </Typography>
              </Box>

              {Array.isArray(importReport?.convertedFutureOnlinePaymentsToOfflineOrderNumbers) &&
              importReport.convertedFutureOnlinePaymentsToOfflineOrderNumbers.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Online → Offline (Zukunft {'>'} 2 Monate)
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Typography variant='body2' color='text.secondary'>
                      Betroffene Regiondo Buchungsnummern (zum manuellen Storno).
                    </Typography>
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() =>
                        copyToClipboard((importReport?.convertedFutureOnlinePaymentsToOfflineOrderNumbers || []).join('\n'))
                      }
                    >
                      Kopieren
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: rmSurface.b10,
                      overflow: 'auto',
                      maxHeight: 160,
                      backgroundColor: rmSurface.s02,
                      p: 1.25,
                      whiteSpace: 'pre',
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: 12,
                      lineHeight: 1.55,
                    }}
                  >
                    {(importReport?.convertedFutureOnlinePaymentsToOfflineOrderNumbers || []).join('\n')}
                  </Box>
                </Box>
              ) : null}

              {importDetailsTab === 0 && (
                <>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Created (Sample)
                  </Typography>
                  {Array.isArray(importReport.created) && importReport.created.length > 0 ? (
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: rmSurface.b10,
                        overflow: 'auto',
                        maxHeight: 420,
                        backgroundColor: rmSurface.s02,
                        p: 1.25,
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        fontSize: 12,
                        lineHeight: 1.6,
                      }}
                    >
                      {importReport.created.map((c, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            mb: 1,
                            pb: 1,
                            borderBottom:
                              idx === importReport.created.length - 1
                                ? 'none'
                                : rmSurface.borderBottom08,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Button
                              size='small'
                              variant='outlined'
                              disabled={!c.internalBookingId && !c.bookingKey && !c.orderNumber}
                              onClick={() =>
                                openCompare(
                                  {
                                    internalBookingId: c.internalBookingId || '',
                                    bookingKey: c.bookingKey || '',
                                    orderNumber: c.orderNumber || c.bookingNumber || '',
                                  },
                                  { kind: 'import', title: 'Created', row: c, bookingKey: c.bookingKey || '' }
                                )
                              }
                            >
                              Side-by-side
                            </Button>
                            {c.bookingKey ? (
                              <Button
                                size='small'
                                variant='outlined'
                                onClick={() => {
                                  const url = buildRegiondoExternalBookingUrl(c.bookingKey);
                                  if (url) window.open(url, '_blank');
                                }}
                              >
                                Regiondo öffnen
                              </Button>
                            ) : null}
                          </Box>
                          <div>
                            <span style={{ opacity: 0.7 }}>bookingKey:</span> {String(c.bookingKey || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>orderNumber:</span> {String(c.orderNumber || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>bookingNumber:</span> {String(c.bookingNumber || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>internalBookingId:</span> {String(c.internalBookingId || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>upsells:</span> {String(c.upsellCount ?? '—')}
                          </div>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Keine Created-Details vorhanden.
                    </Typography>
                  )}
                </>
              )}

              {importDetailsTab === 1 && (
                <>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Skipped (Sample)
                  </Typography>
                  {Array.isArray(importReport.skippedSamples) && importReport.skippedSamples.length > 0 ? (
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: rmSurface.b10,
                        overflow: 'auto',
                        maxHeight: 420,
                        backgroundColor: rmSurface.s02,
                        p: 1.25,
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        fontSize: 12,
                        lineHeight: 1.6,
                      }}
                    >
                      {importReport.skippedSamples.map((s, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            mb: 1,
                            pb: 1,
                            borderBottom:
                              idx === importReport.skippedSamples.length - 1
                                ? 'none'
                                : rmSurface.borderBottom08,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Button
                              size='small'
                              variant='outlined'
                              disabled={!s.bookingKey && !s.orderNumber}
                              onClick={() =>
                                openCompare(
                                  {
                                    bookingKey: s.bookingKey || '',
                                    orderNumber: s.orderNumber || '',
                                  },
                                  { kind: 'import', title: 'Skipped', row: s, bookingKey: s.bookingKey || '' }
                                )
                              }
                            >
                              Side-by-side
                            </Button>
                            {s.bookingKey ? (
                              <Button
                                size='small'
                                variant='outlined'
                                onClick={() => {
                                  const url = buildRegiondoExternalBookingUrl(s.bookingKey);
                                  if (url) window.open(url, '_blank');
                                }}
                              >
                                Regiondo öffnen
                              </Button>
                            ) : null}
                          </Box>
                          <div>
                            <span style={{ opacity: 0.7 }}>bookingKey:</span> {String(s.bookingKey || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>orderNumber:</span> {String(s.orderNumber || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>product:</span> {String(s.productName || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>reason:</span> {String(s.reason || '—')}
                          </div>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Keine Skipped-Details vorhanden (für alte Sessions erst nach erneutem Import verfügbar).
                    </Typography>
                  )}
                </>
              )}

              {importDetailsTab === 2 && (
                <>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Failed (Sample)
                  </Typography>
                  {Array.isArray(importReport.failedSamples) && importReport.failedSamples.length > 0 ? (
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: rmSurface.b10,
                        overflow: 'auto',
                        maxHeight: 420,
                        backgroundColor: rmSurface.s02,
                        p: 1.25,
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        fontSize: 12,
                        lineHeight: 1.6,
                      }}
                    >
                      {importReport.failedSamples.map((f, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            mb: 1,
                            pb: 1,
                            borderBottom:
                              idx === importReport.failedSamples.length - 1
                                ? 'none'
                                : rmSurface.borderBottom08,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Button
                              size='small'
                              variant='outlined'
                              disabled={!f.bookingKey && !f.orderNumber}
                              onClick={() =>
                                openCompare(
                                  {
                                    bookingKey: f.bookingKey || '',
                                    orderNumber: f.orderNumber || '',
                                  },
                                  { kind: 'import', title: 'Failed', row: f, bookingKey: f.bookingKey || '' }
                                )
                              }
                            >
                              Side-by-side
                            </Button>
                            {f.bookingKey ? (
                              <Button
                                size='small'
                                variant='outlined'
                                onClick={() => {
                                  const url = buildRegiondoExternalBookingUrl(f.bookingKey);
                                  if (url) window.open(url, '_blank');
                                }}
                              >
                                Regiondo öffnen
                              </Button>
                            ) : null}
                          </Box>
                          <div>
                            <span style={{ opacity: 0.7 }}>bookingKey:</span> {String(f.bookingKey || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>orderNumber:</span> {String(f.orderNumber || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>product:</span> {String(f.productName || '—')}
                          </div>
                          <div>
                            <span style={{ opacity: 0.7 }}>reason:</span> {String(f.reason || f.error || '—')}
                          </div>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Keine Failure-Details vorhanden.
                    </Typography>
                  )}
                </>
              )}

              <Divider sx={{ my: 2 }} />
              {importDetailsTab === 3 && (
                <>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Total mismatches (Sample)
                  </Typography>
                  {Array.isArray(importReport.totalMismatches) && importReport.totalMismatches.length > 0 ? (
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: rmSurface.b10,
                        overflow: 'auto',
                        maxHeight: 320,
                        backgroundColor: rmSurface.s02,
                        p: 1.25,
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        fontSize: 12,
                        lineHeight: 1.6,
                      }}
                    >
                      {importReport.totalMismatches.map((m, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            py: 0.5,
                            borderBottom:
                              idx === importReport.totalMismatches.length - 1
                                ? 'none'
                                : rmSurface.borderBottom08,
                          }}
                        >
                          <div>
                            {String(m.bookingKey || '—')} • {String(m.kind || '—')} • delta=
                            {String(m.delta ?? '—')}
                          </div>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size='small'
                              variant='outlined'
                              disabled={!m.bookingKey}
                              onClick={() =>
                                openCompare(
                                  { bookingKey: m.bookingKey || '' },
                                  { kind: 'import', title: `Mismatch (${String(m.kind || '—')})`, row: m, bookingKey: m.bookingKey || '' }
                                )
                              }
                            >
                              Side-by-side
                            </Button>
                            {m.bookingKey ? (
                              <Button
                                size='small'
                                variant='outlined'
                                onClick={() => {
                                  const url = buildRegiondoExternalBookingUrl(m.bookingKey);
                                  if (url) window.open(url, '_blank');
                                }}
                              >
                                Regiondo öffnen
                              </Button>
                            ) : null}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Keine Mismatches vorhanden.
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setImportDetailsOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dryRunDetailsOpen} onClose={() => setDryRunDetailsOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Dry-Run Details
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const samples = dryRunReport?.samples || {};
            const prepared = Array.isArray(preparedSuccessRows) ? preparedSuccessRows : [];
            const isImportableList = dryRunDetailsKey === 'success' || dryRunDetailsKey === 'successChecked';
            const isExcludedList = dryRunDetailsKey === 'excludedByUser';
            const importableBaseList =
              (prepared.length > 0
                ? prepared
                : Array.isArray(samples?.successChecked)
                  ? samples.successChecked
                  : Array.isArray(samples?.success)
                    ? samples.success
                    : []) || [];
            const list =
              isExcludedList
                ? importableBaseList.filter(r => {
                    const bk = String(r?.bookingKey || '').trim();
                    return bk && excludeBookingKeysSet.has(bk);
                  })
                : (dryRunDetailsKey === 'success' || dryRunDetailsKey === 'successChecked') && prepared.length > 0
                  ? prepared
                  : Array.isArray(samples?.[dryRunDetailsKey])
                    ? samples[dryRunDetailsKey]
                    : [];
            const counters = dryRunReport?.counters || {};
            const counterValueRaw = counters && typeof counters === 'object' ? counters?.[dryRunDetailsKey] : null;
            const counterValue =
              (dryRunDetailsKey === 'success' || dryRunDetailsKey === 'successChecked') && prepared.length > 0
                ? list.length
                : Number(counterValueRaw || 0);
            const titleMap = {
              success: 'Erfolgreich (Importierbar)',
              successChecked: 'Importierbar (voll geprüft)',
              excludedByUser: 'Ausgeschlossen (vom Import)',
              missingProductMapping: 'Fehlende Produkt-Zuordnungen',
              missingPricing: 'Fehlende Pricing-Mappings',
              missingDuration: 'Fehlende Duration-Mappings',
              missingCategory: 'Fehlende Kategorie-Mappings',
              missingAddress: 'Fehlende Adressdaten (Regiondo)',
              lineItemsMismatch: 'LineItems Summe != Total',
              lineItemsCheckErrors: 'Cart/LineItems nicht prüfbar',
              missingPackageMapping: 'Fehlende Paket-Mappings',
              missingUpsellMapping: 'Fehlende Upsell-Mappings',
              regiondoTotalMismatch: 'Regiondo Total != Cart Total',
              regiondoTotalCheckErrors: 'Regiondo Total Check nicht prüfbar',
              regiondoTotalMismatchButCheckoutOk: 'Regiondo Total Mismatch (Checkout ok)',
              checkoutTotalMismatch: 'Discount/Checkout Mismatch',
              checkoutTotalCheckErrors: 'Discount/Checkout Check fehlgeschlagen',
              regiondoAdjustmentsOk: 'Wertgutschein-Korrekturen (ok)',
              regiondoAdjustmentsUnresolved: 'Regiondo Ausgleich (ungeklärt)',
              regiondoTotalsUnclearNotImportable: 'Regiondo Totals ungeklärt (nicht importierbar)',
              regiondoPersonsAddedByScrape: 'Person hinzugefügt (Scrape ok)',
              regiondoPosScrapeOk: 'B2B-Rabatt (Scrape ok)',
              regiondoBookingScrapeErrors: 'Scraping-Fehler (BookingView)',
              giftcertRoundingOk: 'Gutschein: Abweichung < 1€ (ok)',
            };
            const title = titleMap[dryRunDetailsKey] || dryRunDetailsKey || '—';
            const keys = list.length > 0 ? Object.keys(list[0] || {}) : [];
            const listBookingKeys = isImportableList
              ? list
                  .map(r => String(r?.bookingKey || '').trim())
                  .filter(Boolean)
              : [];
            const excludedInListCount = isImportableList
              ? listBookingKeys.filter(bk => excludeBookingKeysSet.has(bk)).length
              : 0;

            return (
              <>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  {title}
                </Typography>
                {isImportableList && listBookingKeys.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                    <Chip
                      size='small'
                      color={excludedInListCount > 0 ? 'warning' : 'default'}
                      label={`${excludedInListCount} ausgeschlossen • ${Math.max(0, listBookingKeys.length - excludedInListCount)} aktiv`}
                    />
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() => {
                        for (const bk of listBookingKeys) {
                          setBookingExcluded(bk, true);
                        }
                      }}
                    >
                      Alle in Liste ausschließen
                    </Button>
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() => {
                        for (const bk of listBookingKeys) {
                          setBookingExcluded(bk, false);
                        }
                      }}
                      disabled={excludedInListCount === 0}
                    >
                      Alle in Liste einschließen
                    </Button>
                    <Button
                      size='small'
                      variant='text'
                      color='error'
                      onClick={() => setExcludeBookingKeys([])}
                      disabled={excludeBookingKeysSet.size === 0}
                    >
                      Ausschlüsse zurücksetzen
                    </Button>
                  </Box>
                ) : null}
                {list.length === 0 ? (
                  <Box>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: counterValue > 0 ? 1 : 0 }}>
                      Keine Details vorhanden.
                    </Typography>
                    {counterValue > 0 ? (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant='body2' color='text.secondary'>
                          Dieser Dry-Run Report enthält keine Sample-Liste für diese Kategorie (typisch bei älteren Runs). Bitte Dry-Run neu starten, dann sind die betroffenen Buchungen klickbar.
                        </Typography>
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => {
                            setDryRunDetailsOpen(false);
                            startDryRun();
                          }}
                          disabled={!sessionId || isDryRunning || dryRunInProgress}
                        >
                          Dry-Run neu starten
                        </Button>
                      </Box>
                    ) : null}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: rmSurface.b10,
                      overflow: 'auto',
                      maxHeight: 520,
                      backgroundColor: rmSurface.s02,
                      p: 1.25,
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: 12,
                      lineHeight: 1.6,
                    }}
                  >
                    {list.map((row, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          mb: 1,
                          pb: 1,
                          borderBottom: idx === list.length - 1 ? 'none' : rmSurface.borderBottom08,
                        }}
                      >
                        {(row?.bookingKey || row?.orderNumber || row?.internalBookingId) ? (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={() =>
                                openCompareQueueAt(
                                  list
                                    .map(r => {
                                      const bk = String(r?.bookingKey || '').trim();
                                      const on = String(r?.orderNumber || r?.bookingNumber || '').trim();
                                      const ib = String(r?.internalBookingId || '').trim();
                                      if (!bk && !ib) return null;
                                      return {
                                        target: { bookingKey: bk, orderNumber: on, internalBookingId: ib },
                                        context: { kind: 'dryRun', detailsKey: dryRunDetailsKey, row: r },
                                      };
                                    })
                                    .filter(Boolean),
                                  idx
                                )
                              }
                            >
                              Side-by-side
                            </Button>
                            {row?.bookingKey ? (
                              <Button
                                size='small'
                                variant='outlined'
                                onClick={() => {
                                  const url = buildRegiondoExternalBookingUrl(row.bookingKey);
                                  if (url) window.open(url, '_blank');
                                }}
                              >
                                Regiondo öffnen
                              </Button>
                            ) : null}
                          </Box>
                        ) : null}
                        {isImportableList && row?.bookingKey ? (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  size='small'
                                  checked={excludeBookingKeysSet.has(String(row.bookingKey).trim())}
                                  onChange={e => setBookingExcluded(String(row.bookingKey).trim(), e.target.checked)}
                                />
                              }
                              label='Vom Import ausschließen'
                            />
                          </Box>
                        ) : null}
                        {keys.map(k => (
                          <div key={k}>
                            <span style={{ opacity: 0.7 }}>{k}:</span>{' '}
                            <span>{row?.[k] != null ? String(row[k]) : '—'}</span>
                          </div>
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setDryRunDetailsOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={compareOpen}
        onClose={closeCompare}
        fullScreen
      >
        <DialogTitle sx={{ py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='h6' sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                Side-by-side
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {(() => {
                  const ctx = compareContext && typeof compareContext === 'object' ? compareContext : null;
                  const kind = String(ctx?.kind || '').trim();
                  const row = ctx?.row && typeof ctx.row === 'object' ? ctx.row : null;
                  if (!ctx) {
                    const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
                    const on = String(compareTarget?.orderNumber || compareTarget?.order_number || '').trim();
                    const parts = [];
                    if (bk) parts.push(`bookingKey ${bk}`);
                    if (on) parts.push(`order ${on}`);
                    return parts.length > 0 ? parts.join(' • ') : '—';
                  }

                  if (kind === 'dryRun') {
                    const detailsKey = String(ctx?.detailsKey || '').trim();
                    const titleMap = {
                      success: 'Erfolgreich (Importierbar)',
                      successChecked: 'Importierbar (voll geprüft)',
                      missingProductMapping: 'Fehlende Produkt-Zuordnung',
                      missingPricing: 'Fehlendes Pricing-Mapping',
                      missingDuration: 'Fehlendes Duration-Mapping',
                      missingCategory: 'Fehlendes Kategorie-Mapping',
                      missingAddress: 'Fehlende Adressdaten',
                      missingPackageMapping: 'Fehlendes Paket-Mapping',
                      missingUpsellMapping: 'Fehlendes Upsell-Mapping',
                      lineItemsMismatch: 'LineItems Summe != Total',
                      lineItemsCheckErrors: 'Cart/LineItems nicht prüfbar',
                      regiondoTotalMismatch: 'Regiondo Total != Cart Total',
                      regiondoTotalCheckErrors: 'Regiondo Total Check nicht prüfbar',
                      regiondoTotalMismatchButCheckoutOk: 'Regiondo Total Mismatch (Checkout ok)',
                      checkoutTotalMismatch: 'Discount/Checkout Mismatch',
                      checkoutTotalCheckErrors: 'Discount/Checkout Check fehlgeschlagen',
                      discountCodesUsed: 'Rabattcodes verwendet',
                      discountCodesRounding: 'Rabattcode: Abweichung < 1€ (ok)',
                      discountCodesMismatch: 'Rabattcode: Abweichung > 1€',
                      bookingsWithComment: 'Buchungen mit Kommentar',
                      giftcertApplied: 'Gutschein angewendet',
                      giftcertRoundingOk: 'Gutschein: Abweichung < 1€ (ok)',
                      regiondoAdjustmentsOk: 'Gutschein (ok)',
                      regiondoTotalsUnclearNotImportable: 'Regiondo Totals ungeklärt (nicht importierbar)',
                      regiondoPersonsAddedByScrape: 'Person hinzugefügt (Scrape ok)',
                      regiondoPosScrapeOk: 'B2B-Rabatt (Scrape ok)',
                      regiondoBookingScrapeErrors: 'Scraping-Fehler (BookingView)',
                    };
                    const title = titleMap[detailsKey] || detailsKey || 'Dry-Run Detail';

                    const regiondoTotal = row?.regiondoTotal != null ? Number(row.regiondoTotal) : null;
                    const cartTotal = row?.cartTotal != null ? Number(row.cartTotal) : null;
                    const cartTotalExclUpsells =
                      row?.cartTotalExclUpsells != null
                        ? Number(row.cartTotalExclUpsells)
                        : cartTotal != null && row?.upsellTotal != null
                          ? Number(cartTotal) - Number(row.upsellTotal)
                          : null;
                    const cartTotalExclUpsellsAndPackages =
                      row?.cartTotalExclUpsellsAndPackages != null
                        ? Number(row.cartTotalExclUpsellsAndPackages)
                        : null;
                    const upsellTotal = row?.upsellTotal != null ? Number(row.upsellTotal) : null;
                    const packageTotal = row?.packageTotal != null ? Number(row.packageTotal) : null;
                    const delta = row?.delta != null ? Number(row.delta) : null;
                    const supplierMatches = row?.supplierMatches != null ? String(row.supplierMatches) : '';
                    const parts = [title];

                    if (detailsKey === 'regiondoTotalMismatch' && regiondoTotal != null) {
                      const lhs = moneyEUR(regiondoTotal) || String(regiondoTotal);
                      const rhs = moneyEUR(cartTotalExclUpsells != null ? cartTotalExclUpsells : cartTotal) || String(cartTotalExclUpsells != null ? cartTotalExclUpsells : cartTotal);
                      parts.push(`${lhs} vs ${rhs}`);
                      if (packageTotal != null) parts.push(`Package ${moneyEUR(packageTotal) || packageTotal}`);
                      if (upsellTotal != null) parts.push(`Upsells ${moneyEUR(upsellTotal) || upsellTotal}`);
                      if (cartTotalExclUpsellsAndPackages != null) {
                        parts.push(
                          `Cart (−Upsells−Package) ${moneyEUR(cartTotalExclUpsellsAndPackages) || cartTotalExclUpsellsAndPackages}`
                        );
                      }
                      if (supplierMatches) {
                        const map = {
                          cartExclUpsells: 'matches Cart (−Upsells)',
                          cartExclUpsellsAndPackages: 'matches Cart (−Upsells−Package)',
                        };
                        parts.push(map[supplierMatches] || supplierMatches);
                      }
                      if (delta != null) parts.push(`Δ ${moneyEUR(delta) || delta}`);
                    } else {
                      const bk = row?.bookingKey ? String(row.bookingKey) : String(compareTarget?.bookingKey || compareTarget?.booking_key || '');
                      const on = row?.orderNumber ? String(row.orderNumber) : String(compareTarget?.orderNumber || compareTarget?.order_number || '');
                      const idParts = [];
                      if (bk) idParts.push(`bookingKey ${bk}`);
                      if (on) idParts.push(`order ${on}`);
                      if (idParts.length) parts.push(idParts.join(' • '));
                    }

                    return parts.filter(Boolean).join(' • ');
                  }

                  if (kind === 'import') {
                    const title = String(ctx?.title || '').trim() || 'Import Detail';
                    const bk = String(ctx?.bookingKey || compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
                    const parts = [title];
                    if (bk) parts.push(`bookingKey ${bk}`);
                    return parts.join(' • ');
                  }

                  return '—';
                })()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: -0.25, flexShrink: 0 }}>
              {(() => {
                const ctx = compareContext && typeof compareContext === 'object' ? compareContext : null;
                const kind = String(ctx?.kind || '').trim();
                if (kind !== 'dryRun') return null;
                const row = ctx?.row && typeof ctx.row === 'object' ? ctx.row : null;
                const bk = String(row?.bookingKey || compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
                if (!bk) return null;
                return (
                  <FormControlLabel
                    sx={{ m: 0, mr: 0.5 }}
                    control={
                      <Checkbox
                        size='small'
                        checked={excludeBookingKeysSet.has(bk)}
                        onChange={e => setBookingExcluded(bk, e.target.checked)}
                      />
                    }
                    label='Vom Import ausschließen'
                  />
                );
              })()}
              {(() => {
                const hasQueue = Array.isArray(compareQueueItems) && compareQueueItems.length > 0;
                const idx = Number.isFinite(Number(compareQueueIndex)) ? Number(compareQueueIndex) : -1;
                const pos = hasQueue && idx >= 0 ? `${idx + 1}/${compareQueueItems.length}` : '—';
                return (
                  <>
                    <IconButton
                      aria-label='Prev'
                      onClick={() => moveCompareQueue(-1)}
                      disabled={!hasQueue || idx <= 0}
                      size='small'
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant='caption' color='text.secondary' sx={{ minWidth: 64, textAlign: 'center' }}>
                      {pos}
                    </Typography>
                    <IconButton
                      aria-label='Next'
                      onClick={() => moveCompareQueue(1)}
                      disabled={!hasQueue || idx >= compareQueueItems.length - 1}
                      size='small'
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </>
                );
              })()}
              <Tooltip title='Random Sample anzeigen'>
                <span>
                  <IconButton
                    aria-label='Random Sample anzeigen'
                    onClick={openRandomCompareQueueItem}
                    disabled={!Array.isArray(compareQueueItems) || compareQueueItems.length === 0}
                    size='small'
                    sx={{ flexShrink: 0 }}
                  >
                    <ShuffleIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton aria-label='Close' onClick={closeCompare}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', height: 'calc(100vh - 112px)' }}>
            <Box sx={{ width: '50%', borderRight: rmSurface.borderRight10, minWidth: 420 }}>
              {(() => {
                const preview = comparePreview?.data || null;
                const internalOnly = preview?.internalBooking || null;
                const effectiveInternal = compareJsonApplied || internalOnly || null;
                const isPreparedPreview = String(preview?.source || '').trim() === 'preparedInternalBookings';
                const isDryRunContext = String(compareContext?.kind || '').trim() === 'dryRun';
                const shouldRenderSyntheticBooking = Boolean((isPreparedPreview || isDryRunContext) && effectiveInternal);
                let json = '';
                try {
                  json = JSON.stringify(effectiveInternal || preview || {}, null, 2);
                } catch (_) {
                  json = String(effectiveInternal || preview || '');
                }

                return (
                  <Box ref={compareSplitContainerRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flex: '1 1 0%', minHeight: 0, overflow: 'auto' }}>
                      {(() => {
                        if (compareBookingId && !shouldRenderSyntheticBooking) {
                          return (
                            <BookingDetailsModal
                              embedded
                              open={true}
                              bookingId={compareBookingId}
                              onClose={() => setCompareOpen(false)}
                              onUpdate={() => {}}
                            />
                          );
                        }
                        // If there is no persisted internal booking (e.g. skipped / failed),
                        // show the preview-only internal booking built from cart/lineItems.
                        if (internalOnly) {
                          return (
                            <Box sx={{ p: 2 }}>
                              <Alert severity='info' sx={{ mb: 2 }}>
                                Vorschau: Diese Buchung ist nicht in der Datenbank gespeichert. Details/JSON unten zeigen die geplante Konvertierung.
                              </Alert>
                              <BookingDetailsModal
                                embedded
                                open={true}
                                bookingId={null}
                                bookingOverride={effectiveInternal}
                                readOnly
                                onClose={() => setCompareOpen(false)}
                                onUpdate={() => {}}
                              />
                            </Box>
                          );
                        }
                        return (
                        <Box sx={{ p: 2 }}>
                          {comparePreview?.loading ? (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                <CircularProgress size={18} />
                                <Typography variant='body2' color='text.secondary'>
                                  Baue Preview (Cart/LineItems)…
                                </Typography>
                              </Box>
                              <Skeleton variant='rounded' height={84} sx={{ mb: 1.25 }} />
                              <Skeleton variant='rounded' height={220} sx={{ mb: 1.25 }} />
                              <Skeleton variant='rounded' height={160} />
                            </Box>
                          ) : comparePreview?.error ? (
                            <Alert severity='error' sx={{ mb: 2 }}>
                              {comparePreview.error}
                            </Alert>
                          ) : (
                            <>
                              {Array.isArray(preview?.errors) && preview.errors.length > 0 ? (
                                <Alert severity='error' sx={{ mb: 2 }}>
                                  Konvertierung/Preview hat Fehler ({preview.errors.length}). JSON unten zeigt Details.
                                </Alert>
                              ) : null}
                              {Array.isArray(preview?.warnings) && preview.warnings.length > 0 ? (
                                <Alert severity='warning' sx={{ mb: 2 }}>
                                  Hinweise ({preview.warnings.length}). JSON unten zeigt Details.
                                </Alert>
                              ) : null}

                              <Alert severity='info' sx={{ mb: 2 }}>
                                Kein `internalBooking` verfügbar (Preview fehlgeschlagen oder unvollständig). Details stehen im JSON unten.
                              </Alert>
                            </>
                          )}
                        </Box>
                        );
                      })()}
                    </Box>

                    <Box
                      sx={{
                        flex: '0 0 auto',
                        height: compareJsonPaneHeight,
                        minHeight: 160,
                        maxHeight: '80%',
                        borderTop: rmSurface.borderTop10,
                        backgroundColor: rmSurface.s02,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        onPointerDown={onCompareSplitPointerDown}
                        sx={{
                          height: 10,
                          cursor: 'row-resize',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: rmSurface.s02,
                          borderBottom: rmSurface.borderBottom08,
                          '&:hover': { backgroundColor: rmSurface.rowHover },
                          '&::before': {
                            content: '""',
                            width: 46,
                            height: 4,
                            borderRadius: 999,
                            backgroundColor: rmSurface.dragGrip,
                          },
                        }}
                      />
                      <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant='body2' sx={{ fontWeight: 700 }}>
                          InternalBooking JSON (Preview)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {!compareJsonEditOpen ? (
                            <Button
                              size='small'
                              variant='outlined'
                              disabled={!effectiveInternal}
                              onClick={() => {
                                if (!effectiveInternal) return;
                                const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
                                compareJsonOwnerBookingKeyRef.current = bk;
                                setCompareJsonText(JSON.stringify(effectiveInternal || {}, null, 2));
                                setCompareJsonEditOpen(true);
                              }}
                            >
                              JSON bearbeiten
                            </Button>
                          ) : (
                            <>
                              <Button
                                size='small'
                                variant='outlined'
                                disabled={isSavingCompareJson || !sessionId}
                                onClick={async () => {
                                  const sid = String(sessionId || '').trim();
                                  const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
                                  if (!sid || !bk) return;
                                  compareJsonOwnerBookingKeyRef.current = bk;

                                  let parsed = null;
                                  try {
                                    parsed = JSON.parse(compareJsonText || '');
                                  } catch (_) {
                                    toast.error('Ungültiges JSON');
                                    return;
                                  }
                                  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                                    toast.error('Ungültiges JSON (muss ein Objekt sein)');
                                    return;
                                  }

                                  setIsSavingCompareJson(true);
                                  try {
                                    const headers = authHeadersRef.current || (auth?.getAuthHeaders?.() || null);
                                    const req = sendRequestRef.current || sendRequest;

                                    const res = await req({
                                      url: `/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(
                                        sid
                                      )}/regiondo/booking/${encodeURIComponent(bk)}/override`,
                                      method: 'PUT',
                                      headers,
                                      body: { internalBooking: parsed },
                                    });

                                    const cacheKey = `v2:${sid}:${bk}`;
                                    // Reload canonical preview from backend so the UI shows exactly what is stored in the session.
                                    let nextPreview = null;
                                    try {
                                      const previewRes = await req({
                                        url: `/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(
                                          sid
                                        )}/regiondo/booking/${encodeURIComponent(bk)}/preview`,
                                        method: 'GET',
                                        headers,
                                      });
                                      nextPreview = previewRes?.preview || null;
                                    } catch (_) {
                                      // fallback below
                                    }
                                    if (!nextPreview) {
                                      const nextInternal =
                                        res?.internalBooking && typeof res.internalBooking === 'object' ? res.internalBooking : parsed;
                                      nextPreview = {
                                        sessionId: sid,
                                        bookingKey: bk,
                                        source: 'preparedInternalBookings',
                                        regiondo: null,
                                        mapping: null,
                                        cart: null,
                                        totals: null,
                                        internalBooking: nextInternal,
                                        warnings: [],
                                        errors: [],
                                        meta: {
                                          createdAt: null,
                                          createdBy: null,
                                          editedAt: Date.now(),
                                          editedBy: auth?.userId || null,
                                        },
                                      };
                                    }

                                    comparePreviewCacheRef.current.set(cacheKey, nextPreview);
                                    setComparePreview({ loading: false, error: null, data: nextPreview });

                                    const nextInternal =
                                      nextPreview?.internalBooking && typeof nextPreview.internalBooking === 'object'
                                        ? nextPreview.internalBooking
                                        : null;

                                    setCompareJsonApplied(nextInternal || null);
                                    setCompareJsonText(JSON.stringify(nextInternal || {}, null, 2));
                                    toast.success('Änderungen gespeichert');
                                  } catch (_) {
                                    toast.error('Änderungen konnten nicht gespeichert werden');
                                  } finally {
                                    setIsSavingCompareJson(false);
                                  }
                                }}
                              >
                                Anwenden
                              </Button>
                              <Button
                                size='small'
                                variant='outlined'
                                color={(() => {
                                  const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
                                  const list = Array.isArray(dryRunReport?.manualOkBookingKeys) ? dryRunReport.manualOkBookingKeys : [];
                                  const isOk = bk && list.includes(bk);
                                  return isOk ? 'warning' : 'success';
                                })()}
                                disabled={isSavingManualOk || !sessionId}
                                onClick={async () => {
                                  const sid = String(sessionId || '').trim();
                                  const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
                                  if (!sid || !bk) return;
                                  const list = Array.isArray(dryRunReport?.manualOkBookingKeys) ? dryRunReport.manualOkBookingKeys : [];
                                  const isOk = bk && list.includes(bk);

                                  setIsSavingManualOk(true);
                                  try {
                                    const headers = authHeadersRef.current || (auth?.getAuthHeaders?.() || null);
                                    const req = sendRequestRef.current || sendRequest;
                                    const res = await req({
                                      url: `/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(
                                        sid
                                      )}/regiondo/booking/${encodeURIComponent(bk)}/manual-ok`,
                                      method: 'PUT',
                                      headers,
                                      body: { ok: !isOk },
                                    });
                                    if (res?.dryRun) setDryRunReport(res.dryRun);
                                    toast.success(!isOk ? 'Als ok markiert' : 'Ok-Markierung entfernt');
                                  } catch (_) {
                                    toast.error('Ok-Markierung konnte nicht gespeichert werden');
                                  } finally {
                                    setIsSavingManualOk(false);
                                  }
                                }}
                              >
                                {(() => {
                                  const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
                                  const list = Array.isArray(dryRunReport?.manualOkBookingKeys) ? dryRunReport.manualOkBookingKeys : [];
                                  const isOk = bk && list.includes(bk);
                                  return isOk ? 'Ok-Markierung entfernen' : 'Als ok markieren';
                                })()}
                              </Button>
                              <Button size='small' variant='text' onClick={() => setCompareJsonEditOpen(false)}>
                                Schließen
                              </Button>
                              <Button
                                size='small'
                                variant='text'
                                color='error'
                                disabled={isSavingCompareJson || !sessionId}
                                onClick={async () => {
                                  const sid = String(sessionId || '').trim();
                                  const bk = String(compareTarget?.bookingKey || compareTarget?.booking_key || '').trim();
                                  if (!sid || !bk) return;
                                  setIsSavingCompareJson(true);
                                  try {
                                    const headers = authHeadersRef.current || (auth?.getAuthHeaders?.() || null);
                                    const req = sendRequestRef.current || sendRequest;
                                    await req({
                                      url: `/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(
                                        sid
                                      )}/regiondo/booking/${encodeURIComponent(bk)}/override`,
                                      method: 'PUT',
                                      headers,
                                      body: { internalBooking: null },
                                    });

                                    const cacheKey = `v2:${sid}:${bk}`;
                                    comparePreviewCacheRef.current.delete(cacheKey);

                                    // Reload preview to remove the override from the UI.
                                    try {
                                      const previewRes = await req({
                                        url: `/api/v1/internalBookingMigration/regiondo/session/${encodeURIComponent(
                                          sid
                                        )}/regiondo/booking/${encodeURIComponent(bk)}/preview`,
                                        method: 'GET',
                                        headers,
                                      });
                                      const nextPreview = previewRes?.preview || null;
                                      if (nextPreview) {
                                        comparePreviewCacheRef.current.set(cacheKey, nextPreview);
                                        setComparePreview({ loading: false, error: null, data: nextPreview });
                                      }
                                    } catch (_) {
                                      // ignore, fallback below
                                    }

                                    const freshInternal =
                                      comparePreviewCacheRef.current.get(cacheKey)?.internalBooking ||
                                      comparePreview?.data?.internalBooking ||
                                      internalOnly ||
                                      {};

                                    setCompareJsonApplied(null);
                                    setCompareJsonText(JSON.stringify(freshInternal || {}, null, 2));
                                    toast.info('Override entfernt');
                                  } catch (_) {
                                    toast.error('Override konnte nicht entfernt werden');
                                  } finally {
                                    setIsSavingCompareJson(false);
                                  }
                                }}
                              >
                                Override löschen
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ px: 1.25, pb: 1.25, height: 'calc(100% - 56px)', overflow: 'auto' }}>
                        {compareJsonEditOpen ? (
                          <TextField
                            value={compareJsonText}
                            onChange={e => setCompareJsonText(e.target.value)}
                            multiline
                            minRows={10}
                            fullWidth
                            size='small'
                            sx={{
                              '& .MuiInputBase-root': {
                                fontFamily:
                                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                fontSize: 12,
                                lineHeight: 1.5,
                                bgcolor: rmSurface.s02,
                              },
                            }}
                          />
                        ) : (
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre',
                              fontSize: 12,
                              lineHeight: 1.5,
                              fontFamily:
                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            }}
                          >
                            {json}
                          </pre>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
            <Box sx={{ width: '50%', minWidth: 420 }}>
              {(() => {
                const bk = compareTarget?.bookingKey || compareTarget?.booking_key || '';
                const sid = String(sessionId || '').trim();
                const url = bk && sid ? buildRegiondoEmbedUrl(bk) : '';
                return (
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.25,
                        borderBottom: rmSurface.borderBottom10,
                      }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 700 }}>
                        Regiondo
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => setRegiondoAuthInfo(prev => ({ ...prev, hasCookie: false, isValid: false }))}
                        >
                          Neu einloggen
                        </Button>
                    {bk ? (
                          <Button
                            size='small'
                            variant='outlined'
                            onClick={() => {
                          const url = buildRegiondoExternalBookingUrl(bk);
                              if (url) window.open(url, '_blank');
                            }}
                          >
                            In neuem Tab öffnen
                          </Button>
                        ) : null}
                      </Box>
                    </Box>
                    {!bk ? (
                      <Box sx={{ p: 2 }}>
                        <Typography variant='body2' color='text.secondary'>
                          Kein bookingKey vorhanden.
                        </Typography>
                      </Box>
                    ) : !(regiondoAuthInfo?.hasCookie && regiondoAuthInfo?.isValid) ? (
                      <Box sx={{ p: 2, maxWidth: 520 }}>
                        <Alert severity={regiondoAuthInfo?.hasCookie ? 'error' : 'warning'} sx={{ mb: 2 }}>
                          {regiondoAuthInfo?.hasCookie
                            ? 'Regiondo Session ist abgelaufen. Bitte neu einloggen, dann funktioniert Side-by-side für alle Einträge.'
                            : 'Regiondo ist nicht eingeloggt. Bitte einmal einloggen, dann funktioniert Side-by-side für alle Einträge.'}
                        </Alert>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <TextField
                              label='Regiondo E-Mail'
                              value={regiondoUsername}
                              onChange={e => setRegiondoUsername(e.target.value)}
                              size='small'
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label='Regiondo Passwort'
                              value={regiondoPassword}
                              onChange={e => setRegiondoPassword(e.target.value)}
                              size='small'
                              type='password'
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant='contained'
                              disabled={isRegiondoLoggingIn || !regiondoUsername || !regiondoPassword}
                              onClick={loginRegiondoReseller}
                              fullWidth
                            >
                              {isRegiondoLoggingIn ? 'Login…' : 'Einloggen & Session speichern'}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    ) : !url ? (
                      <Box sx={{ p: 2 }}>
                        <Typography variant='body2' color='text.secondary'>
                          Session/bookingKey fehlt.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                        {Array.isArray(regiondoEmbedFrames) && regiondoEmbedFrames.length > 0 ? (
                          regiondoEmbedFrames
                            .filter(x => x && x.bookingKey && x.url)
                            .map(frame => {
                              const frameBk = String(frame.bookingKey || '').trim();
                              const isActive = frameBk && frameBk === String(bk || '').trim();
                              return (
                                <iframe
                                  key={frameBk}
                                  title={`Regiondo booking ${frameBk}`}
                                  src={frame.url}
                                  onLoad={() => markRegiondoEmbedLoaded(frameBk)}
                                  style={
                                    isActive
                                      ? { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }
                                      : {
                                          position: 'absolute',
                                          width: 1,
                                          height: 1,
                                          border: 0,
                                          opacity: 0,
                                          pointerEvents: 'none',
                                          left: -99999,
                                          top: 0,
                                        }
                                  }
                                />
                              );
                            })
                        ) : (
                          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <CircularProgress size={18} />
                            <Typography variant='body2' color='text.secondary'>
                              Lade Regiondo…
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            onClick={closeCompare}
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {finalizeDialogMode === 'undo' ? 'Final-Migration zurücksetzen?' : 'Entertainment final migriert markieren?'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' sx={{ mb: 1 }}>
            {finalizeDialogMode === 'undo'
              ? 'Damit taucht das Entertainment (und ggf. die Filiale) wieder in der Migration auf.'
              : 'Danach taucht das Entertainment nicht mehr in der Liste auf. Wenn alle Entertainments der Filiale final sind, verschwindet auch die Filiale (außer „Auch abgeschlossene anzeigen“ ist aktiv).'}
          </Typography>
          <Box sx={{ p: 1.25, borderRadius: 2, backgroundColor: rmSurface.finalizeHint }}>
            <Typography variant='caption' color='text.secondary'>
              Entertainment
            </Typography>
            <Typography variant='body2' sx={{ fontWeight: 700 }}>
              {selectedEntertainment?.longName ||
                selectedEntertainment?.entertainmentName ||
                selectedEntertainmentId ||
                '—'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setFinalizeDialogOpen(false)} disabled={isFinalizingEntertainment}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color={finalizeDialogMode === 'undo' ? 'warning' : 'success'}
            disabled={isFinalizingEntertainment || !selectedEntertainmentId}
            onClick={async () => {
              if (!selectedEntertainmentId) return;
              const completed = finalizeDialogMode !== 'undo';
              setIsFinalizingEntertainment(true);
              try {
                await sendRequest({
                  url: `/api/v1/internalBookingMigration/regiondo/entertainment/${selectedEntertainmentId}/complete`,
                  method: 'POST',
                  headers: auth.getAuthHeaders(),
                  body: { completed },
                });
                toast.success(completed ? 'Als final migriert markiert' : 'Final-Migration zurückgesetzt');

                const b = await getBranches();
                const all = Array.isArray(b) ? b : [];
                const { byBranchId } = buildRolloutIndex();
                const filtered = showCompleted ? all : all.filter(x => !x?.regiondoMigration?.completedAt);
                const sorted = [...filtered].sort((a, b2) => {
                  const ai = byBranchId.has(String(a?._id)) ? byBranchId.get(String(a._id)) : 9999;
                  const bi = byBranchId.has(String(b2?._id)) ? byBranchId.get(String(b2._id)) : 9999;
                  if (ai !== bi) return ai - bi;
                  const an = String(a?.locationName || a?.longName || '').localeCompare(
                    String(b2?.locationName || b2?.longName || ''),
                    undefined,
                    { sensitivity: 'base' }
                  );
                  if (an !== 0) return an;
                  return String(a?._id || '').localeCompare(String(b2?._id || ''));
                });
                setBranches(sorted);

                if (selectedBranchId) {
                  const ents = await getAllEntertainmentsByBranch(selectedBranchId);
                  const eAll = Array.isArray(ents) ? ents : [];
                  const eFiltered = showCompleted ? eAll : eAll.filter(x => !x?.regiondoMigration?.completedAt);
                  const eSorted = [...eFiltered].sort((a, b3) => {
                    const an = String(a?.entertainmentName || a?.longName || '').localeCompare(
                      String(b3?.entertainmentName || b3?.longName || ''),
                      undefined,
                      { sensitivity: 'base' }
                    );
                    if (an !== 0) return an;
                    return String(a?._id || '').localeCompare(String(b3?._id || ''));
                  });
                  setEntertainments(eSorted);
                  if (!eSorted.some(x => String(x?._id) === String(selectedEntertainmentId))) {
                    setSelectedEntertainmentId(eSorted[0]?._id ? String(eSorted[0]._id) : '');
                  }
                }

                if (completed) {
                  resetSessionUi();
                }
                setFinalizeDialogOpen(false);
              } catch (e) {
                toast.error(finalizeDialogMode === 'undo' ? 'Konnte nicht zurücksetzen' : 'Konnte nicht final markieren');
              } finally {
                setIsFinalizingEntertainment(false);
              }
            }}
          >
            {isFinalizingEntertainment ? 'Speichere…' : finalizeDialogMode === 'undo' ? 'Zurücksetzen' : 'Bestätigen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={forceImportDialogOpen} onClose={() => setForceImportDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Force Import – alte InternalBookings löschen?</DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' sx={{ mb: 1 }}>
            Es werden alle{' '}
            <Box component='span' sx={{ fontWeight: 600 }}>
              InternalBookings
            </Box>{' '}
            gelöscht, die durch diese Migration-Session importiert wurden, und danach wird der Import erneut gestartet.
          </Typography>
          <Alert severity='warning'>
            Nur Buchungen, die bereits mit dieser Session importiert wurden, werden gelöscht. (Bei sehr alten Imports ohne Marker kann nichts gelöscht werden.)
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setForceImportDialogOpen(false)} disabled={isImporting || importInProgress}>
            Abbrechen
          </Button>
          <Button variant='contained' color='error' onClick={forceRestartImport} disabled={isImporting || importInProgress}>
            Löschen & Import starten
          </Button>
        </DialogActions>
      </Dialog>

      <Card variant='outlined'>
        <CardContent>
          <Typography variant='overline' color='text.secondary'>
            Live Log
          </Typography>
          <Divider sx={{ my: 1 }} />
          {logs.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Noch keine Events. Starte eine Session, um Live-Updates zu sehen.
            </Typography>
          ) : (
            <Box
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 12,
                lineHeight: 1.7,
                maxHeight: 320,
                overflow: 'auto',
                backgroundColor: rmSurface.finalizeHint,
                borderRadius: 2,
                px: 2,
                py: 1.5,
              }}
            >
              {logs.map((l, idx) => (
                <div key={idx}>
                  <span style={{ opacity: 0.6 }}>{dayjs(l.at).format('HH:mm:ss')}</span> {'  '}
                  <span>{l.text}</span>
                </div>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={unmatchedOpen} onClose={() => setUnmatchedOpen(false)} maxWidth='lg' fullWidth>
        <DialogTitle>Ungematchte Buchungen</DialogTitle>
        <DialogContent dividers>
          {unmatchedLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant='body2' color='text.secondary'>
                Lade Daten…
              </Typography>
            </Box>
          ) : (
            <>
              <Tabs
                value={unmatchedTab}
                onChange={(_, v) => setUnmatchedTab(v)}
                variant='scrollable'
                allowScrollButtonsMobile
                sx={{ borderBottom: rmSurface.borderBottom08 }}
              >
                <Tab
                  label={`Fehlt in DB (${unmatchedData?.totals?.missingInDb ?? 0})`}
                  id='unmatched-tab-missing'
                />
                <Tab
                  label={`Nur in DB (${unmatchedData?.totals?.onlyInDb ?? 0})`}
                  id='unmatched-tab-onlydb'
                />
              </Tabs>

              <Box sx={{ mt: 2, maxHeight: 520, overflow: 'auto' }}>
                {(() => {
                  const rows =
                    unmatchedTab === 0
                      ? unmatchedData?.missingInDb || []
                      : unmatchedData?.onlyInDb || [];
                  if (rows.length === 0) {
                    return (
                  <Typography variant='body2' color='text.secondary'>
                    Keine Einträge.
                  </Typography>
                    );
                  }
                  return rows.map((row, idx) => (
                    <Box
                      key={`${row?.booking_key || row?.bookingId || idx}`}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        backgroundColor: rmSurface.s03,
                        mb: 1,
                        border: rmSurface.b06,
                      }}
                    >
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} md={4}>
                          <Typography variant='caption' color='text.secondary'>
                            booking_key
                          </Typography>
                          <Typography variant='body2' sx={{ fontWeight: 700 }}>
                            {row?.booking_key || '—'}
                          </Typography>
                          <Typography variant='body2' sx={{ opacity: 0.8 }}>
                            order: {row?.order_number || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <Typography variant='caption' color='text.secondary'>
                            Produkt / Variation
                          </Typography>
                          <Typography variant='body2' sx={{ fontWeight: 700 }}>
                            {row?.product_name || '—'}
                          </Typography>
                          <Typography variant='body2' sx={{ opacity: 0.8 }}>
                            {row?.variation_name || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant='caption' color='text.secondary'>
                            Zeit / Status / Total
                          </Typography>
                          <Typography variant='body2' sx={{ opacity: 0.85 }}>
                            {row?.event_date_time
                              ? row.event_date_time
                              : row?.eventStart
                                ? dayjs(row.eventStart).format('YYYY-MM-DD HH:mm')
                                : '—'}
                          </Typography>
                          <Typography variant='body2' sx={{ opacity: 0.85 }}>
                            {row?.status || '—'}
                          </Typography>
                          <Typography variant='body2' sx={{ fontWeight: 700 }}>
                            {row?.total_amount != null
                              ? `${row.total_amount}€`
                              : row?.totalAmount != null
                                ? `${row.totalAmount}€`
                                : '—'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  ));
                })()}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => {
              if (sessionId) loadUnmatched(sessionId);
            }}
            disabled={!sessionId || unmatchedLoading}
          >
            Reload
          </Button>
          <Button variant='contained' onClick={() => setUnmatchedOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

